/* Joysent Paddle Customizer V7 R4.4.13 — matched white viewer/snapshot + R4.4.12 studio lighting + R4.4.9 business rules */
(() => {
  'use strict';

  const CFG = window.JoysentCustomizerConfig || {};
  const section = document.getElementById(`JoysentCustomizer-${CFG.sectionId}`);
  if (!section) return;
  const FEATURES = {
    inspirationGallery: CFG.features?.inspirationGallery === true,
    usableTemplates: CFG.features?.usableTemplates === true
  };

  function initCaseShowcase() {
    const showcase = section.querySelector('[data-case-showcase]');
    if (!showcase || showcase.dataset.caseShowcaseReady === 'true') return;
    showcase.dataset.caseShowcaseReady = 'true';

    const rail = showcase.querySelector('[data-case-rail]');
    const lightbox = section.querySelector('[data-case-lightbox]');
    const lightboxImage = lightbox?.querySelector('[data-case-lightbox-image]');
    const lightboxTitle = lightbox?.querySelector('[data-case-lightbox-title]');
    const lightboxCaption = lightbox?.querySelector('[data-case-lightbox-caption]');
    const previousButton = showcase.querySelector('[data-case-previous]');
    const nextButton = showcase.querySelector('[data-case-next]');
    let lastFocusedCase = null;

    function openCase(button) {
      if (!lightbox || !lightboxImage || !button?.dataset.caseSrc) return;
      lastFocusedCase = button;
      lightboxImage.src = button.dataset.caseSrc;
      lightboxImage.alt = button.dataset.caseAlt || button.dataset.caseTitle || 'Custom Joysent paddle';
      lightboxTitle.textContent = button.dataset.caseTitle || 'Custom Joysent Paddle';
      lightboxCaption.textContent = button.dataset.caseCaption || '';
      lightboxCaption.hidden = !button.dataset.caseCaption;
      window.clearTimeout(resumeTimer);
      setPaused(true);
      lightbox.hidden = false;
      document.body.classList.add('jc-case-lightbox-open');
      lightbox.querySelector('.case-lightbox__close')?.focus({ preventScroll: true });
    }

    function closeCase() {
      if (!lightbox || lightbox.hidden) return;
      lightbox.hidden = true;
      document.body.classList.remove('jc-case-lightbox-open');
      lightboxImage?.removeAttribute('src');
      if (lastFocusedCase?.isConnected) lastFocusedCase.focus({ preventScroll: true });
      else setPaused(false);
    }

    showcase.addEventListener('click', (event) => {
      const button = event.target.closest('[data-case-open]');
      if (button) openCase(button);
    });
    lightbox?.addEventListener('click', (event) => {
      if (event.target.closest('[data-case-close]')) closeCase();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && lightbox && !lightbox.hidden) closeCase();
    });

    if (!rail) return;
    const originals = [...rail.querySelectorAll('.player-case')];
    if (!originals.length) return;
    const cardStep = () => {
      const first = rail.querySelector('.player-case');
      if (!first) return Math.max(260, rail.clientWidth * .8);
      const styles = getComputedStyle(rail);
      return first.getBoundingClientRect().width + (parseFloat(styles.columnGap || styles.gap) || 16);
    };
    let resumeTimer = 0;
    let paused = false;
    let loopWidth = 0;
    let inViewport = true;
    let animationFrame = 0;
    let animationTimer = 0;
    let lastTime = 0;
    let loopPositioned = false;
    const scrollSpeed = window.matchMedia?.('(max-width: 820px)').matches ? 28 : 36;

    function stopAnimation() {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      if (animationTimer) window.clearTimeout(animationTimer);
      animationFrame = 0;
      animationTimer = 0;
      lastTime = 0;
    }

    function scheduleAnimation(delay = 0) {
      if (animationFrame || animationTimer || paused || !inViewport || document.hidden) return;
      if (delay > 0) {
        animationTimer = window.setTimeout(() => {
          animationTimer = 0;
          if (!paused && inViewport && !document.hidden) {
            animationFrame = window.requestAnimationFrame(animate);
          }
        }, delay);
      } else {
        animationFrame = window.requestAnimationFrame(animate);
      }
    }

    function startAnimation() {
      if (!loopPositioned && loopWidth > 0) {
        rail.scrollLeft = 0;
        loopPositioned = true;
      }
      scheduleAnimation();
    }

    function setPaused(nextPaused) {
      paused = nextPaused;
      if (paused) stopAnimation();
      else startAnimation();
    }

    const pauseForInteraction = (delay = 3500) => {
      setPaused(true);
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => setPaused(false), delay);
    };
    previousButton?.addEventListener('click', () => {
      pauseForInteraction();
      rail.scrollBy({ left: -cardStep(), behavior: 'smooth' });
    });
    nextButton?.addEventListener('click', () => {
      pauseForInteraction();
      rail.scrollBy({ left: cardStep(), behavior: 'smooth' });
    });
    rail.addEventListener('pointerdown', () => pauseForInteraction(4500), { passive: true });
    rail.addEventListener('touchstart', () => pauseForInteraction(4500), { passive: true });
    rail.addEventListener('pointerenter', () => {
      window.clearTimeout(resumeTimer);
      setPaused(true);
    }, { passive: true });
    rail.addEventListener('pointerleave', () => {
      window.clearTimeout(resumeTimer);
      setPaused(false);
    }, { passive: true });
    rail.addEventListener('focusin', () => {
      window.clearTimeout(resumeTimer);
      setPaused(true);
    });
    rail.addEventListener('focusout', () => pauseForInteraction(1800));

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (originals.length < 2 || reduceMotion) return;

    originals.forEach((card) => {
      const clone = card.cloneNode(true);
      clone.removeAttribute('data-shopify-editor-block');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('button, a').forEach((control) => { control.tabIndex = -1; });
      rail.appendChild(clone);
    });

    const supportsDockHover = Boolean(window.matchMedia?.('(hover: hover) and (pointer: fine)').matches);
    let hoverPreview = null;
    let hoverPreviewCard = null;

    const ensureHoverPreview = () => {
      if (hoverPreview) return hoverPreview;
      hoverPreview = document.createElement('div');
      hoverPreview.className = 'jc-player-case-hover-preview';
      hoverPreview.setAttribute('aria-hidden', 'true');
      hoverPreview.hidden = true;
      document.body.appendChild(hoverPreview);
      return hoverPreview;
    };

    const hideHoverPreview = () => {
      hoverPreviewCard = null;
      if (hoverPreview) hoverPreview.hidden = true;
    };

    const showHoverPreview = (card) => {
      if (!card || !supportsDockHover || lightbox?.hidden === false) {
        hideHoverPreview();
        return;
      }

      const preview = ensureHoverPreview();
      if (hoverPreviewCard !== card) {
        hoverPreviewCard = card;
        preview.replaceChildren(...[...card.childNodes].map((node) => node.cloneNode(true)));
        preview.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        preview.querySelectorAll('button, a').forEach((control) => {
          control.tabIndex = -1;
          control.setAttribute('aria-hidden', 'true');
        });
      }

      const rect = card.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const maximumWidth = Math.max(220, window.innerWidth - 32);
      const maximumHeight = Math.max(300, window.innerHeight - 32);
      let width = Math.min(rect.width * 1.62, maximumWidth);
      let height = rect.height * (width / rect.width);
      if (height > maximumHeight) {
        height = maximumHeight;
        width = rect.width * (height / rect.height);
      }

      const left = Math.min(
        Math.max(16, rect.left + rect.width / 2 - width / 2),
        Math.max(16, window.innerWidth - width - 16)
      );
      const top = Math.min(
        Math.max(16, rect.bottom - height - 14),
        Math.max(16, window.innerHeight - height - 16)
      );

      preview.style.left = `${left}px`;
      preview.style.top = `${top}px`;
      preview.style.width = `${width}px`;
      preview.style.height = `${height}px`;
      preview.hidden = false;
    };

    const resetDock = () => {
      rail.querySelectorAll('.player-case').forEach((card) => {
        card.style.removeProperty('--dock-scale');
        card.style.removeProperty('--dock-lift');
        card.style.removeProperty('--dock-z');
      });
      hideHoverPreview();
    };
    const updateDock = (event) => {
      const pointerX = event.clientX;
      rail.querySelectorAll('.player-case').forEach((card) => {
        const rect = card.getBoundingClientRect();
        const center = rect.left + rect.width / 2;
        const distance = Math.abs(pointerX - center);
        const influence = Math.max(0, 1 - distance / Math.max(rect.width * 2.25, 240));
        const eased = influence * influence * (3 - 2 * influence);
        card.style.setProperty('--dock-scale', (1 + eased * .14).toFixed(3));
        card.style.setProperty('--dock-lift', `${(-eased * 6).toFixed(1)}px`);
        card.style.setProperty('--dock-z', String(Math.round(10 + eased * 90)));
      });
      showHoverPreview(event.target.closest('.player-case'));
    };
    if (supportsDockHover) {
      rail.addEventListener('pointermove', updateDock, { passive: true });
      rail.addEventListener('pointerleave', resetDock, { passive: true });
      rail.addEventListener('scroll', () => {
        if (hoverPreviewCard) resetDock();
      }, { passive: true });
      window.addEventListener('resize', hideHoverPreview, { passive: true });
    }

    const measureLoop = () => {
      const firstClone = rail.children[originals.length];
      loopWidth = firstClone ? firstClone.offsetLeft - rail.children[0].offsetLeft : rail.scrollWidth / 2;
    };
    function animate(time) {
      animationFrame = 0;
      if (paused || !inViewport || document.hidden) return;
      if (!lastTime) lastTime = time;
      const elapsed = Math.min(50, time - lastTime);
      lastTime = time;
      if (lightbox?.hidden !== false && loopWidth > 0) {
        rail.scrollLeft += elapsed * (scrollSpeed / 1000);
        if (rail.scrollLeft >= loopWidth) rail.scrollLeft -= loopWidth;
      }
      scheduleAnimation(33);
    }
    measureLoop();
    window.addEventListener('resize', measureLoop, { passive: true });
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        inViewport = entries.some((entry) => entry.isIntersecting);
        if (inViewport) startAnimation();
        else stopAnimation();
      }, { rootMargin: '120px 0px', threshold: .01 });
      observer.observe(showcase);
    } else {
      startAnimation();
    }
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAnimation();
      else startAnimation();
    });
    const handleSectionUnload = (event) => {
      if (event.target !== section) return;
      stopAnimation();
      hoverPreview?.remove();
      hoverPreview = null;
      document.removeEventListener('shopify:section:unload', handleSectionUnload);
    };
    document.addEventListener('shopify:section:unload', handleSectionUnload);
  }

  function initReviewShowcase() {
    const reviews = section.querySelector('[data-review-showcase]');
    if (!reviews || reviews.dataset.reviewShowcaseReady === 'true') return;
    reviews.dataset.reviewShowcaseReady = 'true';

    const source = reviews.querySelector('[data-review-source]');
    const stage = reviews.querySelector('[data-review-stage]');
    const activeCard = reviews.querySelector('[data-review-active]');
    const footer = reviews.querySelector('[data-review-footer]');
    const count = reviews.querySelector('[data-review-count]');
    const progress = reviews.querySelector('[data-review-progress]');
    const empty = reviews.querySelector('[data-review-empty]');
    const viewAll = reviews.querySelector('[data-review-view-all]');
    const modal = reviews.querySelector('[data-review-modal]');
    const allList = reviews.querySelector('[data-review-all-list]');
    let items = [];
    let currentIndex = 0;
    let progressAnimation = null;
    let sourceObserver = null;
    let sourceTimeout = 0;
    let lastFocusedControl = null;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const firstText = (node, selectors) => {
      for (const selector of selectors) {
        const value = node.querySelector(selector)?.textContent?.replace(/\s+/g, ' ').trim();
        if (value) return value;
      }
      return '';
    };

    const parseRating = (node) => {
      const labelled = node.querySelector('[aria-label*="out of 5" i]')?.getAttribute('aria-label') || '';
      const labelledMatch = labelled.match(/([0-5](?:\.\d+)?)\s*out of 5/i);
      if (labelledMatch) return Math.max(1, Math.min(5, Math.round(Number(labelledMatch[1]))));
      const dataRating = node.querySelector('[data-score], [data-rating]');
      const rawRating = dataRating?.getAttribute('data-score') || dataRating?.getAttribute('data-rating');
      if (rawRating && Number.isFinite(Number(rawRating))) return Math.max(1, Math.min(5, Math.round(Number(rawRating))));
      const modernStars = node.querySelectorAll('.jm-star-rating__font-icon').length;
      if (modernStars) return Math.min(5, modernStars);
      const legacyStars = node.querySelectorAll('.jdgm-star.jdgm--on').length;
      return legacyStars ? Math.min(5, legacyStars) : 5;
    };

    const parseReview = (node, index) => {
      const body = firstText(node, [
        '.jdgm-review-content__body-content',
        '.jm-review-content__body',
        '.jdgm-rev__body'
      ]);
      if (!body) return null;
      const title = firstText(node, [
        '.jm-review-content__title',
        '.jdgm-rev__title'
      ]);
      const author = firstText(node, [
        '.jm-reviewer-info__name',
        '.jdgm-rev__author'
      ]) || 'Joysent customer';
      const date = firstText(node, [
        '.jm-reviewer-info__details > .jm-cluster:last-child .jm-text',
        '.jdgm-rev__timestamp'
      ]);
      const verified = Boolean(node.querySelector('.jm-verified-buyer-badge, .jdgm-rev__buyer-badge, [data-verified-buyer="true"]'));
      const image = node.querySelector('.jm-media-thumbnail-list__thumbnail img, .jdgm-rev__pic-img, .jdgm-rev__video-thumbnail img');
      return {
        id: node.getAttribute('review-uuid') || node.getAttribute('data-review-id') || `${author}-${title}-${index}`,
        rating: parseRating(node),
        title,
        body,
        author,
        date,
        verified,
        imageUrl: image?.currentSrc || image?.src || '',
        imageAlt: image?.alt || 'Customer custom paddle'
      };
    };

    const starRow = (rating) => {
      const stars = document.createElement('span');
      stars.className = 'showcase-review__stars';
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${rating} out of 5 stars`);
      stars.textContent = '★'.repeat(rating) + '☆'.repeat(Math.max(0, 5 - rating));
      return stars;
    };

    const createCard = (review, expanded = false) => {
      const card = document.createElement('article');
      card.className = expanded ? 'showcase-review showcase-review--all' : 'showcase-review';
      const copy = document.createElement('div');
      copy.className = 'showcase-review__copy';
      copy.appendChild(starRow(review.rating));
      if (review.title) {
        const title = document.createElement('h3');
        title.textContent = review.title;
        copy.appendChild(title);
      }
      const quote = document.createElement('blockquote');
      quote.textContent = review.body;
      copy.appendChild(quote);
      const meta = document.createElement('footer');
      const author = document.createElement('strong');
      author.textContent = review.author;
      meta.appendChild(author);
      if (review.verified) {
        const verified = document.createElement('span');
        verified.className = 'showcase-review__verified';
        verified.textContent = 'Verified';
        meta.appendChild(verified);
      }
      if (review.date) {
        const date = document.createElement('time');
        date.textContent = review.date;
        meta.appendChild(date);
      }
      copy.appendChild(meta);
      card.appendChild(copy);
      if (review.imageUrl) {
        card.classList.add('has-media');
        const media = document.createElement('div');
        media.className = 'showcase-review__media';
        const image = document.createElement('img');
        image.src = review.imageUrl;
        image.alt = review.imageAlt;
        image.loading = 'lazy';
        media.appendChild(image);
        card.appendChild(media);
      }
      return card;
    };

    const readingDuration = (review) => {
      const words = `${review.title} ${review.body}`.trim().split(/\s+/).filter(Boolean).length;
      return Math.max(6500, Math.min(14000, 3000 + words / 210 * 60000));
    };

    const stopProgress = () => {
      progressAnimation?.cancel();
      progressAnimation = null;
    };

    const startProgress = () => {
      stopProgress();
      if (reduceMotion || items.length < 2 || modal?.hidden === false || document.hidden || !progress) return;
      progressAnimation = progress.animate(
        [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
        { duration: readingDuration(items[currentIndex]), easing: 'linear', fill: 'forwards' }
      );
      progressAnimation.onfinish = () => {
        currentIndex = (currentIndex + 1) % items.length;
        renderActive();
      };
    };

    const renderActive = () => {
      if (!items.length || !activeCard) return;
      const next = createCard(items[currentIndex]);
      activeCard.replaceChildren(...next.childNodes);
      if (count) count.textContent = `${String(currentIndex + 1).padStart(2, '0')} / ${String(items.length).padStart(2, '0')}`;
      startProgress();
    };

    const closeModal = () => {
      if (!modal || modal.hidden) return;
      modal.hidden = true;
      document.body.classList.remove('jc-reviews-modal-open');
      lastFocusedControl?.focus({ preventScroll: true });
      startProgress();
    };

    const openModal = () => {
      if (!modal || !allList || !items.length) return;
      lastFocusedControl = document.activeElement;
      stopProgress();
      allList.replaceChildren(...items.map((review) => createCard(review, true)));
      modal.hidden = false;
      document.body.classList.add('jc-reviews-modal-open');
      modal.querySelector('.showcase-reviews-modal__close')?.focus({ preventScroll: true });
    };

    const publishReviews = () => {
      if (!source) return false;
      const nodes = [...source.querySelectorAll('.jm-review-item, .jdgm-rev')];
      const seen = new Set();
      const parsed = nodes.map(parseReview).filter((review) => {
        if (!review || seen.has(review.id)) return false;
        seen.add(review.id);
        return true;
      });
      if (!parsed.length) return false;
      items = parsed;
      currentIndex = 0;
      sourceObserver?.disconnect();
      window.clearTimeout(sourceTimeout);
      empty && (empty.hidden = true);
      footer && (footer.hidden = false);
      viewAll && (viewAll.hidden = items.length < 2);
      renderActive();
      return true;
    };

    viewAll?.addEventListener('click', openModal);
    modal?.addEventListener('click', (event) => {
      if (event.target.closest('[data-review-close]')) closeModal();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal?.hidden === false) closeModal();
    });
    stage?.addEventListener('pointerenter', () => progressAnimation?.pause(), { passive: true });
    stage?.addEventListener('pointerleave', () => progressAnimation?.play(), { passive: true });
    stage?.addEventListener('focusin', () => progressAnimation?.pause());
    stage?.addEventListener('focusout', () => progressAnimation?.play());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) progressAnimation?.pause();
      else progressAnimation?.play();
    });

    if (!publishReviews() && source) {
      sourceObserver = new MutationObserver(publishReviews);
      sourceObserver.observe(source, { childList: true, subtree: true });
      sourceTimeout = window.setTimeout(() => {
        sourceObserver?.disconnect();
        activeCard?.setAttribute('hidden', '');
        empty && (empty.hidden = false);
      }, 10000);
    }

    const handleSectionUnload = (event) => {
      if (event.target !== section) return;
      stopProgress();
      sourceObserver?.disconnect();
      window.clearTimeout(sourceTimeout);
      document.removeEventListener('shopify:section:unload', handleSectionUnload);
    };
    document.addEventListener('shopify:section:unload', handleSectionUnload);
  }

  if (FEATURES.inspirationGallery) initCaseShowcase();
  if (FEATURES.inspirationGallery) initReviewShowcase();
  if (!window.THREE || !THREE.GLTFLoader) return;

  const $ = (selector) => section.querySelector(selector);
  const $$ = (selector) => [...section.querySelectorAll(selector)];
  const TEX = CFG.textures || {};
  const COPY = CFG.uiCopy || {};
  const STEP_COPY = Array.isArray(CFG.stepCopy) ? CFG.stepCopy.filter(Boolean).reduce((map, entry) => {
    map[Number(entry.step)] = entry;
    return map;
  }, {}) : {};
  const BUILDS = CFG.builds || {
    cold: { id: 'cold', code: 'CP', label: 'Cold Press', description: 'An easy-to-play build designed for beginners and recreational players.' },
    hot: { id: 'hot', code: 'TF', label: 'Thermoformed', description: 'A performance-focused build designed for advanced and professional players.' }
  };
  const SHAPES = Array.isArray(CFG.shapes) && CFG.shapes.some((entry) => entry && entry.enabled !== false) ? CFG.shapes.filter((entry) => entry && entry.enabled !== false) : [
    { id: 'fc005', code: 'FC005', label: 'FC005', build: 'cold', thickness: '16mm', dimensions: 'Dimensions to be confirmed', description: 'A versatile shape.', modelUrl: null }
  ];
  const CORES = Array.isArray(CFG.cores) && CFG.cores.some((entry) => entry && entry.enabled !== false) ? CFG.cores.filter((entry) => entry && entry.enabled !== false) : [
    { id: 'pp-honeycomb', code: 'PP-HONEYCOMB', label: 'PP Honeycomb', build: 'cold', playerType: 'All-Court', description: 'A responsive, familiar core.', holeInfo: '13mm and 14mm use 6mm cells; 16mm uses 8mm cells.' }
  ];
  const SURFACES = Array.isArray(CFG.surfaces) && CFG.surfaces.some((entry) => entry && entry.enabled !== false) ? CFG.surfaces.filter((entry) => entry && entry.enabled !== false) : [
    { id: 't300-smooth', code: 'T300-SMOOTH', label: 'T300 Carbon Fiber Smooth', build: 'cold', description: 'A clean, consistent response.', pattern: '3k', grit: '' }
  ];
  const RECOMMENDATIONS = Array.isArray(CFG.recommendations) ? CFG.recommendations.filter(Boolean) : [];
  const ARTWORK_TEMPLATES = FEATURES.usableTemplates && Array.isArray(CFG.artworkTemplates) ? CFG.artworkTemplates.filter((template) => template && template.frontUrl) : [];
  const TEMPLATE_FAMILIES = FEATURES.usableTemplates && Array.isArray(CFG.templateFamilies)
    ? CFG.templateFamilies.filter((family) => family && family.enabled !== false && family.manifestUrl)
    : [];

  const DEFAULT_EDGES = [
    { id: 'BK', label: 'Black', hex: '#1b1d20' },
    { id: 'WH', label: 'White', hex: '#ecece8' },
    { id: 'PK', label: 'Pink', hex: '#e75b86' },
    { id: 'BL', label: 'Blue', hex: '#2161d1' },
    { id: 'RD', label: 'Red', hex: '#c22a20' },
    { id: 'OR', label: 'Orange', hex: '#f05a28' },
    { id: 'GN', label: 'Green', hex: '#16845b' },
    { id: 'PR', label: 'Purple', hex: '#7651c9' }
  ];
  const DEFAULT_FERRULES = [
    { id: 'BK', label: 'Black', hex: '#17191a' },
    { id: 'WH', label: 'White', hex: '#f0f0ec' }
  ];
  const DEFAULT_GRIPS = [
    { id: 'BK', label: 'Black', base: '#17191a' },
    { id: 'WH', label: 'White', base: '#ecece7' },
    { id: 'PK', label: 'Pink', base: '#e75b86' },
    { id: 'BL', label: 'Blue', base: '#2466d8' },
    { id: 'RD', label: 'Red', base: '#c52b27' },
    { id: 'OR', label: 'Orange', base: '#f05a28' },
    { id: 'GN', label: 'Green', base: '#16845b' },
    { id: 'PR', label: 'Purple', base: '#7651c9' }
  ];
  const readThemeBlockConfigs = (selector) => {
    return [...section.querySelectorAll(selector)].map((node) => {
      try {
        return JSON.parse(node.textContent || '{}');
      } catch (error) {
        console.warn('Joysent palette block could not be read.', error);
        return null;
      }
    }).filter((entry) => entry && entry.enabled !== false);
  };
  const themeEdgeColors = readThemeBlockConfigs('[data-jc-edge-color]');
  const themeHandleColors = readThemeBlockConfigs('[data-jc-handle-color]');
  const themeFerruleColors = themeHandleColors
    .filter((entry) => entry.type === 'ferrule')
    .map((entry) => ({ ...entry, hex: entry.hex || '#17191a' }));
  const themeGripColors = themeHandleColors
    .filter((entry) => entry.type === 'grip')
    .map((entry) => ({ ...entry, base: entry.hex || '#17191a' }));
  const normalizePalette = (configured, fallback, colorKey = 'hex') => {
    const source = Array.isArray(configured) && configured.length ? configured : fallback;
    const seen = new Set();
    return source.filter((entry) => {
      const id = String(entry?.id || entry?.code || '').trim();
      const label = String(entry?.label || '').trim();
      if (!id || !label || seen.has(id)) return false;
      seen.add(id);
      entry.id = id;
      entry.code = entry.code || id;
      entry.build = entry.build || 'both';
      if (!entry[colorKey]) entry[colorKey] = colorKey === 'base' ? '#17191a' : '#1b1d20';
      return true;
    });
  };
  const EDGES = normalizePalette(themeEdgeColors.length ? themeEdgeColors : CFG.edgeColors, DEFAULT_EDGES)
    .map((entry) => ({
      ...entry,
      mode: entry.mode === 'mirrored_gradient' ? 'mirrored_gradient' : 'solid',
      stop0: entry.stop0 || entry.hex,
      stop25: entry.stop25 || entry.hex,
      stop50: entry.stop50 || entry.hex,
      gradientAxis: entry.gradientAxis === 'v' ? 'v' : 'u'
    }));
  const FERRULES = normalizePalette(themeFerruleColors.length ? themeFerruleColors : CFG.ferruleColors, DEFAULT_FERRULES);
  const GRIPS = normalizePalette(themeGripColors.length ? themeGripColors : CFG.gripColors, DEFAULT_GRIPS, 'base');
  const edgeCssBackground = (entry) => {
    if (entry?.mode !== 'mirrored_gradient') return entry?.hex || '#1b1d20';
    return `linear-gradient(90deg, ${entry.stop0} 0%, ${entry.stop25} 12.5%, ${entry.stop50} 25%, ${entry.stop25} 37.5%, ${entry.stop0} 50%, ${entry.stop25} 62.5%, ${entry.stop50} 75%, ${entry.stop25} 87.5%, ${entry.stop0} 100%)`;
  };
  const colorAvailableForBuild = (entry, build = 'cold') => !entry?.build || entry.build === 'both' || entry.build === build;
  const colorsForBuild = (items, build) => {
    const available = items.filter((entry) => colorAvailableForBuild(entry, build));
    return available.length ? available : items;
  };
  const firstColorForBuild = (items, build) => colorsForBuild(items, build)[0];

  const firstFor = (items, build) => items.find((item) => item.build === build) || items[0];
  const state = {
    build: 'cold',
    shape: firstFor(SHAPES, 'cold')?.id,
    core: firstFor(CORES, 'cold')?.id,
    surface: firstFor(SURFACES, 'cold')?.id,
    edge: firstColorForBuild(EDGES, 'cold')?.id,
    ferrule: firstColorForBuild(FERRULES, 'cold')?.id,
    grip: firstColorForBuild(GRIPS, 'cold')?.id,
    faces: { front: { layers: [], sel: -1 }, back: { layers: [], sel: -1 } },
    sync: false,
    activeFace: 'front',
    recommendedSetup: '',
    template: {
      familyId: '',
      familyName: '',
      variantId: '',
      version: '',
      colorPreset: ''
    }
  };
  const item = (items, id) => items.find((entry) => entry.id === id) || items[0];
  const currentShape = () => item(SHAPES, state.shape);
  const currentCore = () => item(CORES, state.core);
  const currentSurface = () => item(SURFACES, state.surface);
  const findByCode = (items, code, build) => items.find((entry) => String(entry.code).toLowerCase() === String(code || '').toLowerCase() && (!build || entry.build === build));
  /*
   * Production compatibility rules must not depend on a Theme Editor block ID.
   * Merchants can change that ID independently from the visible label and the
   * Shopify option value, so inspect all stable descriptors for each option.
   */
  const compatibilityTokens = (entry) => [entry?.id, entry?.code, entry?.label, entry?.variantValue]
    .filter(Boolean)
    .map((value) => String(value).normalize('NFKC').toLowerCase().replace(/[^a-z0-9]+/g, ''));
  const compatibilityTokenIncludes = (entry, value) => {
    const wanted = String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
    return Boolean(wanted) && compatibilityTokens(entry).some((token) => token.includes(wanted));
  };
  const isGH8001Shape = (shape) => compatibilityTokenIncludes(shape, 'GH8001');
  const isJS005Shape = (shape) => compatibilityTokenIncludes(shape, 'JS005');
  const isGlassFiberSurface = (surface) => compatibilityTokens(surface).some((token) => (
    token.includes('fiberglass') ||
    token.includes('fibreglass') ||
    token.includes('glassfiber') ||
    token.includes('glassfibre')
  ));
  const isT300Surface = (surface) => compatibilityTokenIncludes(surface, 'T300');
  const isEppCarbonFrameGroovedCore = (core) => compatibilityTokens(core).some((token) => (
    token.includes('eppcarbonframegrooved') ||
    (token.includes('grooved') && (token.includes('epp') || token.includes('carbonframe')))
  ));
  function coreAvailableForShape(core, shape, build = state.build) {
    if (!core || core.build !== build) return false;
    if (build === 'hot' && isEppCarbonFrameGroovedCore(core)) return isJS005Shape(shape);
    return true;
  }
  function surfaceAvailableForShape(surface, shape, build = state.build) {
    if (!surface || surface.build !== build) return false;
    if (build === 'cold' && isGH8001Shape(shape)) return isGlassFiberSurface(surface) && !isT300Surface(surface);
    return true;
  }
  const coresForSelection = (shape = currentShape(), build = state.build) => CORES.filter((core) => coreAvailableForShape(core, shape, build));
  const surfacesForSelection = (shape = currentShape(), build = state.build) => SURFACES.filter((surface) => surfaceAvailableForShape(surface, shape, build));
  function selectionIsCompatible(shape = currentShape(), core = currentCore(), surface = currentSurface(), build = state.build) {
    return coreAvailableForShape(core, shape, build) && surfaceAvailableForShape(surface, shape, build);
  }
  const hasArtwork = () => Boolean(state.faces.front.layers.length || state.faces.back.layers.length);
  let cartSubmissionInProgress = false;
  const productForBuild = (build) => build === 'cold'
    ? {
        id: CFG.coldProductId,
        title: CFG.coldProductTitle || 'JOYSENT CUSTOM PADDLE — COLD PRESS',
        handle: CFG.coldProductHandle || 'joysent-custom-paddle-cold-press-v6'
      }
    : {
        id: CFG.hotProductId,
        title: CFG.hotProductTitle || 'JOYSENT CUSTOM PADDLE — THERMOFORMED',
        handle: CFG.hotProductHandle || 'joysent-custom-paddle-thermoformed-v6'
      };
  const productBindingIsValid = (build) => build === 'cold'
    ? CFG.coldProductBindingValid === true && String(CFG.coldResolvedProductId || '') === String(CFG.coldProductId || '')
    : CFG.hotProductBindingValid === true && String(CFG.hotResolvedProductId || '') === String(CFG.hotProductId || '');
  const variantsForBuild = (build) => build === 'cold' ? (CFG.coldVariants || []) : (CFG.hotVariants || []);
  const formatMoney = (cents) => new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: CFG.currency || 'USD'
  }).format(Number(cents || 0) / 100);
  const normalizeVariantOption = (value) => String(value || '')
    .normalize('NFKC')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  const variantOptionValue = (entry) => entry?.variantValue || entry?.label || '';
  function variantForSelection(selection = {}) {
    const build = selection.build || state.build;
    const shape = selection.shape || currentShape();
    const core = selection.core || currentCore();
    const surface = selection.surface || currentSurface();
    if (!selectionIsCompatible(shape, core, surface, build)) return null;
    const artwork = selection.artwork === undefined ? hasArtwork() : Boolean(selection.artwork);
    const wanted = build === 'cold'
      ? [variantOptionValue(shape), variantOptionValue(surface), artwork ? 'With Artwork' : 'No Artwork']
      : [variantOptionValue(shape), variantOptionValue(core), `${variantOptionValue(surface)} - ${artwork ? 'With Artwork' : 'No Artwork'}`];
    const normalizedWanted = wanted.map(normalizeVariantOption);
    const optionsFor = (variant) => (variant.options || [variant.option1, variant.option2, variant.option3])
      .filter(Boolean)
      .map(normalizeVariantOption);
    return variantsForBuild(build).find((variant) => {
      const options = optionsFor(variant);
      return normalizedWanted.every((value, index) => options[index] === value);
    }) || null;
  }
  const selectedVariant = () => variantForSelection();
  const compareAtPriceForBuild = (build = state.build) => Number(CFG.compareAtPrices?.[build] || (build === 'hot' ? 19900 : 9900));
  function minimumVariant(build) {
    const variants = variantsForBuild(build);
    return [...variants]
      .sort((a, b) => Number(a.price || 0) - Number(b.price || 0))[0] || null;
  }
  const checkoutVariants = () => CFG.checkoutVariants || [];
  const variantOptions = (variant) => (variant?.options || [variant?.option1, variant?.option2, variant?.option3])
    .filter(Boolean)
    .map(normalizeVariantOption);
  function checkoutVariantForSelection(sourceVariant = selectedVariant()) {
    if (!sourceVariant) return null;
    const sourcePrice = Number(sourceVariant.price || 0);
    const buildLabel = normalizeVariantOption(BUILDS[state.build]?.label);
    const matchingPrice = checkoutVariants().filter((variant) => (
      variant.available !== false && Number(variant.price || 0) === sourcePrice
    ));
    return matchingPrice.find((variant) => variantOptions(variant)[0] === buildLabel)
      || matchingPrice[0]
      || null;
  }
  function optionPriceLabel(candidate, baseline = selectedVariant()) {
    if (!candidate) return '';
    if (!baseline) return formatMoney(candidate.price);
    const difference = Number(candidate.price || 0) - Number(baseline.price || 0);
    if (difference > 0) return `+${formatMoney(difference)}`;
    if (difference < 0) return `−${formatMoney(Math.abs(difference))}`;
    return 'Included';
  }
  const storageKey = `joysent-customizer-${CFG.version || 'V7'}`;
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
    if (saved && ['cold', 'hot'].includes(saved.build)) {
      /*
       * Do not restore the two-sided artwork switch from a previous visit.
       * Uploaded image layers are intentionally not restored from localStorage,
       * so restoring only sync=true creates an invisible stale state: the
       * checkbox looks off while a new front upload is also painted on Back.
       * Mirroring must be explicitly enabled by the shopper in this session.
       */
      ['build', 'shape', 'core', 'surface', 'edge', 'ferrule', 'grip', 'recommendedSetup'].forEach((key) => {
        if (saved[key] !== undefined) state[key] = saved[key];
      });
    }
  } catch (error) {
    /* Storage is optional; cart properties remain the source of truth. */
  }
  function ensureColorSelections() {
    if (!EDGES.some((entry) => entry.id === state.edge && colorAvailableForBuild(entry, state.build))) {
      state.edge = firstColorForBuild(EDGES, state.build)?.id;
    }
    if (!FERRULES.some((entry) => entry.id === state.ferrule && colorAvailableForBuild(entry, state.build))) {
      state.ferrule = firstColorForBuild(FERRULES, state.build)?.id;
    }
    if (!GRIPS.some((entry) => entry.id === state.grip && colorAvailableForBuild(entry, state.build))) {
      state.grip = firstColorForBuild(GRIPS, state.build)?.id;
    }
  }
  ensureColorSelections();

  function applyThemeCopy() {
    const setText = (selector, value) => {
      const element = $(selector);
      if (element && value) element.textContent = value;
    };
    setText('#loading span', COPY.loadingLabel);
    ['viewFront', 'viewBack', 'viewSide', 'viewSpin'].forEach((key, index) => {
      const label = $$('.viewbtns button span')[index];
      if (label && COPY[key]) label.textContent = COPY[key];
    });
    setText('.hint', COPY.rotateHint);
    setText('.panel-eyebrow', COPY.panelEyebrow);
    setText('.p-head strong', COPY.panelHeading);
    setText('.panel-count', COPY.panelCount);
    setText('[data-build="cold"] .build-badge', COPY.coldBadge);
    setText('[data-build="cold"] .build-feel', COPY.coldCardCopy);
    setText('[data-build="hot"] .build-badge', COPY.hotBadge);
    setText('[data-build="hot"] .build-feel', COPY.hotCardCopy);
    setText('#imgVal', COPY.artworkEmpty);
    setText('#tabF', COPY.artworkFront);
    setText('#tabB', COPY.artworkBack);
    const horizontalCopy = String(COPY.horizontal || 'Horizontal').trim();
    const verticalCopy = String(COPY.vertical || 'Vertical').trim();
    const rotateCopy = String(COPY.rotate || 'Rotate').trim();
    const heightCopy = String(COPY.height || 'Height').trim();
    setText('#horizontalControlLabel', horizontalCopy.toLowerCase() === verticalCopy.toLowerCase() ? 'Horizontal' : horizontalCopy);
    setText('#verticalControlLabel', verticalCopy);
    setText('#sizeControlLabel', COPY.size || 'Size');
    setText('#widthControlLabel', COPY.width || 'Width');
    setText('#heightControlLabel', heightCopy.toLowerCase() === rotateCopy.toLowerCase() ? 'Height' : heightCopy);
    setText('#rotateControlLabel', rotateCopy);
    const syncLabel = $('#syncBF');
    if (syncLabel && COPY.syncBoth) {
      const checkbox = syncLabel.querySelector('input');
      syncLabel.textContent = COPY.syncBoth;
      if (checkbox) syncLabel.prepend(checkbox);
    }
    const subheadings = $$('.choice-subheading');
    if (subheadings[0] && COPY.ferruleLabel) subheadings[0].textContent = COPY.ferruleLabel;
    if (subheadings[1] && COPY.gripLabel) subheadings[1].textContent = COPY.gripLabel;
    setText('.result-step .sec-h .val', COPY.resultReady);
    setText('#previewBtn', COPY.previewButton);
    setText('#previewStatus', COPY.previewHelp);
    setText('#snapshotHeading', COPY.finalPreviewHeading);
    setText('#snapshotCopy', COPY.finalPreviewCopy);
    setText('#frontPreviewLabel', `${COPY.viewFront || 'Front'} View`);
    setText('#backPreviewLabel', `${COPY.viewBack || 'Back'} View`);
    setText('#downloadFrontPreview', COPY.downloadPreview || 'Download preview');
    setText('#downloadBackPreview', COPY.downloadPreview || 'Download preview');
    setText('.result-total > span', COPY.totalLabel);
    setText('.result-total small', COPY.totalHelp);
    setText('#checkoutBtn span', COPY.addToCart);
    setText('#wizardBack', COPY.backLabel);
    const experience = $$('.experience-strip span');
    [COPY.experienceOne, COPY.experienceTwo, COPY.experienceThree].forEach((value, index) => {
      if (!experience[index] || !value) return;
      const number = experience[index].querySelector('b');
      experience[index].textContent = ` ${value}`;
      if (number) experience[index].prepend(number);
    });
    $$('#heroSteps button').forEach((button, index) => {
      const copy = STEP_COPY[index];
      if (copy?.navLabel) button.querySelector('span').textContent = copy.navLabel;
    });
    $$('.wizard-step').forEach((step, index) => {
      const copy = STEP_COPY[index];
      if (!copy) return;
      const eyebrow = step.querySelector('.step-label');
      const heading = step.querySelector('.sec-h .tt');
      const description = step.querySelector('.sec-copy');
      if (eyebrow && copy.eyebrow) eyebrow.textContent = copy.eyebrow;
      if (heading && copy.heading) heading.textContent = copy.heading;
      if (description && copy.description) description.textContent = copy.description;
    });
    $$('#resultList button').forEach((button) => {
      const copy = STEP_COPY[Number(button.dataset.editStep)];
      if (copy?.navLabel) button.querySelector('span').textContent = copy.navLabel;
    });
  }

  /* ---------- Procedural materials ---------- */
  const FACE_W = 768;
  const FACE_H = 1694;
  const WEAVE_PX = 344;
  const faceCanvas = { front: document.createElement('canvas'), back: document.createElement('canvas') };
  faceCanvas.front.width = faceCanvas.back.width = FACE_W;
  faceCanvas.front.height = faceCanvas.back.height = FACE_H;
  const printMaskCanvas = { front: document.createElement('canvas'), back: document.createElement('canvas') };
  const maskedArtworkCanvas = { front: document.createElement('canvas'), back: document.createElement('canvas') };
  const printAreaBounds = {
    front: { x: 0, y: 0, width: FACE_W, height: FACE_H },
    back: { x: 0, y: 0, width: FACE_W, height: FACE_H }
  };
  const faceAspectCorrection = { front: 1, back: 1 };
  ['front', 'back'].forEach((which) => {
    printMaskCanvas[which].width = maskedArtworkCanvas[which].width = FACE_W;
    printMaskCanvas[which].height = maskedArtworkCanvas[which].height = FACE_H;
  });
  const normCanvas = document.createElement('canvas');
  const roughCanvas = document.createElement('canvas');
  normCanvas.width = roughCanvas.width = FACE_W;
  normCanvas.height = roughCanvas.height = FACE_H;
  const weaveImgs = {};
  let activeSurfaceImages = {};
  let scaledTiles = {};
  const surfaceTextureCache = new Map();
  const faceTextures = { front: [], back: [] };
  const faceMaterials = { front: [], back: [] };
  let normTexture = null;
  let roughTexture = null;

  function tileCanvas(size) {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    return canvas;
  }
  function setDefaultPrintMask(which) {
    const ctx = printMaskCanvas[which].getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, FACE_W, FACE_H);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, FACE_W, FACE_H);
    printAreaBounds[which] = { x: 0, y: 0, width: FACE_W, height: FACE_H };
  }
  ['front', 'back'].forEach(setDefaultPrintMask);

  function normalizedModelPartName(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\u4e00-\u9fff]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }
  function modelPartDescriptor(node) {
    const materials = Array.isArray(node?.material) ? node.material : [node?.material];
    return normalizedModelPartName([
      node?.name,
      node?.geometry?.name,
      ...materials.map((material) => material?.name)
    ].filter(Boolean).join('_'));
  }
  function modelPartScore(node, role) {
    const name = modelPartDescriptor(node);
    if (!name) return 0;
    const exact = {
      front: ['face_front', 'front_face', 'paddle_front', '正面', '前面'],
      back: ['face_back', 'back_face', 'paddle_back', '背面', '后面'],
      edge: ['edge_guard', 'border', 'edge', '边条', '护边'],
      handle: ['handle_wrap', 'handle', 'grip', '手柄', '手柄片'],
      ferrule: ['ferrule', '胶圈']
    }[role] || [];
    if (exact.includes(name)) return 100;
    if (role === 'front' && (name.endsWith('_face_front') || name.includes('face_front'))) return 95;
    if (role === 'back' && (name.endsWith('_face_back') || name.includes('face_back'))) return 95;
    if (role === 'edge' && (name.endsWith('_border') || name.includes('edge_guard') || name.includes('边条'))) return 90;
    if (role === 'handle' && (name.endsWith('_handle') || name.includes('handle_wrap') || name.includes('手柄片'))) return 90;
    if (role === 'ferrule' && (name.endsWith('_ferrule') || name.includes('ferrule') || name.includes('胶圈'))) return 90;
    return 0;
  }
  function findModelPart(model, role) {
    let best = null;
    let bestScore = 0;
    model?.traverse((node) => {
      const score = modelPartScore(node, role);
      if (score > bestScore) {
        best = node;
        bestScore = score;
      }
    });
    return best;
  }
  function ensurePlanarUv(mesh, mirrorX = false) {
    if (!mesh?.isMesh || !mesh.geometry) return;
    /*
     * The supplied paddle GLBs use several incompatible UV atlases. Some atlases
     * reserve large regions for bevels, which makes a full canvas appear only on
     * part of the face. Project the face from world X/Y instead so every shape
     * receives one predictable, complete printable rectangle.
     */
    mesh.geometry = mesh.geometry.clone();
    const geometry = mesh.geometry;
    const position = geometry.getAttribute('position');
    if (!position) return;
    mesh.updateWorldMatrix(true, false);
    const worldPoints = new Array(position.count);
    const point = new THREE.Vector3();
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (let index = 0; index < position.count; index += 1) {
      point.fromBufferAttribute(position, index).applyMatrix4(mesh.matrixWorld);
      const worldPoint = point.clone();
      worldPoints[index] = worldPoint;
      minX = Math.min(minX, worldPoint.x);
      maxX = Math.max(maxX, worldPoint.x);
      minY = Math.min(minY, worldPoint.y);
      maxY = Math.max(maxY, worldPoint.y);
    }
    const width = Math.max(.000001, maxX - minX);
    const height = Math.max(.000001, maxY - minY);
    const uv = new Float32Array(position.count * 2);
    for (let index = 0; index < position.count; index += 1) {
      const u = (worldPoints[index].x - minX) / width;
      uv[index * 2] = mirrorX ? 1 - u : u;
      uv[index * 2 + 1] = (worldPoints[index].y - minY) / height;
    }
    geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  }
  function faceMeshCandidates(model) {
    const modelBounds = new THREE.Box3().setFromObject(model);
    const modelSize = new THREE.Vector3();
    modelBounds.getSize(modelSize);
    const candidates = [];
    model?.traverse((node) => {
      if (!node?.isMesh || !node.geometry?.getAttribute('position')) return;
      const descriptor = modelPartDescriptor(node);
      if (/(edge|border|guard|handle|grip|ferrule|边条|护边|手柄|胶圈)/.test(descriptor)) return;
      const bounds = new THREE.Box3().setFromObject(node);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      bounds.getSize(size);
      bounds.getCenter(center);
      if (size.x < modelSize.x * .42 || size.y < modelSize.y * .32) return;
      const faceKeyword = /(face|surface|carbon|fiber|碳板|拍面|面板)/.test(descriptor);
      const areaRatio = (size.x / Math.max(modelSize.x, .000001)) * (size.y / Math.max(modelSize.y, .000001));
      candidates.push({
        node,
        centerZ: center.z,
        score: areaRatio * 100 + (faceKeyword ? 150 : 0)
      });
    });
    return candidates.sort((a, b) => b.score - a.score);
  }
  function resolveFaceNodes(model) {
    const resolved = {
      front: findModelPart(model, 'front'),
      back: findModelPart(model, 'back')
    };
    const used = new Set([resolved.front?.uuid, resolved.back?.uuid].filter(Boolean));
    const candidates = faceMeshCandidates(model).filter((candidate) => !used.has(candidate.node.uuid));
    if (!resolved.front && !resolved.back && candidates.length >= 2) {
      const likelyFaces = candidates.slice(0, Math.min(4, candidates.length));
      resolved.front = [...likelyFaces].sort((a, b) => b.centerZ - a.centerZ)[0]?.node || null;
      resolved.back = [...likelyFaces]
        .filter((candidate) => candidate.node !== resolved.front)
        .sort((a, b) => a.centerZ - b.centerZ)[0]?.node || null;
    } else {
      if (!resolved.front) resolved.front = candidates.shift()?.node || null;
      if (!resolved.back) resolved.back = candidates.shift()?.node || null;
    }
    if (!resolved.front && resolved.back) {
      resolved.front = resolved.back;
      resolved.back = null;
    }
    return resolved;
  }

  function buildPrintableMasks(model, faceNodes) {
    /*
     * The mesh clips the final texture to the paddle silhouette. Placement and
     * contain-scaling still need the face-only rectangle: several supplied GLBs
     * include the handle in the front/back surface bounds.
     */
    ['front', 'back'].forEach(setDefaultPrintMask);
    const handle = findModelPart(model, 'handle');
    const ferrule = model.getObjectByName(CFG.ferruleNodeName || 'joysent001') || findModelPart(model, 'ferrule');
    const handleBounds = handle ? new THREE.Box3().setFromObject(handle) : null;
    const ferruleBounds = ferrule ? new THREE.Box3().setFromObject(ferrule) : null;
    const faceStartY = Math.max(
      Number.isFinite(handleBounds?.max?.y) ? handleBounds.max.y : -Infinity,
      Number.isFinite(ferruleBounds?.max?.y) ? ferruleBounds.max.y : -Infinity
    );
    ['front', 'back'].forEach((which) => {
      const node = faceNodes?.[which];
      if (!node || !Number.isFinite(faceStartY)) return;
      const bounds = new THREE.Box3().setFromObject(node);
      const totalHeight = bounds.max.y - bounds.min.y;
      if (totalHeight <= .000001 || faceStartY <= bounds.min.y || faceStartY >= bounds.max.y) return;
      const faceHeightRatio = Math.max(.45, Math.min(1, (bounds.max.y - faceStartY) / totalHeight));
      printAreaBounds[which] = {
        x: 0,
        y: 0,
        width: FACE_W,
        height: Math.round(FACE_H * faceHeightRatio)
      };
    });
  }
  function tow(ctx, x, y, w, h, horizontal, light, dark) {
    const gradient = horizontal ? ctx.createLinearGradient(x, y, x, y + h) : ctx.createLinearGradient(x, y, x + w, y);
    gradient.addColorStop(0, light);
    gradient.addColorStop(0.5, dark);
    gradient.addColorStop(1, light);
    ctx.fillStyle = gradient;
    ctx.fillRect(x, y, w, h);
  }
  function grain(ctx, size) {
    const data = ctx.getImageData(0, 0, size, size);
    for (let i = 0; i < data.data.length; i += 4) {
      const noise = (Math.random() - 0.5) * 14;
      data.data[i] += noise;
      data.data[i + 1] += noise;
      data.data[i + 2] += noise;
    }
    ctx.putImageData(data, 0, 0);
  }
  function makeWeave3K() {
    const canvas = tileCanvas(64);
    const ctx = canvas.getContext('2d');
    const unit = 16;
    for (let x = 0; x < 4; x += 1) {
      for (let y = 0; y < 4; y += 1) {
        const horizontal = (x + y) % 2 === 0;
        tow(ctx, x * unit, y * unit, unit, unit, horizontal, '#141518', horizontal ? '#33363c' : '#2a2d33');
      }
    }
    grain(ctx, 64);
    return canvas;
  }
  function makeWeave12K() {
    const canvas = tileCanvas(96);
    const ctx = canvas.getContext('2d');
    const unit = 24;
    for (let x = -1; x < 5; x += 1) {
      for (let y = -1; y < 5; y += 1) {
        const horizontal = ((x + y) % 4 + 4) % 4 < 2;
        tow(ctx, x * unit, y * unit, unit, unit, horizontal, '#101114', horizontal ? '#3a3e45' : '#2c2f35');
      }
    }
    grain(ctx, 96);
    return canvas;
  }
  function selectedPattern() {
    return currentSurface()?.pattern || '3k';
  }
  function selectedSurfaceImages() {
    return {
      albedo: activeSurfaceImages.albedo || (selectedPattern() === 'albedo' ? weaveImgs.albedo : null),
      normal: activeSurfaceImages.normal || (selectedPattern() === 'albedo' ? weaveImgs.normal : null),
      rough: activeSurfaceImages.rough || (selectedPattern() === 'albedo' ? weaveImgs.rough : null)
    };
  }
  function surfaceTextureTransform(image) {
    const surface = currentSurface() || {};
    const size = Math.min(3, Math.max(.25, Number(surface.textureSize || 100) / 100));
    const widthCorrection = Math.min(2, Math.max(.5, Number(surface.textureWidth || 100) / 100));
    const heightCorrection = Math.min(2, Math.max(.5, Number(surface.textureHeight || 100) / 100));
    const rotation = ((Number(surface.textureRotation || 0) % 360) + 360) % 360;
    const sourceRatio = Math.max(.01, Number(image?.height || 1) / Math.max(1, Number(image?.width || 1)));
    return {
      width: Math.max(8, Math.round(WEAVE_PX * size * widthCorrection)),
      height: Math.max(8, Math.round(WEAVE_PX * sourceRatio * size * heightCorrection)),
      rotation
    };
  }
  function makeSurfaceTile(image) {
    const transform = surfaceTextureTransform(image);
    const quarterTurn = transform.rotation === 90 || transform.rotation === 270;
    const canvas = document.createElement('canvas');
    canvas.width = quarterTurn ? transform.height : transform.width;
    canvas.height = quarterTurn ? transform.width : transform.height;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(transform.rotation * Math.PI / 180);
    ctx.drawImage(
      image,
      -transform.width / 2,
      -transform.height / 2,
      transform.width,
      transform.height
    );
    return canvas;
  }
  function getAlbedoPattern(ctx) {
    const images = selectedSurfaceImages();
    if (images.albedo) {
      if (!scaledTiles.albedo) {
        scaledTiles.albedo = makeSurfaceTile(images.albedo);
      }
      return ctx.createPattern(scaledTiles.albedo, 'repeat');
    }
    return ctx.createPattern(selectedPattern() === '12k' ? makeWeave12K() : makeWeave3K(), 'repeat');
  }
  function composeAuxMaps() {
    const normalCtx = normCanvas.getContext('2d');
    const roughCtx = roughCanvas.getContext('2d');
    const images = selectedSurfaceImages();
    if (images.normal) {
      if (!scaledTiles.normal) scaledTiles.normal = makeSurfaceTile(images.normal);
      normalCtx.fillStyle = normalCtx.createPattern(scaledTiles.normal, 'repeat');
      normalCtx.fillRect(0, 0, FACE_W, FACE_H);
    } else {
      normalCtx.fillStyle = '#8080ff';
      normalCtx.fillRect(0, 0, FACE_W, FACE_H);
    }
    if (images.rough) {
      if (!scaledTiles.rough) scaledTiles.rough = makeSurfaceTile(images.rough);
      roughCtx.fillStyle = roughCtx.createPattern(scaledTiles.rough, 'repeat');
      roughCtx.fillRect(0, 0, FACE_W, FACE_H);
    } else {
      roughCtx.fillStyle = '#9e9e9e';
      roughCtx.fillRect(0, 0, FACE_W, FACE_H);
    }
    if (normTexture) normTexture.needsUpdate = true;
    if (roughTexture) roughTexture.needsUpdate = true;
    requestRender();
  }
  function loadTextureImage(url) {
    if (!url) return Promise.resolve(null);
    return new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = () => resolve(image);
      image.onerror = () => resolve(null);
      image.src = url;
    });
  }
  function textureSourceKey(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.href).pathname.toLowerCase();
    } catch (error) {
      return String(url).split('?')[0].toLowerCase();
    }
  }
  function activateSurfaceTextures(surface = currentSurface()) {
    if (!surface) return Promise.resolve();
    const key = surface.id;
    const cached = surfaceTextureCache.get(key);
    const apply = (images) => {
      if (currentSurface()?.id !== key) return;
      activeSurfaceImages = images || {};
      scaledTiles = {};
      composeAuxMaps();
      composeFace('front');
      composeFace('back');
      buildSurfaceUI(false);
    };
    if (cached) {
      apply(cached);
      return Promise.resolve();
    }
    const albedoKey = textureSourceKey(surface.albedoUrl);
    const normalUrl = textureSourceKey(surface.normalUrl) === albedoKey ? '' : surface.normalUrl;
    const roughUrl = [albedoKey, textureSourceKey(normalUrl)].includes(textureSourceKey(surface.roughUrl)) ? '' : surface.roughUrl;
    return Promise.all([
      loadTextureImage(surface.albedoUrl),
      loadTextureImage(normalUrl),
      loadTextureImage(roughUrl)
    ]).then(([albedo, normal, rough]) => {
      const images = { albedo, normal, rough };
      surfaceTextureCache.set(key, images);
      apply(images);
    });
  }
  function artworkSource(layer) {
    return layer?.img || null;
  }
  function clipTemplateFrame(ctx, frame) {
    if (!frame) return;
    const x = Number(frame.x || 0) / 100 * FACE_W;
    const y = Number(frame.y || 0) / 100 * FACE_H;
    const width = Number(frame.width || 100) / 100 * FACE_W;
    const height = Number(frame.height || 100) / 100 * FACE_H;
    const radius = Math.min(width, height) * (frame.shape === 'arch' ? .22 : .04);
    ctx.beginPath();
    if (frame.shape === 'ellipse') {
      ctx.ellipse(x + width / 2, y + height / 2, width / 2, height / 2, 0, 0, Math.PI * 2);
    } else {
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      ctx.lineTo(x + radius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
    }
    ctx.closePath();
    ctx.clip();
  }
  function drawArtworkLayer(ctx, layer, which) {
    const source = artworkSource(layer);
    if (!source) return;
    const sourceWidth = Math.max(1, source.naturalWidth || source.width || 1);
    const sourceHeight = Math.max(1, source.naturalHeight || source.height || 1);
    ctx.save();
    if (layer.slotFrame) clipTemplateFrame(ctx, layer.slotFrame);
    if (layer.fixedCanvas) {
      ctx.drawImage(source, 0, 0, FACE_W, FACE_H);
      ctx.restore();
      return;
    }
    const fitToPrintArea = layer.fitMode === 'contain-print-area';
    const bounds = printAreaBounds[which] || printAreaBounds.front;
    const physicalHeightCorrection = Math.max(.5, Math.min(2, Number(faceAspectCorrection[which]) || 1));
    const correctedSourceHeight = sourceHeight * physicalHeightCorrection;
    const centerX = fitToPrintArea
      ? bounds.x + bounds.width / 2 + layer.x / 100 * bounds.width * .5
      : FACE_W / 2 + layer.x / 100 * FACE_W;
    /* Photoshop-style canvas coordinates: positive Y moves artwork down. */
    const centerY = fitToPrintArea
      ? bounds.y + bounds.height / 2 + layer.y / 100 * bounds.height * .5
      : FACE_H * 0.30 + layer.y / 100 * FACE_H * 0.6;
    ctx.translate(centerX, centerY);
    ctx.rotate(layer.r * Math.PI / 180);
    const baseScale = fitToPrintArea
      ? Math.min(bounds.width / sourceWidth, bounds.height / correctedSourceHeight)
      : Math.min(FACE_W / sourceWidth, FACE_W * 1.4 / correctedSourceHeight);
    const widthScale = baseScale * ((layer.w ?? layer.s ?? 85) / 100);
    const heightScale = baseScale * physicalHeightCorrection * ((layer.h ?? layer.s ?? 85) / 100);
    ctx.drawImage(
      source,
      -sourceWidth * widthScale / 2,
      -sourceHeight * heightScale / 2,
      sourceWidth * widthScale,
      sourceHeight * heightScale
    );
    ctx.restore();
  }
  function renderMaskedArtwork(which, layers) {
    const canvas = maskedArtworkCanvas[which];
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, FACE_W, FACE_H);
    layers
      .map((layer, index) => ({ layer, index }))
      .sort((a, b) => Number(a.layer.z || 50) - Number(b.layer.z || 50) || a.index - b.index)
      .forEach(({ layer }) => drawArtworkLayer(ctx, layer, which));
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(printMaskCanvas[which], 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    return canvas;
  }
  function composeFace(which) {
    const cfg = which === 'back' && state.sync ? state.faces.front : state.faces[which];
    const ctx = faceCanvas[which].getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = getAlbedoPattern(ctx);
    ctx.fillRect(0, 0, FACE_W, FACE_H);
    ctx.drawImage(renderMaskedArtwork(which, cfg.layers), 0, 0);
    const sheen = ctx.createLinearGradient(0, 0, FACE_W, FACE_H);
    sheen.addColorStop(0, 'rgba(255,255,255,.04)');
    sheen.addColorStop(0.5, 'rgba(255,255,255,0)');
    sheen.addColorStop(1, 'rgba(0,0,0,.05)');
    ctx.fillStyle = sheen;
    ctx.fillRect(0, 0, FACE_W, FACE_H);
    faceTextures[which].forEach((texture) => {
      texture.needsUpdate = true;
    });
    faceMaterials[which].forEach((material) => {
      if (material.map) material.map.needsUpdate = true;
      material.color.set('#ffffff');
      material.needsUpdate = true;
    });
    requestRender();
  }
  function gripWrap(base) {
    const canvas = tileCanvas(256);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, 256, 256);
    ctx.strokeStyle = 'rgba(0,0,0,.55)';
    ctx.lineWidth = 7;
    for (let y = -256; y < 512; y += 42) {
      ctx.beginPath();
      ctx.moveTo(-20, y);
      ctx.lineTo(276, y + 120);
      ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(255,255,255,.08)';
    ctx.lineWidth = 2;
    for (let y = -256; y < 512; y += 42) {
      ctx.beginPath();
      ctx.moveTo(-20, y + 8);
      ctx.lineTo(276, y + 128);
      ctx.stroke();
    }
    grain(ctx, 256);
    return canvas;
  }

  /* ---------- Three.js scene ---------- */
  const stage = $('#stage');
  const loading = $('#loading');
  const modelRetry = $('#modelRetry');
  const previewExpand = $('#previewExpand');
  const previewClose = $('#previewClose');
  const previewPipDrag = $('#previewPipDrag');
  const STUDIO_BACKGROUND_HEX = 0xfbfbfa;
  const STUDIO_BACKGROUND_CSS = '#fbfbfa';
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    depth: true,
    stencil: false,
    powerPreference: 'high-performance',
    preserveDrawingBuffer: false
  });
  const deviceProfile = () => {
    const mobile = window.innerWidth <= 820;
    const dpr = Math.max(1, Number(window.devicePixelRatio) || 1);
    const memory = Number(navigator.deviceMemory || 0);
    const cores = Number(navigator.hardwareConcurrency || 0);
    const constrained = (memory > 0 && memory <= 4) || (cores > 0 && cores <= 4);
    return { mobile, dpr, constrained };
  };
  const targetPixelRatio = (quality = 'still') => {
    const { mobile, dpr, constrained } = deviceProfile();
    if (quality === 'interaction') {
      return Math.min(dpr, mobile ? (constrained ? 0.85 : 1) : (constrained ? 1 : 1.2));
    }
    return Math.min(dpr, mobile ? (constrained ? 1.15 : 1.4) : (constrained ? 1.35 : 1.75));
  };
  let renderQuality = 'still';
  let qualityRestoreTimer = 0;
  function setRenderQuality(nextQuality, restoreDelay = 0) {
    window.clearTimeout(qualityRestoreTimer);
    qualityRestoreTimer = 0;
    const apply = () => {
      const nextRatio = targetPixelRatio(nextQuality);
      renderQuality = nextQuality;
      if (Math.abs(renderer.getPixelRatio() - nextRatio) > 0.01) {
        renderer.setPixelRatio(nextRatio);
        if (stage.clientWidth && stage.clientHeight) renderer.setSize(stage.clientWidth, stage.clientHeight, false);
      }
      requestRender();
    };
    if (restoreDelay > 0) qualityRestoreTimer = window.setTimeout(apply, restoreDelay);
    else apply();
  }
  renderer.setPixelRatio(targetPixelRatio('still'));
  renderer.outputEncoding = THREE.sRGBEncoding;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1;
  renderer.setClearColor(STUDIO_BACKGROUND_HEX, 0);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.shadowMap.autoUpdate = false;
  stage.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(25, 1, 0.1, 100);
  camera.position.set(0, 0.6, 22);
  const root = new THREE.Group();
  scene.add(root);
  let currentModel = null;
  let activeModelUrl = '';
  let shapeLoadingUrl = '';
  let shapeLoadSerial = 0;
  let currentCoreModel = null;
  let activeCoreModelUrl = '';
  let coreLoadingUrl = '';
  let coreLoadSerial = 0;
  let coreAbortController = null;
  let shapeTransform = { scale: 1, position: new THREE.Vector3() };
  let coreTransform = { scale: 1, position: new THREE.Vector3() };
  let modelPresentationMode = 'shape';
  let modelLoadingOwner = '';
  let modelTransitions = [];
  let cameraFramingIsAutomatic = true;
  let cameraFitFrame = 0;
  let paddleParts = {};
  let needsRender = true;
  let sceneAnimationFrame = 0;
  let sceneVisible = true;
  let viewerDisposed = false;
  const sceneLifecycle = new AbortController();
  const requestRender = () => {
    needsRender = true;
    startSceneAnimation();
  };
  const CORE_STEP_INDEX = 2;
  const reduceModelMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  function makeEnvironment() {
    const canvas = document.createElement('canvas');
    canvas.width = 768;
    canvas.height = 384;
    const ctx = canvas.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, '#cbd0cd');
    gradient.addColorStop(0.5, '#747b77');
    gradient.addColorStop(1, '#aeb4b0');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    /* This is a reflection-only light rig. The darker flags define white
     * plastic edges; the white cards create broad photographic highlights.
     * None of these panels are rendered as the visible page background. */
    [
      [0, 20, 118, 344, 'rgba(23,28,26,.78)'],
      [512, 72, 96, 278, 'rgba(28,33,31,.68)'],
      [704, 24, 64, 338, 'rgba(34,39,37,.54)']
    ].forEach(([x, y, width, height, color]) => {
      const flag = ctx.createLinearGradient(x, y, x + width, y);
      flag.addColorStop(0, color);
      flag.addColorStop(1, 'rgba(80,87,83,.05)');
      ctx.fillStyle = flag;
      ctx.fillRect(x, y, width, height);
    });
    [
      [82, 32, 232, 330, 'rgba(255,255,255,.98)'],
      [302, 14, 254, 112, 'rgba(255,255,255,.96)'],
      [620, 42, 92, 314, 'rgba(255,255,255,.84)'],
      [278, 288, 270, 64, 'rgba(255,255,255,.54)']
    ].forEach(([x, y, width, height, color]) => {
      const panel = ctx.createLinearGradient(x, y, x + width, y + height);
      panel.addColorStop(0, color);
      panel.addColorStop(1, 'rgba(255,255,255,.08)');
      ctx.fillStyle = panel;
      ctx.fillRect(x, y, width, height);
    });
    const texture = new THREE.CanvasTexture(canvas);
    texture.encoding = THREE.sRGBEncoding;
    texture.mapping = THREE.EquirectangularReflectionMapping;
    const pmrem = new THREE.PMREMGenerator(renderer);
    pmrem.compileEquirectangularShader();
    const environment = pmrem.fromEquirectangular(texture).texture;
    texture.dispose();
    pmrem.dispose();
    return environment;
  }
  scene.environment = makeEnvironment();
  scene.add(new THREE.HemisphereLight(0xffffff, 0xc7ceca, 0.38));
  const keyLight = new THREE.DirectionalLight(0xfffdf8, 1.05);
  keyLight.position.set(5.8, 8.5, 10);
  keyLight.castShadow = true;
  const shadowResolution = deviceProfile().mobile ? 512 : 1024;
  keyLight.shadow.mapSize.set(shadowResolution, shadowResolution);
  keyLight.shadow.camera.left = -9;
  keyLight.shadow.camera.right = 9;
  keyLight.shadow.camera.top = 9;
  keyLight.shadow.camera.bottom = -9;
  keyLight.shadow.camera.near = 1;
  keyLight.shadow.camera.far = 32;
  keyLight.shadow.bias = -0.00035;
  keyLight.shadow.normalBias = 0.035;
  keyLight.shadow.radius = deviceProfile().mobile ? 2 : 4;
  scene.add(keyLight);
  const fillLight = new THREE.DirectionalLight(0xf4f7ff, 0.26);
  fillLight.position.set(-6, 3.5, 7);
  scene.add(fillLight);
  const rimLight = new THREE.DirectionalLight(0xffffff, 0.52);
  rimLight.position.set(-6, 4.5, -7);
  scene.add(rimLight);

  const shadowCatcherMaterial = new THREE.ShadowMaterial({
    color: 0xa8afab,
    opacity: deviceProfile().mobile ? 0.075 : 0.09,
    transparent: true,
    depthWrite: false
  });
  const shadowCatcher = new THREE.Mesh(new THREE.PlaneGeometry(24, 24), shadowCatcherMaterial);
  shadowCatcher.position.set(0, 0, -0.9);
  shadowCatcher.receiveShadow = true;
  shadowCatcher.renderOrder = -20;
  scene.add(shadowCatcher);

  function makeContactShadowTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 96;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.scale(1, 0.38);
    const gradient = ctx.createRadialGradient(128, 126, 4, 128, 126, 112);
    gradient.addColorStop(0, 'rgba(255,255,255,.82)');
    gradient.addColorStop(0.36, 'rgba(255,255,255,.36)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 256, 252);
    ctx.restore();
    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = false;
    return texture;
  }
  const contactShadowTexture = makeContactShadowTexture();
  const contactShadow = new THREE.Sprite(new THREE.SpriteMaterial({
    map: contactShadowTexture,
    color: 0x7d8580,
    opacity: deviceProfile().mobile ? 0.09 : 0.11,
    transparent: true,
    depthWrite: false,
    depthTest: true
  }));
  contactShadow.position.set(0, -5.55, -0.72);
  contactShadow.scale.set(4.2, 0.62, 1);
  contactShadow.renderOrder = -10;
  scene.add(contactShadow);

  function b64ToBuffer(base64) {
    const binary = atob(base64);
    const buffer = new ArrayBuffer(binary.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < binary.length; i += 1) view[i] = binary.charCodeAt(i);
    return buffer;
  }
  function setModelBadge(shape, placeholder) {
    const box = $('#moldTabs');
    box.innerHTML = '';
    const badge = document.createElement('button');
    badge.type = 'button';
    badge.className = 'on';
    badge.innerHTML = `<span class="n">${shape.code || shape.label}</span><span class="d">${placeholder ? (COPY.modelPlaceholder || 'Model placeholder') : (COPY.modelConnected || '3D model ready')}</span>`;
    box.appendChild(badge);
  }
  function showModelError(message) {
    modelLoadingOwner = 'shape';
    loading.style.display = 'flex';
    loading.classList.add('is-error');
    loading.querySelector('span').textContent = message || 'Your paddle preview could not load.';
    modelRetry.hidden = false;
    refreshResult();
  }
  function showModelLoading(message, owner = 'shape') {
    modelLoadingOwner = owner;
    loading.style.display = 'flex';
    loading.classList.remove('is-error');
    modelRetry.hidden = true;
    loading.querySelector('span').textContent = message || (COPY.loadingLabel || 'CREATING YOUR PADDLE');
  }
  function hideModelLoading(owner = '') {
    if (owner && modelLoadingOwner && modelLoadingOwner !== owner) return;
    loading.style.display = 'none';
    loading.classList.remove('is-error');
    modelRetry.hidden = true;
    modelLoadingOwner = '';
  }
  function disposeMaterial(material, disposedTextures, protectedTextures = null) {
    if (!material) return;
    Object.keys(material).forEach((key) => {
      const value = material[key];
      if (!value?.isTexture || disposedTextures.has(value) || protectedTextures?.has(value)) return;
      disposedTextures.add(value);
      value.dispose();
    });
    material.dispose?.();
  }
  function disposeObject(object) {
    if (!object) return;
    root.remove(object);
    const disposedTextures = new Set();
    const disposedMaterials = new Set();
    const disposedGeometries = new Set();
    object.traverse((node) => {
      if (!node.isMesh) return;
      if (node.geometry && !disposedGeometries.has(node.geometry)) {
        disposedGeometries.add(node.geometry);
        node.geometry.dispose?.();
      }
      const materials = Array.isArray(node.material) ? node.material : [node.material];
      materials.forEach((material) => {
        if (!material || disposedMaterials.has(material)) return;
        disposedMaterials.add(material);
        disposeMaterial(material, disposedTextures);
      });
    });
  }
  function disposeCoreModel() {
    modelTransitions = modelTransitions.filter((transition) => transition.object !== currentCoreModel);
    disposeObject(currentCoreModel);
    currentCoreModel = null;
    activeCoreModelUrl = '';
    coreLoadingUrl = '';
  }
  function cancelCorePreviewLoad(disposeLoadedModel = true) {
    coreLoadSerial += 1;
    if (coreAbortController) {
      coreAbortController.abort();
      coreAbortController = null;
    }
    coreLoadingUrl = '';
    hideModelLoading('core');
    if (disposeLoadedModel) disposeCoreModel();
  }
  function disposeCurrentModel() {
    if (!currentModel) return;
    modelTransitions = modelTransitions.filter((transition) => transition.object !== currentModel);
    disposeObject(currentModel);
    currentModel = null;
    activeModelUrl = '';
    shapeLoadingUrl = '';
    faceTextures.front.length = 0;
    faceTextures.back.length = 0;
    faceMaterials.front.length = 0;
    faceMaterials.back.length = 0;
    normTexture = null;
    roughTexture = null;
  }
  function fittedTransform(model, targetHeight) {
    model.scale.setScalar(1);
    model.position.set(0, 0, 0);
    model.updateMatrixWorld(true);
    const bounds = new THREE.Box3().setFromObject(model);
    const size = bounds.getSize(new THREE.Vector3());
    const center = bounds.getCenter(new THREE.Vector3());
    const scale = targetHeight / Math.max(size.y, 0.001);
    return {
      scale,
      position: new THREE.Vector3(-center.x * scale, -center.y * scale, -center.z * scale)
    };
  }
  function applyTransform(object, transform) {
    if (!object || !transform) return;
    object.scale.setScalar(transform.scale);
    object.position.copy(transform.position);
  }
  function fitCameraToObject(object, transform) {
    if (!object || !transform || !stage.clientWidth || !stage.clientHeight) return;

    /* Measure the model at its final presentation transform. This avoids
       framing the smaller entrance-animation scale and keeps long handles
       inside the viewport on first load. */
    const previousScale = object.scale.clone();
    const previousPosition = object.position.clone();
    applyTransform(object, transform);
    root.updateMatrixWorld(true);
    object.updateWorldMatrix(true, true);
    const bounds = new THREE.Box3().setFromObject(object);
    object.scale.copy(previousScale);
    object.position.copy(previousPosition);
    root.updateMatrixWorld(true);
    object.updateWorldMatrix(true, true);
    if (bounds.isEmpty()) return;

    const size = bounds.getSize(new THREE.Vector3());
    const center = bounds.getCenter(new THREE.Vector3());
    /* A restrained floor contact shadow grounds the object without creating a
     * visible platform. Reframe it to the active shape/core automatically. */
    contactShadow.position.x = center.x;
    contactShadow.position.y = bounds.min.y + Math.min(0.16, size.y * 0.015);
    contactShadow.scale.set(
      Math.max(2.4, Math.min(5.2, size.x * 0.82)),
      Math.max(0.42, Math.min(0.68, size.x * 0.12)),
      1
    );
    const verticalFov = THREE.MathUtils.degToRad(camera.fov);
    const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * Math.max(camera.aspect, 0.01));
    const distanceForHeight = (size.y / 2) / Math.tan(verticalFov / 2);
    const distanceForWidth = (size.x / 2) / Math.tan(horizontalFov / 2);
    const mobileMargin = window.matchMedia?.('(max-width: 820px)')?.matches ? 1.17 : 1.12;
    const distance = Math.max(10, Math.min(34,
      Math.max(distanceForHeight, distanceForWidth) * mobileMargin + size.z / 2
    ));

    camera.position.set(center.x, center.y, center.z + distance);
    camera.lookAt(center);
    camera.updateProjectionMatrix();
    requestRender();
  }
  function fitCameraToPresentation() {
    const showCore = modelPresentationMode === 'core' && currentCoreModel;
    fitCameraToObject(showCore ? currentCoreModel : currentModel, showCore ? coreTransform : shapeTransform);
  }
  function scheduleCameraFit() {
    if (!cameraFramingIsAutomatic) return;
    if (cameraFitFrame) cancelAnimationFrame(cameraFitFrame);
    cameraFitFrame = requestAnimationFrame(() => {
      cameraFitFrame = 0;
      fitCameraToPresentation();
    });
  }
  function transitionModel(object, show, transform, duration = 380) {
    if (!object || !transform) return;
    modelTransitions = modelTransitions.filter((transition) => transition.object !== object);
    if (reduceModelMotion) {
      applyTransform(object, transform);
      object.visible = show;
      requestRender();
      return;
    }
    const targetScale = show ? transform.scale : transform.scale * 0.86;
    const targetPosition = transform.position.clone();
    if (!show) targetPosition.y -= 0.32;
    if (show) {
      object.visible = true;
      if (!object.userData.joysentPresented) {
        object.scale.setScalar(transform.scale * 0.84);
        object.position.copy(transform.position);
        object.position.y -= 0.38;
      }
    }
    object.userData.joysentPresented = true;
    modelTransitions.push({
      object,
      hideAfter: !show,
      startedAt: performance.now(),
      duration,
      fromScale: object.scale.x,
      toScale: targetScale,
      fromPosition: object.position.clone(),
      toPosition: targetPosition
    });
    requestRender();
  }
  function updateModelTransitions(now) {
    if (!modelTransitions.length) return false;
    modelTransitions = modelTransitions.filter((transition) => {
      if (!transition.object?.parent) return false;
      const elapsed = Math.max(0, Math.min(1, (now - transition.startedAt) / transition.duration));
      const eased = 1 - Math.pow(1 - elapsed, 3);
      transition.object.scale.setScalar(
        transition.fromScale + (transition.toScale - transition.fromScale) * eased
      );
      transition.object.position.lerpVectors(transition.fromPosition, transition.toPosition, eased);
      if (elapsed < 1) return true;
      if (transition.hideAfter) transition.object.visible = false;
      return false;
    });
    return modelTransitions.length > 0;
  }
  function setCoreModelBadge(core, status) {
    const box = $('#moldTabs');
    if (!box || !core) return;
    box.innerHTML = '';
    const badge = document.createElement('button');
    badge.type = 'button';
    badge.className = 'on';
    badge.innerHTML = `<span class="n">${core.code || core.label}</span><span class="d">${status}</span>`;
    box.appendChild(badge);
  }
  function appendCoreModelNotice(message) {
    if (!coreNote || !message) return;
    coreNote.querySelector('[data-core-model-notice]')?.remove();
    const notice = document.createElement('span');
    notice.dataset.coreModelNotice = '';
    notice.innerHTML = `<br><b>3D preview:</b> ${message}`;
    coreNote.appendChild(notice);
  }
  function isCoreStepActive() {
    return typeof currentStep === 'number' && currentStep === CORE_STEP_INDEX;
  }
  function syncModelPresentation(animate = true) {
    const wantsCore = isCoreStepActive();
    const canShowCore = wantsCore && Boolean(currentCoreModel);
    const nextMode = canShowCore ? 'core' : 'shape';
    const modeChanged = nextMode !== modelPresentationMode;
    modelPresentationMode = nextMode;
    const transition = animate ? transitionModel : (object, show, transform) => {
      if (!object || !transform) return;
      applyTransform(object, transform);
      object.visible = show;
      requestRender();
    };
    transition(currentModel, nextMode === 'shape', shapeTransform);
    transition(currentCoreModel, nextMode === 'core', coreTransform);
    if (nextMode === 'core') {
      setCoreModelBadge(currentCore(), 'INTERNAL CORE PREVIEW');
      hideModelLoading('core');
      if (modeChanged && !reduceModelMotion) {
        autoSpin = false;
        targetRotationX = 0;
        targetRotationY = root.rotation.y + 0.72;
        setViewButton(null);
      }
    } else {
      setModelBadge(currentShape(), !currentShape()?.modelUrl || activeModelUrl === '__embedded_placeholder__');
    }
    if (modeChanged) {
      cameraFramingIsAutomatic = true;
      scheduleCameraFit();
    }
    requestRender();
  }
  function restorePaddlePreview() {
    cancelCorePreviewLoad(true);
    syncModelPresentation(true);
    if (currentModel) {
      hideModelLoading('core');
      return;
    }
    if (shapeLoadingUrl) {
      const shape = currentShape();
      showModelLoading(shape?.modelUrl ? `LOADING ${shape.code} MODEL` : 'LOADING PLACEHOLDER MODEL', 'shape');
      return;
    }
    loadSelectedShapeModel();
  }
  function glbResourcePath(url) {
    try {
      return new URL('.', url, window.location.href).href;
    } catch (_) {
      return '';
    }
  }
  const gltfLoader = new THREE.GLTFLoader();
  const glbBufferCache = new Map();
  const MAX_CACHED_GLB_BUFFERS = 3;
  function cachedGlbBuffer(url) {
    if (glbBufferCache.has(url)) return glbBufferCache.get(url);
    const pending = fetch(url, { cache: 'force-cache', credentials: 'same-origin' })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const buffer = await response.arrayBuffer();
        if (buffer.byteLength < 20) throw new Error('GLB file is empty or incomplete');
        const header = new DataView(buffer, 0, 12);
        const magic = header.getUint32(0, true);
        const version = header.getUint32(4, true);
        const declaredLength = header.getUint32(8, true);
        if (magic !== 0x46546c67 || version !== 2 || declaredLength !== buffer.byteLength) {
          throw new Error('GLB binary is corrupted. Re-upload the original file through Shopify Files and paste its CDN URL into this option.');
        }
        while (glbBufferCache.size > MAX_CACHED_GLB_BUFFERS) {
          const oldestUrl = glbBufferCache.keys().next().value;
          if (!oldestUrl || oldestUrl === url) break;
          glbBufferCache.delete(oldestUrl);
        }
        return buffer;
      })
      .catch((error) => {
        glbBufferCache.delete(url);
        throw error;
      });
    glbBufferCache.set(url, pending);
    return pending;
  }
  async function loadGlbScene(url, signal) {
    if (signal?.aborted) throw new DOMException('The model request was cancelled.', 'AbortError');
    const buffer = await cachedGlbBuffer(url);
    if (signal?.aborted) throw new DOMException('The model request was cancelled.', 'AbortError');
    return new Promise((resolve, reject) => {
      gltfLoader.parse(buffer, glbResourcePath(url), (gltf) => resolve(gltf.scene), reject);
    });
  }
  /*
   * Theme color settings and Canvas colors are authored in sRGB.
   * Three.js r128 expects material.color values in linear space when the
   * renderer outputs sRGB. Convert solid UI colors once so solid edge/ferrule
   * colors follow the same pipeline as the sRGB grip CanvasTexture.
   */
  function setMaterialColorFromSrgb(material, value) {
    if (!material?.color) return;
    material.color.set(value || '#ffffff');
    if (
      renderer.outputEncoding === THREE.sRGBEncoding
      && typeof material.color.convertSRGBToLinear === 'function'
    ) {
      material.color.convertSRGBToLinear();
    }
  }
  /* Keep the authored PBR materials intact. We only repair missing normals,
   * retain double-sided GLB presentation and opt meshes into the soft studio
   * shadow pass. White remains physically white rather than being recolored. */
  function prepareStudioMeshes(object) {
    object?.traverse((mesh) => {
      if (!mesh?.isMesh) return;
      if (mesh.geometry && !mesh.geometry.getAttribute?.('normal')) {
        mesh.geometry.computeVertexNormals?.();
      }
      mesh.castShadow = true;
      mesh.receiveShadow = false;
    });
  }
  function applyGrip() {
    const grip = item(GRIPS, state.grip);
    (paddleParts.gripMats || []).forEach((material) => {
      const texture = new THREE.CanvasTexture(gripWrap(grip.base));
      texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(6, 6);
      texture.flipY = false;
      texture.encoding = THREE.sRGBEncoding;
      if (material.map) material.map.dispose();
      material.map = texture;
      setMaterialColorFromSrgb(material, '#ffffff');
      material.needsUpdate = true;
    });
    requestRender();
  }
  /*
   * Build a geometry-derived edge coordinate instead of trusting the UVs in
   * each supplied GLB. The five paddle shapes do not share one UV origin or
   * one UV range, so texture.repeat cannot reliably mean "half the edge".
   *
   * Physical definition used here:
   *   left half  : throat 0 -> crown 100
   *   right half : crown 100 -> throat 200 (mirrored)
   *
   * Both sides therefore receive the same normalized 0..1 half-edge
   * coordinate. The gradient canvas supplies 0/25/50/75/100 and the second
   * physical half mirrors it automatically. Arc-length normalization keeps
   * the color bands visually even on every paddle outline.
   */
  function ensureHalfEdgeUv(mesh) {
    if (!mesh?.isMesh || !mesh.geometry?.getAttribute('position')) return false;
    mesh.geometry = mesh.geometry.clone();
    const geometry = mesh.geometry;
    const position = geometry.getAttribute('position');
    if (!position || position.count < 3) return false;

    const points = new Array(position.count);
    const componentAt = (index, axis) => axis === 0 ? position.getX(index) : axis === 1 ? position.getY(index) : position.getZ(index);
    const spans = [0, 1, 2].map((axis) => {
      let min = Infinity;
      let max = -Infinity;
      for (let index = 0; index < position.count; index += 1) {
        const value = componentAt(index, axis);
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
      return { axis, min, max, span: Math.max(.000001, max - min) };
    });
    const faceAxes = spans.slice().sort((a, b) => b.span - a.span).slice(0, 2);
    const verticalAxis = faceAxes[0].axis;
    const horizontalAxis = faceAxes[1].axis;
    const centerH = (spans[horizontalAxis].min + spans[horizontalAxis].max) * .5;
    const centerV = (spans[verticalAxis].min + spans[verticalAxis].max) * .5;

    for (let index = 0; index < position.count; index += 1) {
      const h = componentAt(index, horizontalAxis);
      const v = componentAt(index, verticalAxis);
      const dh = h - centerH;
      const dv = v - centerV;
      points[index] = {
        h: Math.abs(dh),
        v,
        angle: Math.abs(Math.atan2(dh, -dv)),
        radius2: dh * dh + dv * dv
      };
    }

    const BIN_COUNT = 720;
    const bins = new Array(BIN_COUNT);
    points.forEach((point) => {
      const binIndex = Math.min(BIN_COUNT - 1, Math.floor((point.angle / Math.PI) * BIN_COUNT));
      if (!bins[binIndex] || point.radius2 > bins[binIndex].radius2) bins[binIndex] = point;
    });
    /* Fold left and right onto one canonical half-outline before measuring
     * distance. This guarantees that matching physical positions on the two
     * sides receive identical colors, even if a GLB contains a slightly
     * asymmetric vertex layout or assigns the crown seam to only one side. */
    const halfCurve = bins.filter(Boolean).sort((a, b) => a.angle - b.angle);
    if (halfCurve.length < 2) return false;
    let distance = 0;
    halfCurve[0].distance = 0;
    for (let index = 1; index < halfCurve.length; index += 1) {
      distance += Math.hypot(halfCurve[index].h - halfCurve[index - 1].h, halfCurve[index].v - halfCurve[index - 1].v);
      halfCurve[index].distance = distance;
    }
    const total = Math.max(distance, .000001);
    halfCurve.forEach((point) => { point.progress = point.distance / total; });

    function progressAt(curve, angle) {
      if (angle <= curve[0].angle) return 0;
      const last = curve[curve.length - 1];
      if (angle >= last.angle) return 1;
      let low = 0;
      let high = curve.length - 1;
      while (high - low > 1) {
        const middle = (low + high) >> 1;
        if (curve[middle].angle <= angle) low = middle;
        else high = middle;
      }
      const start = curve[low];
      const end = curve[high];
      const ratio = (angle - start.angle) / Math.max(.000001, end.angle - start.angle);
      return start.progress + (end.progress - start.progress) * ratio;
    }

    const uv = new Float32Array(position.count * 2);
    points.forEach((point, index) => {
      const halfProgress = progressAt(halfCurve, point.angle);
      /* Store the same perimeter coordinate on both channels. This preserves
       * the existing U/V theme setting without making the result model-UV
       * dependent. The coordinate is generated only for the edge mesh. */
      uv[index * 2] = halfProgress;
      uv[index * 2 + 1] = halfProgress;
    });
    geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    geometry.attributes.uv.needsUpdate = true;
    return true;
  }
  function edgeGradientCanvas(edge) {
    const alongU = edge.gradientAxis !== 'v';
    const canvas = document.createElement('canvas');
    canvas.width = alongU ? 1024 : 16;
    canvas.height = alongU ? 16 : 1024;
    const context = canvas.getContext('2d');
    const gradient = context.createLinearGradient(0, 0, alongU ? canvas.width : 0, alongU ? 0 : canvas.height);
    gradient.addColorStop(0, edge.stop0);
    gradient.addColorStop(0.25, edge.stop25);
    gradient.addColorStop(0.5, edge.stop50);
    gradient.addColorStop(0.75, edge.stop25);
    gradient.addColorStop(1, edge.stop0);
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);
    return canvas;
  }
  function applyEdge() {
    if (!paddleParts.edgeMat) return;
    const edge = item(EDGES, state.edge);
    if (paddleParts.edgeMat.map) {
      paddleParts.edgeMat.map.dispose();
      paddleParts.edgeMat.map = null;
    }
    if (edge.mode === 'mirrored_gradient') {
      const texture = new THREE.CanvasTexture(edgeGradientCanvas(edge));
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.repeat.set(1, 1);
      texture.flipY = false;
      texture.generateMipmaps = false;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.encoding = THREE.sRGBEncoding;
      paddleParts.edgeMat.map = texture;
      setMaterialColorFromSrgb(paddleParts.edgeMat, '#ffffff');
    } else {
      setMaterialColorFromSrgb(paddleParts.edgeMat, edge.hex);
    }
    paddleParts.edgeMat.needsUpdate = true;
    requestRender();
  }
  function applyFerrule() {
    if (paddleParts.ferruleMat) {
      setMaterialColorFromSrgb(paddleParts.ferruleMat, item(FERRULES, state.ferrule).hex);
      paddleParts.ferruleMat.needsUpdate = true;
    }
    requestRender();
  }
  function firstMeshMaterial(object) {
    let resolved = null;
    object?.traverse((mesh) => {
      if (resolved || !mesh?.isMesh) return;
      resolved = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    });
    return resolved;
  }
  function pbrNumber(material, key, fallback) {
    const value = Number(material?.[key]);
    return Number.isFinite(value) ? value : fallback;
  }
  function prepareModel(model, placeholder, requestedUrl = '') {
    disposeCurrentModel();
    prepareStudioMeshes(model);
    const replacedSourceMaterials = new Set();
    const replacedSourceGeometries = new Set();
    shapeTransform = fittedTransform(model, 11.5);
    applyTransform(model, shapeTransform);
    const faceNodes = resolveFaceNodes(model);
    buildPrintableMasks(model, faceNodes);
    ['front', 'back'].forEach((which) => {
      const node = faceNodes[which];
      if (!node) return;
      const faceBounds = new THREE.Box3().setFromObject(node);
      const faceSize = faceBounds.getSize(new THREE.Vector3());
      const physicalAspect = faceSize.x / Math.max(faceSize.y, .000001);
      faceAspectCorrection[which] = Math.max(.5, Math.min(2, (FACE_H / FACE_W) * physicalAspect));
      node.traverse((mesh) => {
        if (!mesh.isMesh) return;
        const sourceMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        sourceMaterials.filter(Boolean).forEach((material) => replacedSourceMaterials.add(material));
        const sourceMaterial = sourceMaterials[0];
        const sourceGeometry = mesh.geometry;
        ensurePlanarUv(mesh, which === 'back');
        if (sourceGeometry && mesh.geometry !== sourceGeometry) replacedSourceGeometries.add(sourceGeometry);
        const texture = new THREE.CanvasTexture(faceCanvas[which]);
        /*
         * This GLB stores the top of the paddle at V=0. Flipping the canvas
         * during the WebGL upload keeps its top row at the top of the paddle.
         */
        texture.flipY = true;
        texture.encoding = THREE.sRGBEncoding;
        texture.generateMipmaps = false;
        texture.minFilter = THREE.LinearFilter;
        texture.anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 4);
        faceTextures[which].push(texture);
        if (!normTexture) {
          normTexture = new THREE.CanvasTexture(normCanvas);
          normTexture.flipY = true;
          normTexture.generateMipmaps = false;
          normTexture.minFilter = THREE.LinearFilter;
          roughTexture = new THREE.CanvasTexture(roughCanvas);
          roughTexture.flipY = true;
          roughTexture.generateMipmaps = false;
          roughTexture.minFilter = THREE.LinearFilter;
        }
        mesh.material = new THREE.MeshStandardMaterial({
          name: sourceMaterial?.name || `${which}-custom-surface`,
          map: texture,
          normalMap: normTexture,
          roughnessMap: roughTexture,
          roughness: pbrNumber(sourceMaterial, 'roughness', 1),
          metalness: pbrNumber(sourceMaterial, 'metalness', 0.04),
          normalScale: sourceMaterial?.normalScale?.clone?.() || new THREE.Vector2(0.75, 0.75),
          envMapIntensity: pbrNumber(sourceMaterial, 'envMapIntensity', 0.9),
          side: THREE.DoubleSide
        });
        faceMaterials[which].push(mesh.material);
      });
    });

    const edge = findModelPart(model, 'edge');
    const edgeSourceMaterial = firstMeshMaterial(edge);
    paddleParts.edgeMat = new THREE.MeshPhysicalMaterial({
      name: edgeSourceMaterial?.name || 'custom-edge',
      color: '#ffffff',
      roughness: pbrNumber(edgeSourceMaterial, 'roughness', 0.62),
      metalness: pbrNumber(edgeSourceMaterial, 'metalness', 0),
      envMapIntensity: Math.max(1, pbrNumber(edgeSourceMaterial, 'envMapIntensity', 1)),
      clearcoat: 0.2,
      clearcoatRoughness: 0.4,
      side: edgeSourceMaterial?.side ?? THREE.DoubleSide
    });
    edge?.traverse((mesh) => {
      if (!mesh.isMesh) return;
      const sourceMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      sourceMaterials.filter(Boolean).forEach((material) => replacedSourceMaterials.add(material));
      const sourceGeometry = mesh.geometry;
      ensureHalfEdgeUv(mesh);
      if (sourceGeometry && mesh.geometry !== sourceGeometry) replacedSourceGeometries.add(sourceGeometry);
      mesh.material = paddleParts.edgeMat;
    });

    const handle = findModelPart(model, 'handle');
    paddleParts.gripMats = [];
    handle?.traverse((mesh) => {
      if (!mesh.isMesh) return;
      const sourceMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      sourceMaterials.filter(Boolean).forEach((entry) => replacedSourceMaterials.add(entry));
      const sourceMaterial = sourceMaterials[0];
      const material = new THREE.MeshStandardMaterial({
        name: sourceMaterial?.name || 'custom-grip',
        roughness: pbrNumber(sourceMaterial, 'roughness', 0.95),
        metalness: pbrNumber(sourceMaterial, 'metalness', 0),
        envMapIntensity: pbrNumber(sourceMaterial, 'envMapIntensity', 0.4),
        side: sourceMaterial?.side ?? THREE.FrontSide
      });
      paddleParts.gripMats.push(material);
      mesh.material = material;
    });

    const ferrule = model.getObjectByName(CFG.ferruleNodeName || 'joysent001') || findModelPart(model, 'ferrule');
    const ferruleSourceMaterial = firstMeshMaterial(ferrule);
    paddleParts.ferruleMat = new THREE.MeshPhysicalMaterial({
      name: ferruleSourceMaterial?.name || 'custom-ferrule',
      color: '#ffffff',
      roughness: pbrNumber(ferruleSourceMaterial, 'roughness', 0.55),
      metalness: pbrNumber(ferruleSourceMaterial, 'metalness', 0.02),
      envMapIntensity: Math.max(0.9, pbrNumber(ferruleSourceMaterial, 'envMapIntensity', 0.9)),
      clearcoat: 0.16,
      clearcoatRoughness: 0.44,
      side: ferruleSourceMaterial?.side ?? THREE.DoubleSide
    });
    ferrule?.traverse((mesh) => {
      if (!mesh.isMesh) return;
      const sourceMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      sourceMaterials.filter(Boolean).forEach((material) => replacedSourceMaterials.add(material));
      mesh.material = paddleParts.ferruleMat;
    });

    /* Materials and source geometries replaced for customization are no longer
       reachable through the model tree. Dispose only those that are not still
       shared by another live mesh in the same GLB. */
    const liveMaterials = new Set();
    const liveGeometries = new Set();
    const liveTextures = new Set();
    model.traverse((mesh) => {
      if (!mesh?.isMesh) return;
      if (mesh.geometry) liveGeometries.add(mesh.geometry);
      (Array.isArray(mesh.material) ? mesh.material : [mesh.material]).filter(Boolean).forEach((material) => {
        liveMaterials.add(material);
        Object.keys(material).forEach((key) => {
          if (material[key]?.isTexture) liveTextures.add(material[key]);
        });
      });
    });
    const disposedOrphanTextures = new Set();
    replacedSourceMaterials.forEach((material) => {
      if (!liveMaterials.has(material)) disposeMaterial(material, disposedOrphanTextures, liveTextures);
    });
    replacedSourceGeometries.forEach((geometry) => {
      if (!liveGeometries.has(geometry)) geometry.dispose?.();
    });

    currentModel = model;
    activeModelUrl = requestedUrl || (placeholder ? '__embedded_placeholder__' : currentShape()?.modelUrl || '');
    shapeLoadingUrl = '';
    model.visible = false;
    root.add(model);
    applyEdge();
    applyGrip();
    applyFerrule();
    composeAuxMaps();
    composeFace('front');
    composeFace('back');
    hideModelLoading('shape');
    setModelBadge(currentShape(), placeholder);
    refreshResult();
    syncModelPresentation(true);
    cameraFramingIsAutomatic = true;
    scheduleCameraFit();
  }
  function prepareCoreModel(model, requestedUrl) {
    if (!isCoreStepActive()) {
      disposeObject(model);
      return;
    }
    disposeCoreModel();
    prepareStudioMeshes(model);
    activeCoreModelUrl = requestedUrl;
    coreLoadingUrl = '';
    coreTransform = fittedTransform(model, 10.25);
    applyTransform(model, coreTransform);
    currentCoreModel = model;
    model.visible = false;
    root.add(model);
    appendCoreModelNotice('Internal structure model loaded. Rotate the preview to inspect it.');
    syncModelPresentation(true);
    cameraFramingIsAutomatic = true;
    scheduleCameraFit();
  }
  function loadSelectedCoreModel(force = false) {
    if (!isCoreStepActive()) {
      restorePaddlePreview();
      return;
    }
    const core = currentCore();
    const requestedUrl = core?.modelUrl || '';
    if (!requestedUrl) {
      cancelCorePreviewLoad(true);
      if (isCoreStepActive()) {
        appendCoreModelNotice('No core GLB is connected. Add a Shopify Files CDN URL in this Paddle core block.');
        setCoreModelBadge(core, 'CORE MODEL NOT CONNECTED');
        syncModelPresentation(true);
        if (!currentModel) {
          if (shapeLoadingUrl) {
            const shape = currentShape();
            showModelLoading(shape?.modelUrl ? `LOADING ${shape.code} MODEL` : 'LOADING PLACEHOLDER MODEL', 'shape');
          } else {
            loadSelectedShapeModel();
          }
        }
      }
      return;
    }
    if (!force && currentCoreModel && activeCoreModelUrl === requestedUrl) {
      syncModelPresentation(true);
      return;
    }
    if (!force && coreLoadingUrl === requestedUrl) return;
    if (coreAbortController) coreAbortController.abort();
    coreAbortController = new AbortController();
    const requestSignal = coreAbortController.signal;
    const requestId = ++coreLoadSerial;
    coreLoadingUrl = requestedUrl;
    if (currentCoreModel && activeCoreModelUrl !== requestedUrl) {
      transitionModel(currentCoreModel, false, coreTransform, 220);
    }
    if (isCoreStepActive()) {
      showModelLoading(`LOADING ${core.code || 'CORE'} INTERNAL MODEL`, 'core');
      setCoreModelBadge(core, 'LOADING INTERNAL CORE');
    }
    loadGlbScene(requestedUrl, requestSignal).then((model) => {
      if (requestId !== coreLoadSerial || !isCoreStepActive() || currentCore()?.modelUrl !== requestedUrl) {
        disposeObject(model);
        return;
      }
      coreAbortController = null;
      prepareCoreModel(model, requestedUrl);
    }).catch((error) => {
      if (error?.name === 'AbortError') return;
      if (requestId !== coreLoadSerial || !isCoreStepActive() || currentCore()?.modelUrl !== requestedUrl) return;
      console.error('Core model failed to load.', error);
      coreAbortController = null;
      disposeCoreModel();
      coreLoadingUrl = '';
      appendCoreModelNotice(error.message || 'The configured core GLB could not load.');
      if (isCoreStepActive()) {
        hideModelLoading('core');
        setCoreModelBadge(core, 'CORE MODEL UNAVAILABLE');
        syncModelPresentation(true);
        if (!currentModel) {
          if (shapeLoadingUrl) {
            const shape = currentShape();
            showModelLoading(shape?.modelUrl ? `LOADING ${shape.code} MODEL` : 'LOADING PLACEHOLDER MODEL', 'shape');
          } else {
            loadSelectedShapeModel();
          }
        }
      }
    });
  }
  function loadSelectedShapeModel(force = false) {
    const shape = currentShape();
    const requestedUrl = shape.modelUrl || '__embedded_placeholder__';
    setModelBadge(shape, !shape.modelUrl);
    if (!force && currentModel && activeModelUrl === requestedUrl) {
      syncModelPresentation(true);
      return;
    }
    if (!force && shapeLoadingUrl === requestedUrl) return;
    const requestId = ++shapeLoadSerial;
    shapeLoadingUrl = requestedUrl;
    showModelLoading(shape.modelUrl ? `LOADING ${shape.code} MODEL` : 'LOADING PLACEHOLDER MODEL');
    if (shape.modelUrl) {
      loadGlbScene(requestedUrl).then((model) => {
        if (requestId !== shapeLoadSerial || currentShape()?.modelUrl !== requestedUrl) {
          disposeObject(model);
          return;
        }
        prepareModel(model, false, requestedUrl);
      }).catch((error) => {
        if (requestId !== shapeLoadSerial || currentShape()?.modelUrl !== requestedUrl) return;
        console.error('Shape model failed; using supplied placeholder.', error);
        shapeLoadingUrl = '';
        loadEmbeddedModel(true, requestId);
        const note = $('#shapeNote');
        if (note) {
          note.querySelector('[data-shape-model-notice]')?.remove();
          const notice = document.createElement('span');
          notice.dataset.shapeModelNotice = '';
          notice.innerHTML = `<br><b>Model notice:</b> ${error.message || 'The configured GLB could not load.'} The supplied placeholder is shown.`;
          note.appendChild(notice);
        }
      });
    } else {
      loadEmbeddedModel(true, requestId);
    }
  }
  function loadEmbeddedModel(placeholder, requestId = shapeLoadSerial) {
    const encoded = (window.JoysentPaddleModelParts || []).join('');
    if (!encoded) {
      showModelError('Your paddle preview could not load. Select Retry 3D Preview to try again.');
      return;
    }
    gltfLoader.parse(
      b64ToBuffer(encoded),
      '',
      (gltf) => {
        if (requestId !== shapeLoadSerial) {
          disposeObject(gltf.scene);
          return;
        }
        prepareModel(gltf.scene, placeholder, '__embedded_placeholder__');
      },
      (error) => {
        if (requestId !== shapeLoadSerial) return;
        console.error(error);
        showModelError('Your paddle preview could not load. Select Retry 3D Preview to try again.');
      }
    );
  }
  modelRetry?.addEventListener('click', () => loadSelectedShapeModel(true), { signal: sceneLifecycle.signal });
  let expandedScrollY = 0;
  let expandedHtmlOverflow = '';
  let expandedBodyOverflow = '';
  let pipPosition = null;
  let pipPointer = null;
  let pipWasManuallyMoved = false;
  let pipDockFrame = 0;
  function mobilePipActive() {
    return window.innerWidth <= 820
      && section.classList.contains('mobile-preview-compact')
      && !section.classList.contains('preview-expanded');
  }

  /*
   * A store-wide mobile navigation drawer must always sit above page-level
   * customizer chrome. Themes use different drawer names/classes, so detect
   * the actual open, visible navigation state instead of relying on one theme
   * selector. CSS uses this state to suspend the floating preview and action
   * dock until the global drawer closes.
   */
  const GLOBAL_NAV_SELECTOR = [
    'menu-drawer',
    '#menu-drawer',
    '[id*="menu-drawer"]',
    '[class*="menu-drawer"]',
    '[class*="mobile-menu"]',
    '[class*="mobile_menu"]',
    '[class*="mobile-nav"]',
    '[class*="mobile_nav"]',
    '[class*="drawer-menu"]',
    '[class*="drawer__menu"]',
    '[data-drawer="menu"]',
    '[data-mobile-menu]',
    '.nt_fk_canvas',
    '#nt_menu_canvas',
    '.push-menu',
    '.mobile-menu-wrapper',
    '.header-drawer'
  ].join(',');
  const OPEN_STATE_PATTERN = /(^|[\s_-])(open|opened|active|visible|show|shown)([\s_-]|$)/i;
  let globalNavSyncFrame = 0;

  function elementIsVisible(element) {
    if (!element || section.contains(element)) return false;
    const style = getComputedStyle(element);
    if (
      style.display === 'none'
      || style.visibility === 'hidden'
      || Number(style.opacity) <= 0.02
      || style.pointerEvents === 'none'
    ) return false;
    const rect = element.getBoundingClientRect();
    return rect.width >= 140
      && rect.height >= Math.min(140, window.innerHeight * 0.22)
      && rect.right > 0
      && rect.left < window.innerWidth
      && rect.bottom > 0
      && rect.top < window.innerHeight;
  }

  function elementLooksOpen(element) {
    if (!elementIsVisible(element)) return false;
    const className = typeof element.className === 'string' ? element.className : '';
    const stateText = `${element.id || ''} ${className}`;
    const explicitlyOpen = element.hasAttribute('open')
      || element.getAttribute('aria-hidden') === 'false'
      || element.getAttribute('aria-expanded') === 'true'
      || OPEN_STATE_PATTERN.test(stateText);
    if (explicitlyOpen) return true;

    const style = getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    const viewportDrawer = (style.position === 'fixed' || style.position === 'absolute')
      && rect.width >= window.innerWidth * 0.55
      && rect.height >= window.innerHeight * 0.35;
    return viewportDrawer;
  }

  function globalNavigationIsOpen() {
    if (window.innerWidth > 820) return false;

    const rootState = `${document.documentElement.className || ''} ${document.body.className || ''}`;
    const rootSignalsOpenMenu = /(menu|drawer|nav)[\w-]*(?:open|active|visible)|(?:open|active|visible)[\w-]*(menu|drawer|nav)/i.test(rootState);
    if (rootSignalsOpenMenu) return true;

    const expandedControl = [...document.querySelectorAll('[aria-expanded="true"]')].find((control) => {
      if (section.contains(control)) return false;
      const context = control.closest('header, nav, menu-drawer, [class*="header"], [class*="menu"], [class*="drawer"]');
      const identity = `${control.id || ''} ${control.className || ''} ${control.getAttribute('aria-controls') || ''}`;
      return Boolean(context) && /(menu|drawer|navigation|nav)/i.test(identity);
    });
    if (expandedControl) return true;

    return [...document.querySelectorAll(GLOBAL_NAV_SELECTOR)].some(elementLooksOpen);
  }

  function syncGlobalNavigationLayer() {
    globalNavSyncFrame = 0;
    const isOpen = globalNavigationIsOpen();
    document.body.classList.toggle('jc-global-navigation-open', isOpen);
    section.classList.toggle('global-navigation-open', isOpen);
    if (isOpen) clearPipInlinePosition();
    else if (mobilePipActive()) requestAnimationFrame(() => applyPipPosition());
  }

  function scheduleGlobalNavigationLayerSync() {
    if (globalNavSyncFrame) return;
    globalNavSyncFrame = requestAnimationFrame(syncGlobalNavigationLayer);
  }

  const globalNavigationObserver = new MutationObserver((mutations) => {
    const hasExternalMutation = mutations.some((mutation) => !section.contains(mutation.target));
    if (hasExternalMutation) scheduleGlobalNavigationLayerSync();
  });
  globalNavigationObserver.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'open', 'aria-hidden', 'aria-expanded']
  });
  document.addEventListener('click', scheduleGlobalNavigationLayerSync, { capture: true, signal: sceneLifecycle.signal });
  document.addEventListener('pointerup', scheduleGlobalNavigationLayerSync, { capture: true, signal: sceneLifecycle.signal });
  window.addEventListener('resize', scheduleGlobalNavigationLayerSync, { passive: true, signal: sceneLifecycle.signal });
  scheduleGlobalNavigationLayerSync();
  function clearPipInlinePosition() {
    stage.style.removeProperty('left');
    stage.style.removeProperty('top');
    stage.style.removeProperty('right');
  }
  function visibleMobileHeaderBottom() {
    const selectors = [
      'body > header',
      '#shopify-section-header',
      '.shopify-section-header',
      'sticky-header',
      '.shopify-section-group-header-group',
      '[id^="shopify-section-sections--"][id$="__header"]'
    ];
    const candidates = [...new Set(selectors.flatMap((selector) => [...document.querySelectorAll(selector)]))];
    let bottom = 0;
    const hasVisibleHeaderContent = (element, headerRect) => {
      const controls = element.querySelectorAll(
        'a, button, img, svg, input, [role="button"], [role="navigation"]'
      );
      return [...controls].some((control) => {
        const controlStyle = getComputedStyle(control);
        if (
          controlStyle.display === 'none'
          || controlStyle.visibility === 'hidden'
          || Number(controlStyle.opacity) <= 0.05
        ) return false;
        const controlRect = control.getBoundingClientRect();
        return controlRect.width >= 8
          && controlRect.height >= 8
          && controlRect.bottom > 0
          && controlRect.top < headerRect.bottom;
      });
    };
    candidates.forEach((element) => {
      if (!element || section.contains(element)) return;
      const style = getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return;
      const rect = element.getBoundingClientRect();
      const anchoredToViewport = style.position === 'fixed' || style.position === 'sticky';
      const overlapsViewportTop = rect.top <= 2 && rect.bottom > 2;
      const plausibleHeader = rect.width >= window.innerWidth * 0.5
        && rect.height >= 20
        && rect.height <= Math.min(120, window.innerHeight * 0.2);
      const hasContent = hasVisibleHeaderContent(element, rect);
      if (anchoredToViewport && overlapsViewportTop && plausibleHeader && hasContent) {
        bottom = Math.max(bottom, rect.bottom);
      }
    });
    return Math.max(0, Math.round(bottom));
  }
  function currentMobileDockTop() {
    const visualTop = Math.max(0, Math.round(window.visualViewport?.offsetTop || 0));
    return Math.max(visualTop, visibleMobileHeaderBottom());
  }
  function publishMobileDockTop() {
    if (window.innerWidth > 820) {
      section.style.removeProperty('--jc-live-mobile-top');
      return 0;
    }
    const dockTop = currentMobileDockTop();
    /*
     * The regular mobile preview is sticky before it becomes the compact
     * floating preview. Both states must use the same live viewport/header
     * offset; otherwise the configured legacy sticky offset leaves page copy
     * visible above the preview.
     */
    section.style.setProperty('--jc-live-mobile-top', `${dockTop}px`);
    return dockTop;
  }
  function applyPipPosition(position = pipPosition) {
    if (!mobilePipActive()) return;
    const rect = stage.getBoundingClientRect();
    const viewportWidth = Math.max(1, Math.round(window.visualViewport?.width || window.innerWidth));
    const viewportLeft = Math.max(0, Math.round(window.visualViewport?.offsetLeft || 0));
    const sideMargin = rect.width >= viewportWidth - 2 ? 0 : 10;
    /*
     * The compact preview is a viewport dock, not a draggable document card.
     * Its vertical coordinate must always follow the currently visible top edge
     * (or the bottom of a real fixed/sticky store header). Keeping a previously
     * dragged Y value is what left page copy visible above the preview on mobile.
     */
    const dockTop = publishMobileDockTop();
    const minX = viewportLeft + sideMargin;
    const maxX = Math.max(minX, viewportLeft + viewportWidth - rect.width - sideMargin);
    const fallback = { x: maxX, y: dockTop };
    const next = pipWasManuallyMoved && position ? position : fallback;
    pipPosition = {
      x: Math.max(minX, Math.min(maxX, Number.isFinite(Number(next.x)) ? Number(next.x) : maxX)),
      y: dockTop
    };
    stage.style.setProperty('left', `${pipPosition.x}px`, 'important');
    stage.style.setProperty('top', `${pipPosition.y}px`, 'important');
    stage.style.setProperty('right', 'auto', 'important');
  }
  function schedulePipDockSync() {
    if (pipDockFrame) return;
    pipDockFrame = requestAnimationFrame(() => {
      pipDockFrame = 0;
      publishMobileDockTop();
      if (mobilePipActive()) applyPipPosition();
    });
  }
  function syncPipMode(compact) {
    const shouldCompact = compact && window.innerWidth <= 820;
    const modeChanged = section.classList.contains('mobile-preview-compact') !== shouldCompact;
    section.classList.toggle('mobile-preview-compact', shouldCompact);
    if (modeChanged) cameraFramingIsAutomatic = true;
    requestAnimationFrame(() => {
      if (mobilePipActive()) applyPipPosition();
      else clearPipInlinePosition();
      resize();
    });
  }
  function openExpandedPreview() {
    expandedScrollY = window.scrollY;
    expandedHtmlOverflow = document.documentElement.style.overflow;
    expandedBodyOverflow = document.body.style.overflow;
    clearPipInlinePosition();
    section.classList.add('preview-expanded');
    cameraFramingIsAutomatic = true;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    previewClose.hidden = false;
    resize();
    previewClose.focus();
  }
  function closeExpandedPreview() {
    section.classList.remove('preview-expanded');
    document.documentElement.style.overflow = expandedHtmlOverflow;
    document.body.style.overflow = expandedBodyOverflow;
    previewClose.hidden = true;
    cameraFramingIsAutomatic = true;
    resize();
    window.scrollTo(0, expandedScrollY);
    requestAnimationFrame(() => applyPipPosition());
    previewExpand.focus();
  }
  previewExpand?.addEventListener('click', openExpandedPreview, { signal: sceneLifecycle.signal });
  previewClose?.addEventListener('click', closeExpandedPreview, { signal: sceneLifecycle.signal });
  previewPipDrag?.addEventListener('pointerdown', (event) => {
    if (!mobilePipActive()) return;
    pipWasManuallyMoved = true;
    const rect = stage.getBoundingClientRect();
    pipPointer = {
      id: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top
    };
    previewPipDrag.setPointerCapture?.(event.pointerId);
    previewPipDrag.classList.add('is-dragging');
    event.preventDefault();
  }, { signal: sceneLifecycle.signal });
  previewPipDrag?.addEventListener('pointermove', (event) => {
    if (!pipPointer || event.pointerId !== pipPointer.id || !mobilePipActive()) return;
    applyPipPosition({
      x: event.clientX - pipPointer.offsetX,
      y: visibleMobileHeaderBottom()
    });
    event.preventDefault();
  }, { signal: sceneLifecycle.signal });
  const finishPipDrag = (event) => {
    if (!pipPointer || event.pointerId !== pipPointer.id) return;
    if (previewPipDrag?.hasPointerCapture?.(event.pointerId)) previewPipDrag.releasePointerCapture(event.pointerId);
    previewPipDrag?.classList.remove('is-dragging');
    pipPointer = null;
  };
  previewPipDrag?.addEventListener('pointerup', finishPipDrag, { signal: sceneLifecycle.signal });
  previewPipDrag?.addEventListener('pointercancel', finishPipDrag, { signal: sceneLifecycle.signal });
  window.addEventListener('resize', () => requestAnimationFrame(() => {
    publishMobileDockTop();
    if (mobilePipActive()) applyPipPosition();
    else clearPipInlinePosition();
  }), { passive: true, signal: sceneLifecycle.signal });
  window.addEventListener('scroll', schedulePipDockSync, { passive: true, signal: sceneLifecycle.signal });
  window.visualViewport?.addEventListener('resize', schedulePipDockSync, { passive: true, signal: sceneLifecycle.signal });
  window.visualViewport?.addEventListener('scroll', schedulePipDockSync, { passive: true, signal: sceneLifecycle.signal });
  publishMobileDockTop();

  let targetRotationY = 0;
  let targetRotationX = 0;
  let autoSpin = false;
  let dragging = false;
  let pointerX = 0;
  let pointerY = 0;
  let pointerTime = 0;
  let inertiaVelocityY = 0;
  let inertiaVelocityX = 0;
  let inertiaActive = false;
  const canvas = renderer.domElement;
  let activeRotatePointerId = null;
  const activeTouchPointers = new Map();
  let pinchGesture = null;
  let touchGesture = null;
  canvas.style.touchAction = 'pan-y';
  function getTouchDistance() {
    const touches = Array.from(activeTouchPointers.values());
    if (touches.length < 2) return 0;
    return Math.hypot(touches[1].x - touches[0].x, touches[1].y - touches[0].y);
  }
  function pointerDown(x, y, time = performance.now()) {
    dragging = true;
    autoSpin = false;
    inertiaActive = false;
    inertiaVelocityY = 0;
    inertiaVelocityX = 0;
    setViewButton(null);
    pointerX = x;
    pointerY = y;
    pointerTime = time;
    setRenderQuality('interaction');
  }
  function pointerMove(x, y, time = performance.now()) {
    if (!dragging) return;
    const deltaX = x - pointerX;
    const deltaY = y - pointerY;
    const elapsed = Math.max(0.008, Math.min(0.05, (time - pointerTime) / 1000 || 0.016));
    const rotationDeltaY = deltaX * 0.008;
    const unclampedX = root.rotation.x + deltaY * 0.005;
    const nextRotationX = Math.max(-1.1, Math.min(1.1, unclampedX));
    const rotationDeltaX = nextRotationX - root.rotation.x;

    /* Dragging is direct: the model is moved before the next display frame
       instead of easing toward a delayed target. Velocity is retained only
       for a very short, low-energy release glide. */
    root.rotation.y += rotationDeltaY;
    root.rotation.x = nextRotationX;
    targetRotationY = root.rotation.y;
    targetRotationX = root.rotation.x;
    inertiaVelocityY = inertiaVelocityY * 0.55 + (rotationDeltaY / elapsed) * 0.45;
    inertiaVelocityX = inertiaVelocityX * 0.55 + (rotationDeltaX / elapsed) * 0.45;
    pointerX = x;
    pointerY = y;
    pointerTime = time;
    requestRender();
  }
  function finishRotationInteraction() {
    dragging = false;
    const speed = Math.hypot(inertiaVelocityY, inertiaVelocityX);
    inertiaActive = !reduceModelMotion && speed > 0.08;
    if (inertiaActive) {
      const maximumSpeed = 1.5;
      const scale = Math.min(1, maximumSpeed / speed);
      inertiaVelocityY *= scale;
      inertiaVelocityX *= scale;
    } else {
      inertiaVelocityY = 0;
      inertiaVelocityX = 0;
    }
    setRenderQuality('still', 120);
    requestRender();
  }
  canvas.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.pointerType === 'touch') {
      activeTouchPointers.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
        startX: event.clientX,
        startY: event.clientY
      });
      if (activeTouchPointers.size >= 2) {
        dragging = false;
        activeRotatePointerId = null;
        touchGesture = null;
        activeTouchPointers.forEach((_, pointerId) => canvas.setPointerCapture?.(pointerId));
        pinchGesture = {
          distance: Math.max(1, getTouchDistance()),
          cameraZ: camera.position.z
        };
        setRenderQuality('interaction');
        event.preventDefault();
        return;
      }
      activeRotatePointerId = event.pointerId;
      touchGesture = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY
      };
      pointerX = event.clientX;
      pointerY = event.clientY;
      pointerTime = event.timeStamp || performance.now();
      return;
    }
    activeRotatePointerId = event.pointerId;
    pointerDown(event.clientX, event.clientY, event.timeStamp || performance.now());
    canvas.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }, { passive: false, signal: sceneLifecycle.signal });
  canvas.addEventListener('pointermove', (event) => {
    if (event.pointerType === 'touch' && activeTouchPointers.has(event.pointerId)) {
      const previousTouch = activeTouchPointers.get(event.pointerId);
      activeTouchPointers.set(event.pointerId, {
        ...previousTouch,
        x: event.clientX,
        y: event.clientY
      });
      if (activeTouchPointers.size >= 2 && pinchGesture) {
        const distance = Math.max(1, getTouchDistance());
        cameraFramingIsAutomatic = false;
        camera.position.z = Math.max(10, Math.min(34, pinchGesture.cameraZ * pinchGesture.distance / distance));
        requestRender();
        event.preventDefault();
        return;
      }
      if (event.pointerId !== activeRotatePointerId) return;
      if (!dragging && touchGesture?.id === event.pointerId) {
        const horizontal = event.clientX - touchGesture.startX;
        const vertical = event.clientY - touchGesture.startY;
        if (Math.hypot(horizontal, vertical) < 7) return;
        /* A primarily vertical one-finger gesture remains normal page scroll.
           Horizontal intent claims the pointer and starts model rotation. */
        if (Math.abs(vertical) > Math.abs(horizontal) * 1.1) return;
        pointerDown(touchGesture.startX, touchGesture.startY, event.timeStamp || performance.now());
        canvas.setPointerCapture?.(event.pointerId);
      }
    }
    if (event.pointerId !== activeRotatePointerId) return;
    const samples = event.getCoalescedEvents?.() || [event];
    samples.forEach((sample) => pointerMove(
      sample.clientX,
      sample.clientY,
      sample.timeStamp || event.timeStamp || performance.now()
    ));
    event.preventDefault();
  }, { passive: false, signal: sceneLifecycle.signal });
  const finishModelDrag = (event) => {
    if (canvas.hasPointerCapture?.(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    if (event.pointerType === 'touch') {
      activeTouchPointers.delete(event.pointerId);
      pinchGesture = null;
      if (activeTouchPointers.size === 1) {
        const [remainingId, remainingTouch] = activeTouchPointers.entries().next().value;
        activeRotatePointerId = remainingId;
        touchGesture = {
          id: remainingId,
          startX: remainingTouch.x,
          startY: remainingTouch.y
        };
        pointerX = remainingTouch.x;
        pointerY = remainingTouch.y;
        pointerTime = event.timeStamp || performance.now();
        dragging = false;
      } else {
        activeRotatePointerId = null;
        touchGesture = null;
        finishRotationInteraction();
      }
      return;
    }
    if (event.pointerId !== activeRotatePointerId) return;
    activeRotatePointerId = null;
    finishRotationInteraction();
  };
  canvas.addEventListener('pointerup', finishModelDrag, { signal: sceneLifecycle.signal });
  canvas.addEventListener('pointercancel', finishModelDrag, { signal: sceneLifecycle.signal });
  canvas.addEventListener('lostpointercapture', (event) => {
    activeTouchPointers.delete(event.pointerId);
    if (activeTouchPointers.size < 2) pinchGesture = null;
    if (event.pointerId === activeRotatePointerId) {
      activeRotatePointerId = null;
      touchGesture = null;
      finishRotationInteraction();
    }
  }, { signal: sceneLifecycle.signal });
  canvas.addEventListener('wheel', (event) => {
    event.preventDefault();
    cameraFramingIsAutomatic = false;
    setRenderQuality('interaction');
    camera.position.z = Math.max(10, Math.min(34, camera.position.z + event.deltaY * 0.015));
    setRenderQuality('still', 140);
    requestRender();
  }, { passive: false, signal: sceneLifecycle.signal });
  const VIEWS = { front: [0, 0], back: [Math.PI, 0], side: [Math.PI / 2, 0] };
  $$('.viewbtns button').forEach((button) => {
    button.addEventListener('click', () => {
      const view = button.dataset.view;
      if (view === 'spin') {
        autoSpin = true;
        inertiaActive = false;
        setRenderQuality('interaction');
        setViewButton(button);
        requestRender();
        return;
      }
      autoSpin = false;
      inertiaActive = false;
      setRenderQuality('still', 100);
      targetRotationY = VIEWS[view][0];
      targetRotationX = VIEWS[view][1];
      setViewButton(button);
      requestRender();
    }, { signal: sceneLifecycle.signal });
  });
  function setViewButton(selected) {
    $$('.viewbtns button').forEach((button) => button.classList.toggle('on', button === selected));
  }
  function resize() {
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    if (!width || !height || viewerDisposed) return;
    const nextRatio = targetPixelRatio(renderQuality);
    if (Math.abs(renderer.getPixelRatio() - nextRatio) > 0.01) renderer.setPixelRatio(nextRatio);
    renderer.setSize(width, height, false);
    camera.aspect = width / Math.max(height, 1);
    camera.updateProjectionMatrix();
    if (cameraFramingIsAutomatic) scheduleCameraFit();
    else requestRender();
  }
  let stageResizeObserver = null;
  if ('ResizeObserver' in window) {
    stageResizeObserver = new ResizeObserver(() => resize());
    stageResizeObserver.observe(stage);
  } else {
    window.addEventListener('resize', resize, { passive: true, signal: sceneLifecycle.signal });
  }
  let stageIntersectionObserver = null;
  if ('IntersectionObserver' in window) {
    stageIntersectionObserver = new IntersectionObserver((entries) => {
      sceneVisible = entries[0]?.isIntersecting !== false;
      if (sceneVisible) requestRender();
      else stopSceneAnimation();
    }, { rootMargin: '120px' });
    stageIntersectionObserver.observe(stage);
  }
  let lastSceneTime = 0;
  function startSceneAnimation() {
    if (sceneAnimationFrame || !sceneVisible || document.hidden || viewerDisposed) return;
    sceneAnimationFrame = requestAnimationFrame(animateScene);
  }
  function stopSceneAnimation() {
    if (sceneAnimationFrame) cancelAnimationFrame(sceneAnimationFrame);
    sceneAnimationFrame = 0;
    lastSceneTime = 0;
  }
  function animateScene(now) {
    sceneAnimationFrame = 0;
    if (!sceneVisible || document.hidden || viewerDisposed) return;
    const delta = lastSceneTime ? Math.min(0.05, Math.max(0.001, (now - lastSceneTime) / 1000)) : 1 / 60;
    lastSceneTime = now;
    const hadTransitions = modelTransitions.length > 0;
    const transitioning = updateModelTransitions(now);
    if (autoSpin) {
      root.rotation.y += 0.48 * delta;
      targetRotationY = root.rotation.y;
      targetRotationX = root.rotation.x;
    } else if (inertiaActive && !dragging) {
      root.rotation.y += inertiaVelocityY * delta;
      root.rotation.x = Math.max(-1.1, Math.min(1.1, root.rotation.x + inertiaVelocityX * delta));
      targetRotationY = root.rotation.y;
      targetRotationX = root.rotation.x;
      const decay = Math.exp(-11 * delta);
      inertiaVelocityY *= decay;
      inertiaVelocityX *= decay;
      if (Math.hypot(inertiaVelocityY, inertiaVelocityX) < 0.025) {
        inertiaActive = false;
        inertiaVelocityY = 0;
        inertiaVelocityX = 0;
      }
    } else if (!dragging) {
      const follow = 1 - Math.exp(-18 * delta);
      root.rotation.y += (targetRotationY - root.rotation.y) * follow;
      root.rotation.x += (targetRotationX - root.rotation.x) * follow;
    }
    const rotationMoving = !dragging && !autoSpin && !inertiaActive && (
      Math.abs(targetRotationY - root.rotation.y) > 0.0005 ||
      Math.abs(targetRotationX - root.rotation.x) > 0.0005
    );
    const moving = autoSpin || inertiaActive || dragging || hadTransitions || transitioning || rotationMoving;
    if (!moving && !needsRender) return;
    renderer.shadowMap.needsUpdate = true;
    renderer.render(scene, camera);
    needsRender = false;
    if (autoSpin || inertiaActive || dragging || transitioning || rotationMoving) startSceneAnimation();
  }
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopSceneAnimation();
    else requestRender();
  }, { signal: sceneLifecycle.signal });

  function disposeViewer() {
    if (viewerDisposed) return;
    viewerDisposed = true;
    sceneLifecycle.abort();
    window.clearTimeout(qualityRestoreTimer);
    if (cameraFitFrame) cancelAnimationFrame(cameraFitFrame);
    if (pipDockFrame) cancelAnimationFrame(pipDockFrame);
    if (globalNavSyncFrame) cancelAnimationFrame(globalNavSyncFrame);
    stopSceneAnimation();
    stageResizeObserver?.disconnect();
    stageIntersectionObserver?.disconnect();
    globalNavigationObserver.disconnect();
    shapeLoadSerial += 1;
    cancelCorePreviewLoad(true);
    disposeCurrentModel();
    glbBufferCache.clear();
    scene.remove(contactShadow);
    contactShadow.material?.dispose?.();
    contactShadowTexture.dispose?.();
    scene.remove(shadowCatcher);
    shadowCatcher.geometry?.dispose?.();
    shadowCatcherMaterial.dispose?.();
    scene.environment?.dispose?.();
    scene.environment = null;
    renderer.renderLists?.dispose?.();
    renderer.dispose();
    renderer.forceContextLoss?.();
    canvas.remove();
    document.body.classList.remove('jc-global-navigation-open');
    document.removeEventListener('shopify:section:unload', handleViewerSectionUnload);
  }
  function handleViewerSectionUnload(event) {
    if (event.target === section) disposeViewer();
  }
  document.addEventListener('shopify:section:unload', handleViewerSectionUnload);
  window.addEventListener('pagehide', disposeViewer, { once: true, signal: sceneLifecycle.signal });
  startSceneAnimation();

  /* ---------- Branch-aware option UI ---------- */
  const buildRow = $('#buildRow');
  const buildVal = $('#buildVal');
  const buildNote = $('#buildNote');
  const shapeRow = $('#shapeRow');
  const shapeVal = $('#shapeVal');
  const shapeNote = $('#shapeNote');
  const coreRow = $('#coreRow');
  const coreVal = $('#coreVal');
  const coreNote = $('#coreNote');
  const coreIntro = $('#coreIntro');
  const weaveRow = $('#weaveRow');
  const weaveVal = $('#weaveVal');
  const surfaceNote = $('#surfaceNote');
  const recommendationRow = $('#recommendationRow');
  const recommendationStatus = $('#recommendationStatus');
  const artworkPriceNote = $('#artworkPriceNote');

  function ensureBranchSelections() {
    if (!SHAPES.some((shape) => shape.id === state.shape && shape.build === state.build)) state.shape = firstFor(SHAPES, state.build)?.id;
    const shape = currentShape();
    const availableCores = coresForSelection(shape, state.build);
    const availableSurfaces = surfacesForSelection(shape, state.build);
    if (!availableCores.some((core) => core.id === state.core)) state.core = availableCores[0]?.id;
    if (!availableSurfaces.some((surface) => surface.id === state.surface)) state.surface = availableSurfaces[0]?.id;
    ensureColorSelections();
  }
  function updateBuildPriceLabels() {
    $$('[data-build-price]').forEach((label) => {
      const build = label.dataset.buildPrice;
      const variant = minimumVariant(build);
      label.innerHTML = variant
        ? `<s>${formatMoney(compareAtPriceForBuild(build))}</s><span>Starting at ${formatMoney(variant.price)}</span>`
        : '<span>Select a Shopify product</span>';
    });
    const heroVariant = minimumVariant(state.build);
    const heroComparePrice = $('#heroComparePrice');
    const heroStartingPrice = $('#heroStartingPrice');
    if (heroComparePrice) heroComparePrice.textContent = formatMoney(compareAtPriceForBuild(state.build));
    if (heroStartingPrice) heroStartingPrice.textContent = heroVariant ? formatMoney(heroVariant.price) : '—';
  }
  function updateBuildUI() {
    const build = BUILDS[state.build];
    $$('[data-build]').forEach((button) => button.classList.toggle('on', button.dataset.build === state.build));
    buildVal.textContent = build.label;
    buildNote.innerHTML = `<b>${build.label}</b> ${build.description}`;
    updateBuildPriceLabels();
    buildEdgeUI();
    buildHandleColorUI();
  }
  function updateArtworkPrice() {
    if (!artworkPriceNote) return;
    const withArtwork = variantForSelection({ artwork: true });
    const withoutArtwork = variantForSelection({ artwork: false });
    if (!withArtwork || !withoutArtwork) {
      artworkPriceNote.textContent = '';
      return;
    }
    const difference = Number(withArtwork.price || 0) - Number(withoutArtwork.price || 0);
    artworkPriceNote.textContent = difference > 0
      ? `Artwork option ${`+${formatMoney(difference)}`}`
      : 'Artwork option included';
  }
  function renderRecommendations() {
    if (!recommendationRow) return;
    recommendationRow.innerHTML = '';
    RECOMMENDATIONS.forEach((recommendation) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'recommendation-card';
      button.classList.toggle('on', state.recommendedSetup === recommendation.title);
      button.innerHTML = `<strong>${recommendation.title}</strong><span>${recommendation.description || ''}</span>`;
      button.addEventListener('click', async () => {
        const build = BUILDS[recommendation.build] ? recommendation.build : state.build;
        state.build = build;
        ensureBranchSelections();
        const shape = findByCode(SHAPES, recommendation.shapeCode, build);
        const core = findByCode(CORES, recommendation.coreCode, build);
        const surface = findByCode(SURFACES, recommendation.surfaceCode, build);
        if (shape) state.shape = shape.id;
        if (core) state.core = core.id;
        if (surface) state.surface = surface.id;
        ensureBranchSelections();
        state.recommendedSetup = recommendation.title;
        updateBuildUI();
        buildShapeUI();
        buildCoreUI();
        buildSurfaceUI();
        activateSurfaceTextures();
        loadSelectedShapeModel();
        await reconcileTemplateAfterAutomaticShapeChange();
        renderRecommendations();
        if (recommendationStatus) recommendationStatus.textContent = CFG.recommendationAppliedText || 'Recommended setup applied. You can still customize every option.';
        configurationChanged();
      });
      recommendationRow.appendChild(button);
    });
  }
  buildRow.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-build]');
    if (!button || button.dataset.build === state.build) return;
    state.build = button.dataset.build;
    state.recommendedSetup = '';
    ensureBranchSelections();
    updateBuildUI();
    buildShapeUI();
    buildCoreUI();
    buildSurfaceUI();
    activateSurfaceTextures();
    loadSelectedShapeModel();
    await reconcileTemplateAfterAutomaticShapeChange();
    renderRecommendations();
    configurationChanged();
  });
  function buildShapeUI() {
    shapeRow.innerHTML = '';
    SHAPES.filter((shape) => shape.build === state.build).forEach((shape) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'shape-card';
      button.dataset.shapeId = shape.id;
      button.classList.toggle('on', shape.id === state.shape);
      button.innerHTML = `
        <span class="shape-code">${shape.label}</span>
        <strong>${shape.thickness || '—'}</strong>
        <small>${shape.dimensions || 'Dimensions to be confirmed'}</small>
        <em>${shape.modelUrl ? '3D model ready' : 'Model placeholder'}</em>
        <span class="option-price">${optionPriceLabel(variantForSelection({ shape }))}</span>`;
      button.addEventListener('click', async () => {
        if (state.shape === shape.id) return;
        if (activeTemplateManifest && !variantForShape(activeTemplateManifest, shape.code)) {
          window.alert('This template is not available for the selected paddle shape. Choose another template or start from blank.');
          return;
        }
        state.shape = shape.id;
        state.recommendedSetup = '';
        ensureBranchSelections();
        [...shapeRow.children].forEach((card) => card.classList.toggle('on', card === button));
        updateShapeDetails();
        buildCoreUI();
        buildSurfaceUI();
        activateSurfaceTextures();
        loadSelectedShapeModel();
        if (activeTemplateManifest) {
          const family = TEMPLATE_FAMILIES.find((entry) => entry.id === state.template.familyId) || { name: state.template.familyName };
          const variant = variantForShape(activeTemplateManifest, shape.code);
          await applyStructuredTemplate(family, activeTemplateManifest, variant, { adapting: true }).catch(() => {});
        }
        configurationChanged();
      });
      shapeRow.appendChild(button);
    });
    updateShapeDetails();
  }
  function updateShapeDetails() {
    const shape = currentShape();
    if (!shape) return;
    shapeVal.textContent = `${shape.label} · ${shape.thickness || ''}`;
    shapeNote.innerHTML = `<b>${shape.label}</b> ${shape.description || ''}<br><span>${shape.dimensions || 'Dimensions to be confirmed'} · ${shape.modelUrl ? 'Dedicated 3D model connected' : 'The supplied model is shown as a temporary preview.'}</span>`;
    setModelBadge(shape, !shape.modelUrl);
  }
  function coldCellSize() {
    const thickness = parseFloat(currentShape()?.thickness || '16');
    return thickness <= 14 ? '6mm' : '8mm';
  }
  function buildCoreUI() {
    coreRow.innerHTML = '';
    const available = coresForSelection();
    available.forEach((core) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'core-card';
      button.dataset.coreId = core.id;
      button.classList.toggle('on', core.id === state.core);
      const fixed = state.build === 'cold' && available.length === 1;
      button.innerHTML = `
        <span class="type-badge">${core.playerType || 'All-Court'}</span>
        <strong>${core.label}</strong>
        <small>${fixed ? `${currentShape()?.thickness || ''} · ${coldCellSize()} cells` : core.description || ''}</small>
        <span class="option-price">${optionPriceLabel(variantForSelection({ core }))}</span>`;
      button.addEventListener('click', () => {
        state.core = core.id;
        state.recommendedSetup = '';
        [...coreRow.children].forEach((card) => card.classList.toggle('on', card === button));
        updateCoreDetails();
        loadSelectedCoreModel();
        configurationChanged();
      });
      coreRow.appendChild(button);
    });
    coreIntro.textContent = STEP_COPY[2]?.description || 'Choose the performance profile that best matches your game.';
    updateCoreDetails();
  }
  function updateCoreDetails() {
    const core = currentCore();
    if (!core) return;
    coreVal.textContent = `${core.label} · ${core.playerType || 'All-Court'}`;
    if (state.build === 'cold') {
      coreNote.innerHTML = `<b>${core.label}</b> ${core.description || ''}<br><span>${currentShape()?.thickness || ''} shape uses ${coldCellSize()} PP honeycomb cells. ${core.holeInfo || ''}</span>`;
    } else {
      coreNote.innerHTML = `<b>${core.playerType || 'All-Court'} profile:</b> ${core.description || ''}`;
    }
  }
  function previewPattern(surface) {
    const loaded = surfaceTextureCache.get(surface.id);
    if (loaded?.albedo?.src) return `url(${loaded.albedo.src})`;
    if (surface.pattern === 'albedo' && TEX.albedo) return `url(${TEX.albedo})`;
    const canvas = surface.pattern === '12k' ? makeWeave12K() : makeWeave3K();
    return `url(${canvas.toDataURL()})`;
  }
  function buildSurfaceUI(updateDetails = true) {
    weaveRow.innerHTML = '';
    surfacesForSelection().forEach((surface) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'opt';
      button.dataset.surfaceId = surface.id;
      button.classList.toggle('on', surface.id === state.surface);
      const thumb = document.createElement('div');
      thumb.className = 'thumb';
      thumb.style.backgroundImage = previewPattern(surface);
      button.appendChild(thumb);
      const label = document.createElement('span');
      label.className = 'lb';
      label.innerHTML = `<b>${surface.label}</b>${state.build === 'hot' ? `<small>Grit ${surface.grit || 'L100'}</small>` : ''}`;
      button.appendChild(label);
      const price = document.createElement('span');
      price.className = 'option-price';
      price.textContent = optionPriceLabel(variantForSelection({ surface }));
      button.appendChild(price);
      button.addEventListener('click', () => {
        state.surface = surface.id;
        state.recommendedSetup = '';
        [...weaveRow.children].forEach((card) => card.classList.toggle('on', card === button));
        updateSurfaceDetails();
        activateSurfaceTextures(surface);
        configurationChanged();
      });
      weaveRow.appendChild(button);
    });
    if (updateDetails) updateSurfaceDetails();
  }
  function updateSurfaceDetails() {
    const surface = currentSurface();
    if (!surface) return;
    weaveVal.textContent = state.build === 'hot' ? `${surface.label} · ${surface.grit || 'L100'}` : surface.label;
    surfaceNote.innerHTML = `<b>${surface.label}</b> ${surface.description || ''}${state.build === 'hot' ? `<br><span>Surface grit: ${surface.grit || 'L100'}</span>` : ''}`;
  }
  let lastOptionPriceContext = '';
  function updateOptionPriceLabels() {
    const context = [state.build, state.shape, state.core, state.surface, hasArtwork() ? 'art' : 'no-art'].join('|');
    if (context === lastOptionPriceContext) return;
    lastOptionPriceContext = context;
    $$('[data-shape-id]').forEach((button) => {
      const shape = SHAPES.find((entry) => entry.id === button.dataset.shapeId);
      const label = button.querySelector('.option-price');
      if (label) label.textContent = optionPriceLabel(variantForSelection({ shape }));
    });
    $$('[data-core-id]').forEach((button) => {
      const core = CORES.find((entry) => entry.id === button.dataset.coreId);
      const label = button.querySelector('.option-price');
      if (label) label.textContent = optionPriceLabel(variantForSelection({ core }));
    });
    $$('[data-surface-id]').forEach((button) => {
      const surface = SURFACES.find((entry) => entry.id === button.dataset.surfaceId);
      const label = button.querySelector('.option-price');
      if (label) label.textContent = optionPriceLabel(variantForSelection({ surface }));
    });
    updateArtworkPrice();
  }

  const edgeRow = $('#edgeRow');
  const edgeVal = $('#edgeVal');
  const ferruleRow = $('#ferruleRow');
  const gripRow = $('#gripRow');
  const handleVal = $('#handleVal');
  function buildEdgeUI() {
    if (!edgeRow) return;
    ensureColorSelections();
    edgeRow.innerHTML = '';
    colorsForBuild(EDGES, state.build).forEach((edge) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sw';
      button.style.background = edgeCssBackground(edge);
      button.title = edge.label;
      button.innerHTML = `<span>${edge.label}</span>`;
      button.setAttribute('aria-label', `Edge color ${edge.label}`);
      button.classList.toggle('on', edge.id === state.edge);
      button.addEventListener('click', () => {
        state.edge = edge.id;
        [...edgeRow.children].forEach((card) => card.classList.toggle('on', card === button));
        edgeVal.textContent = edge.label;
        applyEdge();
        configurationChanged();
      });
      edgeRow.appendChild(button);
    });
    edgeVal.textContent = item(EDGES, state.edge).label;
    applyEdge();
  }
  function buildHandleColorUI() {
    if (!ferruleRow || !gripRow) return;
    ensureColorSelections();
    ferruleRow.innerHTML = '';
    gripRow.innerHTML = '';
    colorsForBuild(FERRULES, state.build).forEach((ferrule) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sw';
      button.style.background = ferrule.hex;
      button.title = ferrule.label;
      button.innerHTML = `<span>${ferrule.label}</span>`;
      button.setAttribute('aria-label', `Ferrule color ${ferrule.label}`);
      button.classList.toggle('on', ferrule.id === state.ferrule);
      button.addEventListener('click', () => {
        state.ferrule = ferrule.id;
        [...ferruleRow.children].forEach((card) => card.classList.toggle('on', card === button));
        applyFerrule();
        updateHandleValue();
        configurationChanged();
      });
      ferruleRow.appendChild(button);
    });
    colorsForBuild(GRIPS, state.build).forEach((grip) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'opt';
      button.classList.toggle('on', grip.id === state.grip);
      const thumb = document.createElement('div');
      thumb.className = 'gth';
      thumb.style.background = `url(${gripWrap(grip.base).toDataURL()})`;
      thumb.style.backgroundSize = '120px';
      button.appendChild(thumb);
      const label = document.createElement('span');
      label.className = 'lb';
      label.textContent = grip.label;
      button.appendChild(label);
      button.addEventListener('click', () => {
        state.grip = grip.id;
        [...gripRow.children].forEach((card) => card.classList.toggle('on', card === button));
        applyGrip();
        updateHandleValue();
        configurationChanged();
      });
      gripRow.appendChild(button);
    });
    applyFerrule();
    applyGrip();
    updateHandleValue();
  }
  function updateHandleValue() {
    handleVal.textContent = `${item(FERRULES, state.ferrule).label} ferrule · ${item(GRIPS, state.grip).label} grip`;
  }

  /* ---------- Artwork upload ---------- */
  const fileInput = $('#fileInput');
  const uploadBox = $('#uplBox');
  const sliders = $('#sliders');
  const imageValue = $('#imgVal');
  const tabFront = $('#tabF');
  const tabBack = $('#tabB');
  const syncBothFaces = $('#syncBF');
  /* The unchecked control is authoritative on every new page load. */
  state.sync = false;
  if (syncBothFaces) syncBothFaces.checked = false;
  const restoreRatioButton = $('#restoreRatioButton');
  const aspectRatioLock = $('#aspectRatioLock');
  const aspectRatioLockText = $('#aspectRatioLockText');
  const uploadStatus = $('#uploadStatus');
  const artworkUploadTrigger = $('#artworkUploadTrigger');
  const templateLibraryTrigger = $('#templateLibraryTrigger');
  const artworkTextTrigger = $('#artworkTextTrigger');
  const templateLibraryModal = $('#templateLibraryModal');
  const templateLibraryGrid = $('#templateLibraryGrid');
  const templateLibraryEmpty = $('#templateLibraryEmpty');
  const templateShapeStatus = $('#templateShapeStatus');
  const startBlankDesign = $('#startBlankDesign');
  const templateDetail = $('#templateDetail');
  const templateDetailBack = $('#templateDetailBack');
  const templateDetailImage = $('#templateDetailImage');
  const templateDetailCategory = $('#templateDetailCategory');
  const templateDetailTitle = $('#templateDetailTitle');
  const templateDetailDescription = $('#templateDetailDescription');
  const templateDetailUseCase = $('#templateDetailUseCase');
  const templateDetailTags = $('#templateDetailTags');
  const templateDetailUse = $('#templateDetailUse');
  const textLayerModal = $('#textLayerModal');
  const textLayerForm = $('#textLayerForm');
  const customTextInput = $('#customTextInput');
  const customTextFont = $('#customTextFont');
  const customTextColor = $('#customTextColor');
  const customTextColorValue = $('#customTextColorValue');
  const textLayerSubmit = $('#textLayerSubmit');
  let customTextFontPicker = null;
  const ranges = { x: $('#sX'), y: $('#sY'), s: $('#sSize'), w: $('#sW'), h: $('#sH'), r: $('#sR') };
  const outputs = { x: $('#oX'), y: $('#oY'), s: $('#oSize'), w: $('#oW'), h: $('#oH'), r: $('#oR') };
  const rotateNumber = $('#sRNumber');
  const MAX_LAYERS = 10;
  const manifestCache = new Map();
  let activeTemplateManifest = null;
  let activeTemplateVariant = null;
  let selectedTemplateFamily = null;
  let pendingTemplateSlot = null;
  const faceConfig = () => state.faces[state.activeFace];
  const currentLayer = () => faceConfig().layers[faceConfig().sel] || null;
  const layerPreviewSource = (layer) => {
    if (!layer?.img) return '';
    if (typeof layer.img.toDataURL === 'function') return layer.img.toDataURL('image/png');
    return layer.img.currentSrc || layer.img.src || '';
  };
  const layerKindLabel = (layer) => {
    if (layer.type === 'text') return 'Custom text';
    if (layer.locked) return 'Locked template layer';
    if (layer.templateSlotId) return 'Template photo';
    if (layer.type === 'template') return 'Template';
    return 'Uploaded artwork';
  };
  function layerScale(layer, fallback = 85) {
    return Math.max(10, Math.min(250, Number(layer?.s ?? fallback) || fallback));
  }
  function normalizeLayerTransform(layer) {
    if (!layer) return;
    layer.aspectLocked = layer.aspectLocked !== false;
    const width = Math.max(10, Math.min(250, Number(layer.w ?? layer.s ?? 85) || 85));
    const height = Math.max(10, Math.min(250, Number(layer.h ?? layer.s ?? 85) || 85));
    if (layer.aspectLocked) {
      const uniform = layerScale(layer, Math.sqrt(width * height));
      layer.w = uniform;
      layer.h = uniform;
      layer.s = uniform;
    } else {
      layer.w = width;
      layer.h = height;
      layer.s = Math.sqrt(width * height);
    }
  }
  function resetArtworkLayer(layer) {
    if (!layer) return;
    const keepLockState = layer.aspectLocked !== false;
    const zone = layer.templateSlotId
      ? activeTemplateVariant?.front?.editable_zones?.find((entry) => entry.id === layer.templateSlotId)
      : null;
    const transform = zone?.default_transform || {};
    const isTemplateSlot = Boolean(zone);
    const isText = layer.type === 'text' && !isTemplateSlot;
    layer.x = Number(isTemplateSlot ? (transform.x ?? 0) : 0);
    layer.y = Number(isTemplateSlot ? (transform.y ?? 0) : 0);
    layer.w = Number(isTemplateSlot ? (transform.width ?? 100) : (isText ? 78 : 100));
    layer.h = Number(isTemplateSlot ? (transform.height ?? 100) : (isText ? 78 : 100));
    layer.r = Number(isTemplateSlot ? (transform.rotation ?? 0) : 0);
    layer.s = Math.sqrt(layer.w * layer.h);
    layer.aspectLocked = keepLockState;
    if (!isTemplateSlot && !isText) layer.fitMode = 'contain-print-area';
  }
  function syncAspectRatioLock() {
    const layer = currentLayer();
    if (!aspectRatioLock) return;
    const isLocked = Boolean(layer && layer.aspectLocked !== false);
    aspectRatioLock.disabled = !layer || Boolean(layer.locked);
    aspectRatioLock.setAttribute('aria-pressed', String(isLocked));
    aspectRatioLock.classList.toggle('is-locked', isLocked);
    aspectRatioLock.title = isLocked
      ? 'Proportions locked. Width and height scale together.'
      : 'Proportions unlocked. Width and height can be changed separately.';
    if (aspectRatioLockText) aspectRatioLockText.textContent = isLocked ? 'Proportions locked' : 'Proportions unlocked';
  }
  function renderUpload() {
    const cfg = faceConfig();
    const backSynced = state.activeFace === 'back' && state.sync;
    if (backSynced) {
      uploadBox.innerHTML = `<div class="note" style="margin-top:0"><b>${COPY.syncedArtworkTitle || 'Your design is matched on both sides.'}</b> ${COPY.syncedArtworkHelp || 'Turn off syncing to customize the back separately.'}</div>`;
      sliders.classList.remove('show');
      return;
    }
    uploadBox.innerHTML = '';
    if (state.activeFace === 'front' && activeTemplateVariant?.front?.editable_zones) {
      activeTemplateVariant.front.editable_zones
        .filter((zone) => ['photo', 'logo'].includes(zone.type))
        .forEach((zone) => {
          const existing = cfg.layers.find((layer) => layer.templateSlotId === zone.id);
          if (existing) return;
          const slotButton = document.createElement('button');
          slotButton.type = 'button';
          slotButton.className = 'template-slot-add';
          slotButton.innerHTML = `<strong>＋ Add ${zone.label || zone.type}</strong><span>Image stays inside the template frame</span>`;
          slotButton.addEventListener('click', () => {
            pendingTemplateSlot = zone;
            fileInput.multiple = false;
            fileInput.click();
          });
          uploadBox.appendChild(slotButton);
        });
    }
    cfg.layers.forEach((layer, index) => {
      const row = document.createElement('div');
      row.className = `layer-row${index === cfg.sel ? ' on' : ''}${layer.locked ? ' is-locked' : ''}`;
      const image = document.createElement('img');
      image.src = layerPreviewSource(layer);
      image.alt = '';
      row.appendChild(image);
      const info = document.createElement('div');
      const filename = document.createElement('div');
      filename.className = 'fn';
      filename.textContent = layer.name;
      const meta = document.createElement('div');
      meta.className = 'sm';
      meta.textContent = `${layerKindLabel(layer)} ${index + 1}`;
      info.append(filename, meta);
      row.appendChild(info);
      if (layer.type === 'text' && !layer.locked) {
        const edit = document.createElement('button');
        edit.type = 'button';
        edit.className = 'edit-layer';
        edit.textContent = 'Edit';
        edit.addEventListener('click', (event) => {
          event.stopPropagation();
          cfg.sel = index;
          openTextEditor(layer);
        });
        row.appendChild(edit);
      }
      if (!layer.locked) {
        if (layer.templateSlotId && ['image', 'logo'].includes(layer.type)) {
          const replace = document.createElement('button');
          replace.type = 'button';
          replace.className = 'edit-layer';
          replace.textContent = 'Replace';
          replace.addEventListener('click', (event) => {
            event.stopPropagation();
            pendingTemplateSlot = activeTemplateVariant?.front?.editable_zones?.find((zone) => zone.id === layer.templateSlotId) || null;
            fileInput.multiple = false;
            fileInput.click();
          });
          row.appendChild(replace);
        }
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'rm';
        remove.textContent = COPY.removeArtwork || 'Remove';
        remove.addEventListener('click', (event) => {
          event.stopPropagation();
          if (!window.confirm(`Remove ${layer.name} from the ${state.activeFace} design?`)) return;
          cfg.layers.splice(index, 1);
          cfg.sel = Math.min(cfg.sel, cfg.layers.length - 1);
          renderUpload();
          composeFace(state.activeFace);
          if (state.sync) composeFace('back');
          refreshImageValue();
          configurationChanged();
        });
        row.appendChild(remove);
        row.addEventListener('click', () => { cfg.sel = index; renderUpload(); });
      }
      uploadBox.appendChild(row);
    });
    if (cfg.layers.length < MAX_LAYERS) {
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'addLayer';
      add.textContent = cfg.layers.length
        ? (COPY.uploadMore || '＋ Add Another Design')
        : (COPY.uploadFirst || '＋ Upload Your Design · PNG / JPG / SVG');
      add.addEventListener('click', () => {
        pendingTemplateSlot = null;
        fileInput.multiple = true;
        fileInput.click();
      });
      uploadBox.appendChild(add);
    }
    const layer = currentLayer();
    if (layer && !layer.locked) {
      sliders.classList.add('show');
      normalizeLayerTransform(layer);
      ranges.x.value = layer.x;
      ranges.y.value = layer.y;
      ranges.s.value = layer.s;
      ranges.w.value = layer.w ?? layer.s ?? 85;
      ranges.h.value = layer.h ?? layer.s ?? 85;
      ranges.r.value = layer.r;
      syncSliderOutputs();
      syncAspectRatioLock();
    } else {
      sliders.classList.remove('show');
      syncAspectRatioLock();
    }
  }
  artworkUploadTrigger?.addEventListener('click', () => {
    pendingTemplateSlot = null;
    fileInput.multiple = true;
    fileInput.click();
  });
  let textLayerTarget = null;
  function openArtworkModal(modal) {
    if (!modal) return;
    modal.hidden = false;
    section.classList.add('artwork-modal-open');
    document.documentElement.style.overflow = 'hidden';
    requestAnimationFrame(() => modal.querySelector('button, input, select')?.focus());
  }
  function closeArtworkModals() {
    [templateLibraryModal, textLayerModal].forEach((modal) => {
      if (modal) modal.hidden = true;
    });
    section.classList.remove('artwork-modal-open');
    document.documentElement.style.overflow = '';
    textLayerTarget = null;
  }
  $$('[data-close-artwork-modal]').forEach((button) => button.addEventListener('click', closeArtworkModals));

  function loadArtworkImage(url) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.addEventListener('load', () => resolve(image), { once: true });
      image.addEventListener('error', () => reject(new Error('This template image could not be loaded.')), { once: true });
      image.src = url;
    });
  }
  function makeImageLayer(image, options = {}) {
    const sourceWidth = Math.max(1, image?.naturalWidth || image?.width || 1);
    const sourceHeight = Math.max(1, image?.naturalHeight || image?.height || 1);
    return {
      type: options.type || 'image',
      sourceType: options.sourceType || 'upload',
      img: image,
      file: options.file || null,
      templateId: options.templateId || '',
      templateFamilyId: options.templateFamilyId || '',
      templateVariantId: options.templateVariantId || '',
      templateSlotId: options.templateSlotId || '',
      templateRole: options.templateRole || '',
      slotFrame: options.slotFrame || null,
      fixedCanvas: Boolean(options.fixedCanvas),
      fitMode: options.fitMode || '',
      originalAspectRatio: sourceWidth / sourceHeight,
      aspectLocked: options.aspectLocked !== false,
      locked: Boolean(options.locked),
      z: Number(options.z ?? 50),
      name: options.name || 'Artwork',
      hash: options.hash || hash6(options.name || image.src || String(Date.now())),
      x: Number(options.x || 0),
      y: Number(options.y || 0),
      s: Number(options.s ?? Math.sqrt(Number(options.w ?? 85) * Number(options.h ?? 85))),
      w: Number(options.w ?? 85),
      h: Number(options.h ?? 85),
      r: Number(options.r || 0)
    };
  }
  async function addTemplateToFace(template, face, url) {
    const cfg = state.faces[face];
    if (cfg.layers.length >= MAX_LAYERS) throw new Error(`The ${face} side already has the maximum number of design layers.`);
    const image = await loadArtworkImage(url);
    cfg.layers.push(makeImageLayer(image, {
      type: 'template',
      sourceType: 'template',
      templateId: template.id,
      name: template.title || 'Joysent Template',
      hash: hash6(`${template.id}-${face}-${url}`),
      x: template.x,
      y: template.y,
      w: template.width,
      h: template.height
    }));
    cfg.sel = cfg.layers.length - 1;
  }
  async function applyArtworkTemplate(template) {
    templateLibraryGrid?.classList.add('is-loading');
    uploadStatus.textContent = `Adding ${template.title || 'template'}…`;
    try {
      if (template.applyBoth) {
        if (state.faces.front.layers.length >= MAX_LAYERS || state.faces.back.layers.length >= MAX_LAYERS) {
          throw new Error('Remove a design layer before applying this template to both sides.');
        }
        state.sync = false;
        syncBothFaces.checked = false;
        await addTemplateToFace(template, 'front', template.frontUrl);
        await addTemplateToFace(template, 'back', template.backUrl || template.frontUrl);
      } else {
        const face = state.activeFace === 'back' && state.sync ? 'front' : state.activeFace;
        const url = face === 'back' ? (template.backUrl || template.frontUrl) : template.frontUrl;
        await addTemplateToFace(template, face, url);
        state.activeFace = face;
      }
      tabFront.classList.toggle('on', state.activeFace === 'front');
      tabBack.classList.toggle('on', state.activeFace === 'back');
      composeFace('front');
      composeFace('back');
      renderUpload();
      refreshImageValue();
      configurationChanged();
      uploadStatus.textContent = `${template.title || 'Template'} added. You can resize it, move it or layer text above it.`;
      closeArtworkModals();
    } catch (error) {
      uploadStatus.textContent = error.message || 'This template could not be added.';
    } finally {
      templateLibraryGrid?.classList.remove('is-loading');
    }
  }
  function absoluteAssetUrl(value, manifestUrl) {
    if (!value) return '';
    if (/^(https?:)?\/\//i.test(value) || /^data:/i.test(value)) return value;
    try {
      const base = new URL(manifestUrl, window.location.href);
      return new URL(value, base).href;
    } catch (error) {
      return value;
    }
  }
  function normalizedShapeCodes(family) {
    return (family.supportedShapes || []).map((value) => String(value || '').trim().toUpperCase()).filter(Boolean);
  }
  function variantForShape(manifest, shapeCode = currentShape()?.code) {
    const wanted = String(shapeCode || '').trim().toUpperCase();
    const entries = Object.entries(manifest?.shape_variants || {});
    return entries.find(([key, variant]) => String(variant?.shape_id || key).trim().toUpperCase() === wanted)?.[1] || null;
  }
  async function loadTemplateManifest(family) {
    if (manifestCache.has(family.id)) return manifestCache.get(family.id);
    const promise = fetch(family.manifestUrl, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('Template could not be loaded. Please try again or start from a blank design.');
        return response.json();
      })
      .then((manifest) => {
        if (!manifest?.template_family_id || !manifest?.shape_variants) throw new Error('The template manifest is incomplete.');
        manifest.__url = family.manifestUrl;
        return manifest;
      })
      .catch((error) => {
        manifestCache.delete(family.id);
        throw error;
      });
    manifestCache.set(family.id, promise);
    return promise;
  }
  function clearStructuredTemplate({ preserveSlotContent = true } = {}) {
    ['front', 'back'].forEach((face) => {
      state.faces[face].layers = state.faces[face].layers.reduce((kept, layer) => {
        if (layer.sourceType === 'template-fixed') return kept;
        if (layer.sourceType === 'template-default-text' && !preserveSlotContent) return kept;
        if (layer.templateSlotId && preserveSlotContent) {
          layer.slotFrame = null;
          layer.templateSlotId = '';
          layer.templateFamilyId = '';
          layer.templateVariantId = '';
          layer.sourceType = layer.type === 'text' ? 'text' : 'upload';
        } else if (layer.templateSlotId) {
          return kept;
        }
        kept.push(layer);
        return kept;
      }, []);
      state.faces[face].sel = Math.min(state.faces[face].sel, state.faces[face].layers.length - 1);
    });
    state.template = { familyId: '', familyName: '', variantId: '', version: '', colorPreset: '' };
    activeTemplateManifest = null;
    activeTemplateVariant = null;
  }
  async function buildStructuredLayers(family, manifest, variant, preservedSlots = new Map()) {
    const fixed = variant?.front?.fixed_layers || [];
    const fixedLayers = await Promise.all(fixed.map(async (definition) => {
      const url = absoluteAssetUrl(definition.asset, manifest.__url);
      const image = await loadArtworkImage(url);
      return makeImageLayer(image, {
        type: 'template',
        sourceType: 'template-fixed',
        templateId: manifest.template_family_id,
        templateFamilyId: manifest.template_family_id,
        templateVariantId: variant.variant_id,
        templateRole: definition.role,
        name: definition.name || definition.role || 'Template layer',
        hash: hash6(`${variant.variant_id}-${definition.id}-${url}`),
        fixedCanvas: true,
        locked: true,
        z: definition.z ?? (definition.role === 'background' ? 0 : 90)
      });
    }));
    const editableZones = variant?.front?.editable_zones || [];
    const editableLayers = [];
    editableZones.forEach((zone) => {
      const transform = zone.default_transform || {};
      const preserved = preservedSlots.get(zone.id);
      if (preserved && ['photo', 'logo'].includes(zone.type)) {
        editableLayers.push(makeImageLayer(preserved.img, {
          type: zone.type === 'logo' ? 'logo' : 'image',
          sourceType: 'template-user',
          file: preserved.file,
          templateFamilyId: manifest.template_family_id,
          templateVariantId: variant.variant_id,
          templateSlotId: zone.id,
          slotFrame: zone.frame,
          name: preserved.name || zone.label,
          hash: preserved.hash,
          x: transform.x,
          y: transform.y,
          w: transform.width,
          h: transform.height,
          r: transform.rotation,
          z: zone.z ?? 30
        }));
      } else if (zone.type === 'text') {
        const previousText = preserved?.text || zone.default_text || '';
        const font = preserved?.font || zone.font || 'Arial';
        const color = preserved?.color || zone.color || '#173c36';
        const canvas = createTextCanvas(previousText, font, color);
        editableLayers.push(makeImageLayer(canvas, {
          type: 'text',
          sourceType: 'template-default-text',
          templateFamilyId: manifest.template_family_id,
          templateVariantId: variant.variant_id,
          templateSlotId: zone.id,
          name: zone.label || `Text: ${previousText}`,
          hash: hash6(`${variant.variant_id}-${zone.id}-${previousText}`),
          x: transform.x,
          y: transform.y,
          w: transform.width,
          h: transform.height,
          r: transform.rotation,
          z: zone.z ?? 60
        }));
        Object.assign(editableLayers[editableLayers.length - 1], {
          text: previousText,
          font,
          color,
          maxLength: Number(zone.max_length || 60)
        });
      }
    });
    return [...fixedLayers, ...editableLayers];
  }
  function templateSlotContent() {
    const map = new Map();
    state.faces.front.layers.forEach((layer) => {
      if (layer.templateSlotId && !layer.locked) map.set(layer.templateSlotId, layer);
    });
    return map;
  }
  async function applyStructuredTemplate(family, manifest, variant, { adapting = false } = {}) {
    templateLibraryGrid?.classList.add('is-loading');
    uploadStatus.textContent = adapting
      ? 'Adapting your template to the new paddle shape…'
      : `Loading ${manifest.name || family.name} for ${currentShape()?.label}…`;
    const preservedSlots = adapting ? templateSlotContent() : new Map();
    const freeLayers = state.faces.front.layers.filter((layer) => !layer.locked && !layer.templateSlotId && !String(layer.sourceType).startsWith('template-'));
    try {
      const templateLayers = await buildStructuredLayers(family, manifest, variant, preservedSlots);
      if (templateLayers.length + freeLayers.length > MAX_LAYERS) {
        throw new Error('This template needs more layer space. Remove one or more free artwork layers and try again.');
      }
      state.faces.front.layers = [...templateLayers, ...freeLayers];
      state.faces.front.sel = state.faces.front.layers.findIndex((layer) => !layer.locked);
      state.sync = false;
      syncBothFaces.checked = false;
      state.activeFace = 'front';
      activeTemplateManifest = manifest;
      activeTemplateVariant = variant;
      state.template = {
        familyId: manifest.template_family_id,
        familyName: manifest.name || family.name,
        variantId: variant.variant_id,
        version: String(manifest.version || family.version || '1'),
        colorPreset: variant.default_color_presets?.[0]?.id || ''
      };
      tabFront.classList.add('on');
      tabBack.classList.remove('on');
      composeFace('front');
      composeFace('back');
      renderUpload();
      refreshImageValue();
      configurationChanged();
      uploadStatus.textContent = adapting
        ? 'Your template has been adapted to the new paddle shape. Please review the artwork placement.'
        : `${state.template.familyName} is ready. Add your photo and edit the placeholder text.`;
      closeArtworkModals();
    } catch (error) {
      uploadStatus.textContent = error.message || 'Template could not be loaded. Please try again or start from a blank design.';
      if (templateLibraryEmpty) {
        templateLibraryEmpty.hidden = false;
        templateLibraryEmpty.textContent = `${uploadStatus.textContent} Use Start from Blank or choose another template.`;
      }
      throw error;
    } finally {
      templateLibraryGrid?.classList.remove('is-loading');
    }
  }
  async function reconcileTemplateAfterAutomaticShapeChange() {
    if (!activeTemplateManifest) return;
    const variant = variantForShape(activeTemplateManifest, currentShape()?.code);
    if (!variant) {
      window.alert('The selected build changed the paddle shape, but this template family has no matching version. The template was removed and your uploaded content was kept in blank design mode.');
      clearStructuredTemplate({ preserveSlotContent: true });
      composeFace('front');
      composeFace('back');
      renderUpload();
      refreshImageValue();
      return;
    }
    const family = TEMPLATE_FAMILIES.find((entry) => entry.id === state.template.familyId) || { name: state.template.familyName };
    await applyStructuredTemplate(family, activeTemplateManifest, variant, { adapting: true }).catch(() => {});
  }
  async function showTemplateDetail(family, manifest, variant) {
    selectedTemplateFamily = { family, manifest, variant };
    templateLibraryGrid.hidden = true;
    templateLibraryEmpty.hidden = true;
    templateDetail.hidden = false;
    const showcase = variant.showcase_image || family.showcaseUrl || variant.thumbnail;
    templateDetailImage.src = absoluteAssetUrl(showcase, manifest.__url);
    templateDetailImage.alt = `${manifest.name || family.name} finished example`;
    templateDetailCategory.textContent = manifest.category || family.category || 'Template';
    templateDetailTitle.textContent = manifest.name || family.name;
    templateDetailDescription.textContent = manifest.description || family.description || '';
    templateDetailUseCase.textContent = `Recommended for: ${manifest.use_case || family.useCase || 'personalized paddle designs'}`;
    templateDetailTags.innerHTML = '';
    (manifest.editable_features || family.editableFeatures || []).forEach((feature) => {
      const tag = document.createElement('span');
      tag.textContent = String(feature).trim();
      templateDetailTags.appendChild(tag);
    });
  }
  async function renderTemplateLibrary() {
    if (!templateLibraryGrid) return;
    templateLibraryGrid.hidden = false;
    templateDetail.hidden = true;
    templateLibraryGrid.innerHTML = '<p class="template-library-loading">Loading templates for this paddle shape…</p>';
    const shapeCode = currentShape()?.code || '';
    if (templateShapeStatus) templateShapeStatus.textContent = `Showing templates for ${currentShape()?.label || shapeCode}.`;
    const families = TEMPLATE_FAMILIES.filter((family) => {
      const supported = normalizedShapeCodes(family);
      return !supported.length || supported.includes(String(shapeCode).toUpperCase());
    });
    const available = [];
    await Promise.all(families.map(async (family) => {
      try {
        const manifest = await loadTemplateManifest(family);
        const variant = variantForShape(manifest, shapeCode);
        if (variant) available.push({ family, manifest, variant });
      } catch (error) {
        console.warn(`Template family ${family.id} could not load.`, error);
      }
    }));
    templateLibraryGrid.innerHTML = '';
    available
      .filter((entry) => entry?.family && entry?.manifest && entry?.variant)
      .sort((a, b) => String(a.family?.name || a.manifest?.name || '').localeCompare(String(b.family?.name || b.manifest?.name || '')))
      .forEach(({ family, manifest, variant }) => {
      const card = document.createElement('article');
      card.className = 'template-card';
      const image = document.createElement('img');
      image.src = absoluteAssetUrl(variant.thumbnail || family.showcaseUrl, manifest.__url);
      image.alt = `${manifest.name || family.name} template for ${currentShape()?.label}`;
      image.loading = 'lazy';
      image.width = 520;
      image.height = 640;
      const copy = document.createElement('span');
      copy.innerHTML = `<small>${manifest.category || family.category || 'Template'}</small><strong>${manifest.name || family.name}</strong><em>${manifest.description || family.description || ''}</em>`;
      const tags = document.createElement('div');
      tags.className = 'template-card__tags';
      (manifest.editable_features || family.editableFeatures || []).slice(0, 4).forEach((feature) => {
        const tag = document.createElement('i');
        tag.textContent = String(feature).trim();
        tags.appendChild(tag);
      });
      const actions = document.createElement('div');
      actions.className = 'template-card__actions';
      const preview = document.createElement('button');
      preview.type = 'button';
      preview.textContent = 'Preview';
      preview.addEventListener('click', () => showTemplateDetail(family, manifest, variant));
      const use = document.createElement('button');
      use.type = 'button';
      use.textContent = 'Use Template';
      use.addEventListener('click', () => applyStructuredTemplate(family, manifest, variant).catch(() => {}));
      actions.append(preview, use);
      copy.append(tags, actions);
      card.append(image, copy);
      templateLibraryGrid.appendChild(card);
      });
    ARTWORK_TEMPLATES.forEach((template) => {
      const card = document.createElement('article');
      card.className = 'template-card template-card--legacy';
      card.innerHTML = `<img src="${template.frontUrl}" alt="${template.title || 'Artwork template'}" loading="lazy" width="520" height="640"><span><small>${template.category || 'Legacy template'}</small><strong>${template.title || 'Joysent Template'}</strong><em>${template.description || ''}</em></span>`;
      const use = document.createElement('button');
      use.type = 'button';
      use.className = 'legacy-template-use';
      use.textContent = 'Use Legacy Template';
      use.addEventListener('click', () => applyArtworkTemplate(template));
      card.querySelector('span').appendChild(use);
      templateLibraryGrid.appendChild(card);
    });
    const count = available.length + ARTWORK_TEMPLATES.length;
    templateLibraryEmpty.hidden = count > 0;
    if (!count) templateLibraryEmpty.textContent = `No template version is available for ${currentShape()?.label || shapeCode}. Choose another template or start from blank.`;
  }
  templateDetailBack?.addEventListener('click', () => {
    templateDetail.hidden = true;
    templateLibraryGrid.hidden = false;
    templateLibraryEmpty.hidden = templateLibraryGrid.children.length > 0;
    selectedTemplateFamily = null;
  });
  templateDetailUse?.addEventListener('click', () => {
    if (!selectedTemplateFamily) return;
    const { family, manifest, variant } = selectedTemplateFamily;
    applyStructuredTemplate(family, manifest, variant).catch(() => {});
  });
  startBlankDesign?.addEventListener('click', () => {
    clearStructuredTemplate({ preserveSlotContent: true });
    composeFace('front');
    composeFace('back');
    renderUpload();
    refreshImageValue();
    configurationChanged();
    uploadStatus.textContent = 'Blank design mode is active. Your uploaded content has been kept as free artwork layers.';
    closeArtworkModals();
  });
  templateLibraryTrigger?.addEventListener('click', () => {
    renderTemplateLibrary().catch(() => {});
    openArtworkModal(templateLibraryModal);
  });

  function createTextCanvas(text, fontFamily, color) {
    const safeFont = String(fontFamily || 'Arial').replace(/["\\]/g, '');
    const measureCanvas = document.createElement('canvas');
    const measureContext = measureCanvas.getContext('2d');
    let fontSize = 260;
    measureContext.font = `800 ${fontSize}px "${safeFont}"`;
    let metrics = measureContext.measureText(text);
    const maximumWidth = 1900;
    if (metrics.width > maximumWidth) {
      fontSize = Math.max(72, Math.floor(fontSize * maximumWidth / metrics.width));
      measureContext.font = `800 ${fontSize}px "${safeFont}"`;
      metrics = measureContext.measureText(text);
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(320, Math.ceil(metrics.width + fontSize * 0.7));
    canvas.height = Math.max(220, Math.ceil(fontSize * 1.55));
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = color || '#ffffff';
    ctx.font = `800 ${fontSize}px "${safeFont}"`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    return canvas;
  }

  function selectedFontLabel(fontValue) {
    const option = Array.from(customTextFont?.options || []).find((item) => item.value === fontValue);
    return option?.textContent?.trim() || String(fontValue || 'Arial').split(',')[0].replace(/["']/g, '').trim();
  }

  function updateTextFontPreview() {
    if (!customTextInput || !customTextFont) return;
    const font = customTextFont.value || 'Arial';
    customTextInput.style.fontFamily = `"${String(font).replace(/["\\]/g, '')}", sans-serif`;
    customTextFontPicker?.querySelectorAll('[data-text-font]').forEach((button) => {
      const selected = button.dataset.textFont === font;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
      button.tabIndex = selected ? 0 : -1;
    });
  }

  function initTextFontPicker() {
    if (!customTextFont || customTextFont.dataset.previewPickerReady === 'true') return;
    customTextFont.dataset.previewPickerReady = 'true';
    customTextFont.classList.add('custom-text-font-native');

    customTextFontPicker = document.createElement('div');
    customTextFontPicker.className = 'custom-text-font-picker';
    customTextFontPicker.setAttribute('role', 'listbox');
    customTextFontPicker.setAttribute('aria-label', 'Choose a font');

    Array.from(customTextFont.options).forEach((option) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'custom-text-font-option';
      button.dataset.textFont = option.value;
      button.setAttribute('role', 'option');
      button.textContent = option.textContent;
      button.style.fontFamily = `"${String(option.value).replace(/["\\]/g, '')}", sans-serif`;
      button.addEventListener('click', () => {
        customTextFont.value = option.value;
        customTextFont.dispatchEvent(new Event('change', { bubbles: true }));
        customTextInput?.focus({ preventScroll: true });
      });
      customTextFontPicker.appendChild(button);
    });

    customTextFont.insertAdjacentElement('afterend', customTextFontPicker);
    updateTextFontPreview();
  }

  function openTextEditor(layer = null) {
    textLayerTarget = layer?.type === 'text' ? layer : null;
    customTextInput.maxLength = Number(textLayerTarget?.maxLength || 60);
    customTextInput.value = textLayerTarget?.text || '';
    customTextFont.value = textLayerTarget?.font || 'Arial';
    customTextColor.value = textLayerTarget?.color || '#ffffff';
    customTextColorValue.textContent = customTextColor.value.toUpperCase();
    updateTextFontPreview();
    textLayerSubmit.textContent = textLayerTarget
      ? (CFG.textUpdateButton || 'Update Text')
      : (CFG.textAddButton || 'Add Text to Paddle');
    openArtworkModal(textLayerModal);
    requestAnimationFrame(() => customTextInput.focus());
  }
  initTextFontPicker();
  artworkTextTrigger?.addEventListener('click', () => openTextEditor(null));
  customTextInput?.addEventListener('input', updateTextFontPreview);
  customTextFont?.addEventListener('change', updateTextFontPreview);
  customTextColor?.addEventListener('input', () => {
    customTextColorValue.textContent = customTextColor.value.toUpperCase();
  });
  textLayerForm?.addEventListener('submit', (event) => {
    event.preventDefault();
    const text = customTextInput.value.trim();
    if (!text) return;
    const font = customTextFont.value;
    const color = customTextColor.value;
    const canvas = createTextCanvas(text, font, color);
    if (textLayerTarget) {
      textLayerTarget.text = text;
      textLayerTarget.font = font;
      textLayerTarget.color = color;
      textLayerTarget.img = canvas;
      textLayerTarget.name = `Text: ${text}`;
      textLayerTarget.hash = hash6(`${text}|${font}|${color}`);
    } else {
      const face = state.activeFace === 'back' && state.sync ? 'front' : state.activeFace;
      const cfg = state.faces[face];
      if (cfg.layers.length >= MAX_LAYERS) {
        uploadStatus.textContent = `The ${face} side already has the maximum number of design layers.`;
        closeArtworkModals();
        return;
      }
      cfg.layers.push({
        type: 'text',
        sourceType: 'text',
        img: canvas,
        file: null,
        text,
        font,
        color,
        name: `Text: ${text}`,
        hash: hash6(`${text}|${font}|${color}`),
        x: 0,
        y: 0,
        s: 78,
        w: 78,
        h: 78,
        r: 0
      });
      cfg.sel = cfg.layers.length - 1;
      state.activeFace = face;
    }
    tabFront.classList.toggle('on', state.activeFace === 'front');
    tabBack.classList.toggle('on', state.activeFace === 'back');
    composeFace(state.activeFace);
    if (state.sync) composeFace('back');
    renderUpload();
    refreshImageValue();
    configurationChanged();
    uploadStatus.textContent = 'Text added to the design. Use the controls below to position and resize it.';
    closeArtworkModals();
  });

  function refreshImageValue() {
    const frontCount = state.faces.front.layers.length;
    const backCount = state.sync ? frontCount : state.faces.back.layers.length;
    imageValue.textContent = !frontCount && !backCount
      ? (COPY.artworkEmpty || 'No artwork yet')
      : `${COPY.artworkFront || 'Front'} ${frontCount} · ${COPY.artworkBack || 'Back'} ${backCount}`;
  }
  fileInput.addEventListener('change', () => {
    const files = [...fileInput.files];
    if (!files.length) return;
    const slot = pendingTemplateSlot;
    const cfg = slot ? state.faces.front : faceConfig();
    const allowed = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']);
    const validFiles = files
      .filter((file) => allowed.has(file.type) && file.size <= 15 * 1024 * 1024)
      .slice(0, slot ? 1 : MAX_LAYERS - cfg.layers.length);
    if (!validFiles.length) {
      uploadStatus.textContent = CFG.uploadErrorText || 'This file could not be added. Use PNG, JPG, WEBP or SVG up to 15 MB.';
      fileInput.value = '';
      pendingTemplateSlot = null;
      fileInput.multiple = true;
      return;
    }
    uploadStatus.textContent = `Uploading ${validFiles.length} design${validFiles.length > 1 ? 's' : ''}…`;
    let completed = 0;
    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        const image = new Image();
        image.addEventListener('load', () => {
          const transform = slot?.default_transform || {};
          const layer = makeImageLayer(image, {
            type: slot?.type === 'logo' ? 'logo' : 'image',
            sourceType: slot ? 'template-user' : 'upload',
            file,
            name: file.name,
            hash: hash6(reader.result),
            templateFamilyId: slot ? state.template.familyId : '',
            templateVariantId: slot ? state.template.variantId : '',
            templateSlotId: slot?.id || '',
            slotFrame: slot?.frame || null,
            fitMode: slot ? '' : 'contain-print-area',
            x: slot ? transform.x : 0,
            y: slot ? transform.y : 0,
            w: slot ? transform.width : 100,
            h: slot ? transform.height : 100,
            r: slot ? transform.rotation : 0,
            z: slot?.z ?? 50
          });
          if (slot) {
            const existingIndex = cfg.layers.findIndex((entry) => entry.templateSlotId === slot.id && !entry.locked);
            if (existingIndex >= 0) cfg.layers.splice(existingIndex, 1, layer);
            else cfg.layers.push(layer);
            state.activeFace = 'front';
            tabFront.classList.add('on');
            tabBack.classList.remove('on');
          } else {
            cfg.layers.push(layer);
          }
          cfg.sel = cfg.layers.indexOf(layer);
          renderUpload();
          composeFace(slot ? 'front' : state.activeFace);
          if (state.sync) composeFace('back');
          refreshImageValue();
          configurationChanged();
          completed += 1;
          if (completed === validFiles.length) uploadStatus.textContent = CFG.uploadSuccessText || 'Artwork uploaded at its original proportions. Proportional scaling is locked by default.';
        });
        image.addEventListener('error', () => {
          completed += 1;
          uploadStatus.textContent = CFG.uploadErrorText || 'This image could not be read. Try another file.';
        });
        image.src = reader.result;
      });
      reader.addEventListener('error', () => {
        completed += 1;
        uploadStatus.textContent = CFG.uploadErrorText || 'This image could not be read. Try another file.';
      });
      reader.readAsDataURL(file);
    });
    if (validFiles.length < files.length) uploadStatus.textContent = `${CFG.uploadErrorText || 'Some files were skipped.'} ${validFiles.length} valid file${validFiles.length > 1 ? 's were' : ' was'} added.`;
    fileInput.value = '';
    pendingTemplateSlot = null;
    fileInput.multiple = true;
  });
  Object.keys(ranges).forEach((key) => {
    ranges[key].addEventListener('input', () => {
      const layer = currentLayer();
      if (!layer) return;
      if (key === 's') {
        const requestedSize = Math.max(10, Math.min(250, Number(ranges.s.value)));
        const previousSize = Math.max(1, Number(layer.s ?? Math.sqrt(Number(layer.w ?? 85) * Number(layer.h ?? 85))));
        const width = Number(layer.w ?? previousSize);
        const height = Number(layer.h ?? previousSize);
        let ratio = requestedSize / previousSize;
        if (width * ratio > 250 || height * ratio > 250) ratio = Math.min(250 / width, 250 / height);
        if (width * ratio < 10 || height * ratio < 10) ratio = Math.max(10 / width, 10 / height);
        layer.w = width * ratio;
        layer.h = height * ratio;
        layer.s = Math.sqrt(layer.w * layer.h);
        ranges.s.value = layer.s;
        ranges.w.value = layer.w;
        ranges.h.value = layer.h;
      } else if ((key === 'w' || key === 'h') && layer.aspectLocked !== false) {
        const requestedScale = Math.max(10, Math.min(250, Number(ranges[key].value)));
        layer.w = requestedScale;
        layer.h = requestedScale;
        layer.s = requestedScale;
        ranges.s.value = requestedScale;
        ranges.w.value = requestedScale;
        ranges.h.value = requestedScale;
      } else {
        layer[key] = Number(ranges[key].value);
        if (key === 'w' || key === 'h') {
          layer.s = Math.sqrt(Number(layer.w ?? 85) * Number(layer.h ?? 85));
          ranges.s.value = layer.s;
        }
      }
      syncSliderOutputs();
      composeFace(state.activeFace);
      if (state.sync) composeFace('back');
      configurationChanged();
    });
  });
  function syncSliderOutputs() {
    const layer = currentLayer();
    if (!layer) return;
    outputs.x.textContent = Math.round(Number(layer.x));
    outputs.y.textContent = Math.round(Number(layer.y));
    outputs.s.textContent = `${Math.round(Number(layer.s ?? Math.sqrt(Number(layer.w ?? 85) * Number(layer.h ?? 85))))}%`;
    outputs.w.textContent = `${Math.round(Number(layer.w ?? layer.s ?? 85))}%`;
    outputs.h.textContent = `${Math.round(Number(layer.h ?? layer.s ?? 85))}%`;
    const rotation = Math.round((Number(layer.r) || 0) * 10) / 10;
    outputs.r.textContent = `${rotation}°`;
    if (rotateNumber && document.activeElement !== rotateNumber) rotateNumber.value = String(rotation);
  }
  function commitTypedRotation(forceNormalize = false) {
    const layer = currentLayer();
    if (!layer || !rotateNumber) return;
    const rawValue = rotateNumber.value.trim();
    if (!rawValue || rawValue === '-' || rawValue === '.' || rawValue === '-.') {
      if (forceNormalize) rotateNumber.value = String(Math.round((Number(layer.r) || 0) * 10) / 10);
      return;
    }
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) {
      if (forceNormalize) rotateNumber.value = String(Math.round((Number(layer.r) || 0) * 10) / 10);
      return;
    }
    const rotation = Math.max(-180, Math.min(180, Math.round(parsed * 10) / 10));
    layer.r = rotation;
    ranges.r.value = rotation;
    outputs.r.textContent = `${rotation}°`;
    if (forceNormalize || rotation !== parsed) rotateNumber.value = String(rotation);
    composeFace(state.activeFace);
    if (state.sync) composeFace('back');
    configurationChanged();
  }
  rotateNumber?.addEventListener('input', () => commitTypedRotation(false));
  rotateNumber?.addEventListener('change', () => commitTypedRotation(true));
  rotateNumber?.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    commitTypedRotation(true);
    rotateNumber.blur();
  });
  aspectRatioLock?.addEventListener('click', () => {
    const layer = currentLayer();
    if (!layer || layer.locked) return;
    layer.aspectLocked = layer.aspectLocked === false;
    if (layer.aspectLocked) {
      const uniform = Math.max(10, Math.min(250, Math.sqrt(
        Number(layer.w ?? layer.s ?? 85) * Number(layer.h ?? layer.s ?? 85)
      )));
      layer.w = uniform;
      layer.h = uniform;
      layer.s = uniform;
      ranges.s.value = uniform;
      ranges.w.value = uniform;
      ranges.h.value = uniform;
      composeFace(state.activeFace);
      if (state.sync) composeFace('back');
      uploadStatus.textContent = 'Original proportions restored and locked.';
      configurationChanged();
    } else {
      uploadStatus.textContent = 'Proportions unlocked. Width and height can now be adjusted independently.';
    }
    syncSliderOutputs();
    syncAspectRatioLock();
  });
  restoreRatioButton?.addEventListener('click', () => {
    const layer = currentLayer();
    if (!layer) return;
    resetArtworkLayer(layer);
    ranges.x.value = layer.x;
    ranges.y.value = layer.y;
    ranges.s.value = layer.s;
    ranges.w.value = layer.w;
    ranges.h.value = layer.h;
    ranges.r.value = layer.r;
    syncSliderOutputs();
    syncAspectRatioLock();
    composeFace(state.activeFace);
    if (state.sync) composeFace('back');
    uploadStatus.textContent = 'Artwork reset to its original proportions and orientation, centered on the printable paddle face.';
    configurationChanged();
  });
  tabFront.addEventListener('click', () => {
    state.activeFace = 'front';
    tabFront.classList.add('on');
    tabBack.classList.remove('on');
    renderUpload();
  });
  tabBack.addEventListener('click', () => {
    state.activeFace = 'back';
    tabBack.classList.add('on');
    tabFront.classList.remove('on');
    renderUpload();
  });
  syncBothFaces.addEventListener('change', () => {
    state.sync = syncBothFaces.checked;
    renderUpload();
    composeFace('back');
    refreshImageValue();
    configurationChanged();
  });
  function hash6(value) {
    let hash = 5381;
    for (let i = 0; i < value.length; i += 7) hash = ((hash << 5) + hash + value.charCodeAt(i)) >>> 0;
    return hash.toString(16).toUpperCase().padStart(6, '0').slice(-6);
  }

  /* ---------- Guided accordion ---------- */
  const wizardSteps = $$('.wizard-step');
  const heroStepButtons = $$('#heroSteps [data-step-target]');
  const wizardBack = $('#wizardBack');
  const wizardNext = $('#wizardNext');
  const wizardProgress = $('#wizardProgress');
  const mobileStepCount = $('#mobileStepCount');
  const mobileStepTitle = $('#mobileStepTitle');
  const mobileStepProgress = $('#mobileStepProgress');
  const mobileStepsTrigger = $('#mobileStepsTrigger');
  const mobileStepsDrawer = $('#mobileStepsDrawer');
  const mobileStepsList = $('#mobileStepsList');
  const fallbackStepActions = ['Confirm Build', 'Confirm Shape', 'Confirm Core', 'Confirm Surface', 'Confirm Artwork', 'Confirm Edge Color', 'Confirm Handle Colors', 'Generate Preview'];
  const fallbackStepNames = ['Build', 'Shape', 'Core', 'Surface', 'Artwork', 'Edge Color', 'Handle Colors', 'Result'];
  const stepActions = fallbackStepActions.map((label, index) => STEP_COPY[index]?.confirmLabel || label);
  const stepNames = fallbackStepNames.map((label, index) => STEP_COPY[index]?.navLabel || label);
  const stepActionLabel = (index) => index === wizardSteps.length - 1 && window.innerWidth <= 430
    ? 'Add to Cart'
    : stepActions[index];
  let currentStep = 0;
  let maxReached = 0;
  const completedSteps = new Set();
  wizardSteps.forEach((step, index) => {
    const summary = document.createElement('button');
    summary.type = 'button';
    summary.className = 'step-summary';
    summary.setAttribute('aria-expanded', index === 0 ? 'true' : 'false');
    summary.innerHTML = `<span><b>${String(index + 1).padStart(2, '0')}</b>${stepNames[index]}</span><strong data-step-summary-value>${index === 0 ? 'In progress' : 'Not started'}</strong><i aria-hidden="true"></i>`;
    summary.addEventListener('click', () => { if (index <= maxReached) goToStep(index); });
    step.prepend(summary);
  });
  function artworkSummary() {
    const count = state.faces.front.layers.filter((layer) => !layer.locked).length
      + (state.sync ? 0 : state.faces.back.layers.filter((layer) => !layer.locked).length);
    if (state.template.familyId) return `${state.template.familyName} · ${count} editable layer${count === 1 ? '' : 's'}`;
    return count ? `${count} design${count > 1 ? 's' : ''}` : 'No artwork';
  }
  function summaryValue(index) {
    if (index === 0) return BUILDS[state.build].label;
    if (index === 1) return `${currentShape()?.label || '—'} · ${currentShape()?.thickness || ''}`;
    if (index === 2) return `${currentCore()?.label || '—'} · ${currentCore()?.playerType || ''}`;
    if (index === 3) return currentSurface()?.label || '—';
    if (index === 4) return artworkSummary();
    if (index === 5) return item(EDGES, state.edge).label;
    if (index === 6) return `${item(FERRULES, state.ferrule).label} ferrule · ${item(GRIPS, state.grip).label} grip`;
    return 'Your custom paddle';
  }
  function updateStepChrome() {
    heroStepButtons.forEach((button, index) => {
      button.classList.toggle('active', index === currentStep);
      button.classList.toggle('done', completedSteps.has(index));
      button.disabled = index > maxReached;
      if (index === currentStep) button.setAttribute('aria-current', 'step');
      else button.removeAttribute('aria-current');
    });
    wizardSteps.forEach((step, index) => {
      const open = index === currentStep;
      step.hidden = index > maxReached;
      step.classList.toggle('active', open);
      step.classList.toggle('collapsed', !open && index <= maxReached);
      const summary = step.querySelector('.step-summary');
      summary.setAttribute('aria-expanded', open ? 'true' : 'false');
      summary.querySelector('[data-step-summary-value]').textContent = open ? 'In progress' : summaryValue(index);
    });
    wizardBack.disabled = currentStep === 0;
    wizardNext.textContent = stepActionLabel(currentStep);
    wizardNext.disabled = currentStep === wizardSteps.length - 1
      ? cartSubmissionInProgress
      : false;
    wizardProgress.textContent = `Step ${currentStep + 1} of ${wizardSteps.length}`;
    const activeStepLabel = wizardSteps[currentStep]?.querySelector('.step-label');
    if (activeStepLabel && wizardProgress.parentElement !== activeStepLabel) {
      activeStepLabel.appendChild(wizardProgress);
    }
    if (mobileStepCount) mobileStepCount.textContent = `Step ${currentStep + 1} of ${wizardSteps.length}`;
    if (mobileStepTitle) mobileStepTitle.textContent = stepNames[currentStep];
    if (mobileStepProgress) mobileStepProgress.style.width = `${(currentStep + 1) / wizardSteps.length * 100}%`;
    renderMobileSteps();
    if (currentStep === CORE_STEP_INDEX) {
      loadSelectedCoreModel();
    } else {
      restorePaddlePreview();
    }
  }
  function renderMobileSteps() {
    if (!mobileStepsList) return;
    mobileStepsList.innerHTML = '';
    stepNames.forEach((name, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.classList.toggle('current', index === currentStep);
      button.classList.toggle('done', completedSteps.has(index));
      button.innerHTML = `<b>${completedSteps.has(index) ? '✓' : String(index + 1).padStart(2, '0')}</b><strong>${name}</strong><span>${index === currentStep ? 'Current' : completedSteps.has(index) ? summaryValue(index) : index <= maxReached ? 'Available' : 'Complete earlier steps first'}</span>`;
      button.addEventListener('click', () => {
        if (index <= maxReached) {
          closeMobileSteps();
          goToStep(index);
        } else {
          button.querySelector('span').textContent = 'Complete the current step before continuing.';
        }
      });
      mobileStepsList.appendChild(button);
    });
  }
  function openMobileSteps() {
    if (!mobileStepsDrawer) return;
    mobileStepsDrawer.hidden = false;
    section.classList.add('steps-drawer-open');
    mobileStepsTrigger?.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
    renderMobileSteps();
    mobileStepsDrawer.querySelector('.mobile-steps-sheet__head button')?.focus();
  }
  function closeMobileSteps() {
    if (!mobileStepsDrawer) return;
    mobileStepsDrawer.hidden = true;
    section.classList.remove('steps-drawer-open');
    mobileStepsTrigger?.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
  }
  mobileStepsTrigger?.addEventListener('click', openMobileSteps);
  $$('[data-close-mobile-steps]').forEach((button) => button.addEventListener('click', closeMobileSteps));
  document.addEventListener('keydown', (event) => {
    if (
      event.key === 'Escape'
      && (
        (templateLibraryModal && !templateLibraryModal.hidden)
        || (textLayerModal && !textLayerModal.hidden)
      )
    ) closeArtworkModals();
    if (event.key === 'Escape' && !mobileStepsDrawer?.hidden) closeMobileSteps();
    if (event.key === 'Escape' && section.classList.contains('preview-expanded')) closeExpandedPreview();
  });
  function validateStep(index) {
    const valid = [
      Boolean(BUILDS[state.build]),
      Boolean(currentShape()),
      Boolean(currentCore()),
      Boolean(currentSurface()),
      true,
      Boolean(item(EDGES, state.edge)),
      Boolean(item(FERRULES, state.ferrule) && item(GRIPS, state.grip)),
      true
    ][index];
    if (valid) return true;
    const step = wizardSteps[index];
    let message = step.querySelector('.step-error');
    if (!message) {
      message = document.createElement('p');
      message.className = 'step-error';
      message.setAttribute('role', 'alert');
      step.appendChild(message);
    }
    message.textContent = index === 7
      ? 'Select the matching Cold Press or Thermoformed Shopify product in the theme editor before adding this design.'
      : 'Choose an option before continuing.';
    message.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return false;
  }
  function goToStep(next) {
    next = Math.max(0, Math.min(wizardSteps.length - 1, next));
    if (next === currentStep) return;
    currentStep = next;
    maxReached = Math.max(maxReached, currentStep);
    if (currentStep === wizardSteps.length - 1) refreshResult();
    updateStepChrome();
    const track = $('#wizardTrack');
    const active = wizardSteps[currentStep];
    requestAnimationFrame(() => {
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      if (window.innerWidth <= 820) {
        const compactHeight = parseFloat(getComputedStyle(section).getPropertyValue('--jc-mobile-stage-compact')) || 200;
        const stickyTop = parseFloat(getComputedStyle(section).getPropertyValue('--jc-sticky-top')) || 0;
        window.scrollTo({ top: Math.max(0, window.scrollY + active.getBoundingClientRect().top - compactHeight - stickyTop - 54), behavior });
      } else {
        track.scrollTo({ top: Math.max(0, active.offsetTop - 6), behavior });
      }
    });
  }
  wizardNext.addEventListener('click', () => {
    if (!validateStep(currentStep)) return;
    if (currentStep < wizardSteps.length - 1) {
      completedSteps.add(currentStep);
      goToStep(currentStep + 1);
    } else {
      checkoutButton?.click();
    }
  });
  wizardBack.addEventListener('click', () => goToStep(currentStep - 1));
  window.addEventListener('resize', () => {
    if (!cartSubmissionInProgress) wizardNext.textContent = stepActionLabel(currentStep);
  }, { passive: true });
  heroStepButtons.forEach((button) => button.addEventListener('click', () => {
    const next = Number(button.dataset.stepTarget);
    if (next <= maxReached) goToStep(next);
  }));

  const customizerApp = $('#customizerApp');
  const mobileCollapseSentinel = $('#mobileCollapseSentinel');
  if ('IntersectionObserver' in window) {
    if (CFG.mobileBottomBarEnabled !== false && customizerApp) {
      new IntersectionObserver((entries) => {
        const entry = entries[0];
        const visible = entry.isIntersecting && entry.intersectionRatio > 0.04;
        section.classList.toggle('action-bar-visible', visible && window.innerWidth <= 820);
      }, { threshold: [0, 0.04, 0.2] }).observe(customizerApp);
    }
    if (CFG.mobileCollapseEnabled !== false && mobileCollapseSentinel) {
      let sentinelSeen = false;
      new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) sentinelSeen = true;
        const passedAbove = sentinelSeen && !entry.isIntersecting && entry.boundingClientRect.top < 0;
        syncPipMode(passedAbove);
      }, { threshold: [0, 1] }).observe(mobileCollapseSentinel);
    }
  }
  if (window.visualViewport) {
    const updateKeyboardState = () => {
      const keyboardOpen = window.innerWidth <= 820 && window.visualViewport.height < window.innerHeight * 0.72;
      section.classList.toggle('keyboard-open', keyboardOpen);
    };
    window.visualViewport.addEventListener('resize', updateKeyboardState, { passive: true });
  }
  const policyItems = $$('.custom-terms details');
  policyItems.forEach((details) => {
    details.addEventListener('toggle', () => {
      details.querySelector('summary')?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      if (!details.open) return;
      policyItems.forEach((other) => {
        if (other !== details) other.open = false;
      });
    });
  });

  /* ---------- Result, final snapshot, Shopify cart ---------- */
  const codeOut = $('#codeOut');
  const checkoutButton = $('#checkoutBtn');
  const checkoutStatus = $('#checkoutStatus');
  const previewButton = $('#previewBtn');
  const previewStatus = $('#previewStatus');
  const snapshotCard = $('#snapshotCard');
  const previewImages = {
    front: $('#frontPreviewImage'),
    back: $('#backPreviewImage')
  };
  const previewDownloads = {
    front: $('#downloadFrontPreview'),
    back: $('#downloadBackPreview')
  };
  let previewFiles = { front: null, back: null };
  let previewObjectUrls = { front: '', back: '' };
  function faceCode(which) {
    const cfg = which === 'back' && state.sync ? state.faces.front : state.faces[which];
    if (!cfg.layers.length) return 'NONE';
    return cfg.layers.map((layer) => {
      const width = layer.w ?? layer.s ?? 85;
      const height = layer.h ?? layer.s ?? 85;
      return `${layer.hash}(${layer.x},${layer.y},${width}x${height},${layer.r})`;
    }).join('+');
  }
  function refreshCode() {
    codeOut.textContent = [
      'JS',
      `BLD.${BUILDS[state.build].code}`,
      `SH.${currentShape()?.code || state.shape}`,
      `CR.${currentCore()?.code || state.core}`,
      `SF.${currentSurface()?.code || state.surface}`,
      `TPL.${state.template.familyId || 'BLANK'}.${state.template.variantId || 'NONE'}.V${state.template.version || '0'}`,
      `EG.${state.edge}`,
      `FR.${state.ferrule}`,
      `GP.${state.grip}`,
      `F.${faceCode('front')}`,
      `B.${faceCode('back')}${state.sync ? '*' : ''}`
    ].join('-');
  }
  function invalidatePreview() {
    previewFiles = { front: null, back: null };
    ['front', 'back'].forEach((view) => {
      if (previewObjectUrls[view]) URL.revokeObjectURL(previewObjectUrls[view]);
      previewObjectUrls[view] = '';
      previewImages[view]?.removeAttribute('src');
      previewDownloads[view]?.removeAttribute('href');
    });
    snapshotCard.hidden = true;
    previewStatus.textContent = 'Design changed. Generate a new final preview when you are ready.';
  }
  let lastStoredConfiguration = '';
  function layerConfiguration(layer) {
    return {
      type: layer.type,
      sourceType: layer.sourceType,
      name: layer.name,
      hash: layer.hash,
      templateSlotId: layer.templateSlotId || '',
      fitMode: layer.fitMode || '',
      originalAspectRatio: Number(layer.originalAspectRatio || 0),
      x: Number(layer.x || 0),
      y: Number(layer.y || 0),
      size: Number(layer.s ?? Math.sqrt(Number(layer.w ?? 85) * Number(layer.h ?? 85))),
      width: Number(layer.w ?? layer.s ?? 85),
      height: Number(layer.h ?? layer.s ?? 85),
      rotation: Number(layer.r || 0),
      z: Number(layer.z ?? 50),
      text: layer.text || '',
      font: layer.font || '',
      color: layer.color || '',
      originalFileName: layer.file?.name || ''
    };
  }
  function configurationPayload() {
    return {
      customizerVersion: CFG.version || 'V7',
      configurationId: codeOut.textContent,
      updatedAt: new Date().toISOString(),
      build: state.build,
      shape: state.shape,
      shapeCode: currentShape()?.code || state.shape,
      core: state.core,
      coreCode: currentCore()?.code || state.core,
      surface: state.surface,
      surfaceCode: currentSurface()?.code || state.surface,
      edge: state.edge,
      edgeConfig: {
        code: item(EDGES, state.edge).code,
        label: item(EDGES, state.edge).label,
        mode: item(EDGES, state.edge).mode,
        solid: item(EDGES, state.edge).hex,
        mirroredStops: item(EDGES, state.edge).mode === 'mirrored_gradient'
          ? {
              '0%': item(EDGES, state.edge).stop0,
              '25%': item(EDGES, state.edge).stop25,
              '50%': item(EDGES, state.edge).stop50,
              '75%': item(EDGES, state.edge).stop25,
              '100%': item(EDGES, state.edge).stop0
            }
          : null,
        gradientRange: item(EDGES, state.edge).mode === 'mirrored_gradient'
          ? '0–100% defines one half of the edge; the second half mirrors it; full perimeter range is 0–200%'
          : null,
        fullPerimeterStops200: item(EDGES, state.edge).mode === 'mirrored_gradient'
          ? {
              '0%': item(EDGES, state.edge).stop0,
              '25%': item(EDGES, state.edge).stop25,
              '50%': item(EDGES, state.edge).stop50,
              '75%': item(EDGES, state.edge).stop25,
              '100%': item(EDGES, state.edge).stop0,
              '125%': item(EDGES, state.edge).stop25,
              '150%': item(EDGES, state.edge).stop50,
              '175%': item(EDGES, state.edge).stop25,
              '200%': item(EDGES, state.edge).stop0
            }
          : null,
        textureAxis: item(EDGES, state.edge).gradientAxis
      },
      ferrule: state.ferrule,
      grip: state.grip,
      sync: state.sync,
      recommendedSetup: state.recommendedSetup,
      template: { ...state.template },
      faces: {
        front: state.faces.front.layers.filter((layer) => !layer.locked).map(layerConfiguration),
        back: state.faces.back.layers.filter((layer) => !layer.locked).map(layerConfiguration)
      }
    };
  }
  function configurationChanged() {
    refreshCode();
    invalidatePreview();
    refreshResult();
    updateStepChrome();
    try {
      const storable = JSON.stringify(configurationPayload());
      if (storable !== lastStoredConfiguration) {
        localStorage.setItem(storageKey, storable);
        lastStoredConfiguration = storable;
      }
    } catch (error) {
      /* Browsers may block storage in private mode; the customizer still works. */
    }
  }
  const resultBuild = $('#resultBuild');
  const resultShape = $('#resultShape');
  const resultCore = $('#resultCore');
  const resultSurface = $('#resultSurface');
  const resultArtwork = $('#resultArtwork');
  const resultEdge = $('#resultEdge');
  const resultHandle = $('#resultHandle');
  const resultTotal = $('#resultTotal');
  const resultCompareAt = $('#resultCompareAt');
  const currentTotal = $('#currentTotal');
  const currentCompareAt = $('#currentCompareAt');
  let lastDisplayedPrice = '';
  function refreshResult() {
    if (!resultBuild) return;
    resultBuild.textContent = BUILDS[state.build].label;
    resultShape.textContent = `${currentShape()?.label || '—'} · ${currentShape()?.thickness || ''}`;
    resultCore.textContent = `${currentCore()?.label || '—'} · ${currentCore()?.playerType || ''}`;
    resultSurface.textContent = `${currentSurface()?.label || '—'}${state.build === 'hot' ? ` · ${currentSurface()?.grit || 'L100'}` : ''}`;
    resultArtwork.textContent = artworkSummary();
    resultEdge.textContent = item(EDGES, state.edge).label;
    resultHandle.textContent = `${item(FERRULES, state.ferrule).label} ferrule · ${item(GRIPS, state.grip).label} grip`;
    const variant = selectedVariant();
    const displayPrice = variant ? formatMoney(variant.price) : 'Select product';
    resultTotal.textContent = variant ? displayPrice : 'Select the Custom Paddle product';
    if (resultCompareAt) resultCompareAt.textContent = formatMoney(compareAtPriceForBuild());
    if (currentTotal) {
      currentTotal.textContent = displayPrice;
      if (displayPrice !== lastDisplayedPrice) {
        currentTotal.classList.remove('price-updated');
        requestAnimationFrame(() => currentTotal.classList.add('price-updated'));
      }
    }
    if (currentCompareAt) currentCompareAt.textContent = formatMoney(compareAtPriceForBuild());
    lastDisplayedPrice = displayPrice;
    checkoutButton.disabled = cartSubmissionInProgress;
    if (currentStep === wizardSteps.length - 1) wizardNext.disabled = checkoutButton.disabled;
    updateOptionPriceLabels();
  }
  $$('[data-edit-step]').forEach((button) => button.addEventListener('click', () => goToStep(Number(button.dataset.editStep))));

  function framePaddleForFinalPreview() {
    applyTransform(currentModel, shapeTransform);
    root.updateMatrixWorld(true);
    currentModel.updateWorldMatrix(true, true);
    const bounds = new THREE.Box3().setFromObject(currentModel);
    if (bounds.isEmpty()) throw new Error('The paddle could not be framed for the final preview.');
    const size = bounds.getSize(new THREE.Vector3());
    const center = bounds.getCenter(new THREE.Vector3());
    const verticalFov = THREE.MathUtils.degToRad(camera.fov);
    const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * Math.max(camera.aspect, 0.01));
    const distanceForHeight = (size.y / 2) / Math.tan(verticalFov / 2);
    const distanceForWidth = (size.x / 2) / Math.tan(horizontalFov / 2);
    const distance = Math.max(10, Math.min(40,
      Math.max(distanceForHeight, distanceForWidth) * 1.18 + size.z / 2
    ));
    camera.position.set(center.x, center.y, center.z + distance);
    camera.lookAt(center);
    camera.updateProjectionMatrix();
  }
  async function capturePreview(view) {
    if (!currentModel) throw new Error('The 3D model is not ready yet.');
    const rendererSize = renderer.getSize(new THREE.Vector2());
    const previous = {
      rootX: root.rotation.x,
      rootY: root.rotation.y,
      targetX: targetRotationX,
      targetY: targetRotationY,
      spin: autoSpin,
      cameraPosition: camera.position.clone(),
      cameraQuaternion: camera.quaternion.clone(),
      cameraAspect: camera.aspect,
      cameraZoom: camera.zoom,
      rendererWidth: rendererSize.x,
      rendererHeight: rendererSize.y,
      rendererPixelRatio: renderer.getPixelRatio(),
      modelVisible: currentModel.visible,
      modelScale: currentModel.scale.clone(),
      modelPosition: currentModel.position.clone(),
      coreVisible: currentCoreModel?.visible,
      transitions: modelTransitions
    };
    let blob;
    try {
      autoSpin = false;
      modelTransitions = [];
      targetRotationX = 0;
      targetRotationY = view === 'back' ? Math.PI : 0;
      root.rotation.x = 0;
      root.rotation.y = targetRotationY;
      currentModel.visible = true;
      if (currentCoreModel) currentCoreModel.visible = false;

      /* The final preview uses its own high-resolution render target and
         camera framing. It therefore never inherits a shopper's zoom level,
         stage size, or current orbit position. */
      const maximumTextureSize = Math.max(1024, Number(renderer.capabilities.maxTextureSize) || 4096);
      /* These files are visual order references, not production artwork.
         Keep them clear enough to review while avoiding two very large PNGs
         in the same cart request as the customer's original files and the
         flattened production artwork. */
      const targetRenderWidth = 1440;
      const targetRenderHeight = 1620;
      const renderScale = Math.min(1, maximumTextureSize / targetRenderHeight);
      const renderWidth = Math.round(targetRenderWidth * renderScale);
      const renderHeight = Math.round(targetRenderHeight * renderScale);
      renderer.setPixelRatio(1);
      renderer.setSize(renderWidth, renderHeight, false);
      camera.aspect = renderWidth / renderHeight;
      camera.zoom = 1;
      camera.updateProjectionMatrix();
      framePaddleForFinalPreview();
      renderer.shadowMap.needsUpdate = true;
      renderer.render(scene, camera);

      const source = renderer.domElement;
      const outputWidth = renderWidth;
      const previewHeight = renderHeight;
      const layoutScale = outputWidth / 1600;
      const px = (value) => Math.max(1, Math.round(value * layoutScale));
      const footerHeight = px(280);
      const output = document.createElement('canvas');
      output.width = outputWidth;
      output.height = previewHeight + footerHeight;
      const ctx = output.getContext('2d', { alpha: false });
      /* Match the live Three.js studio instead of applying the legacy dark
         presentation background after rendering the configured paddle. */
      ctx.fillStyle = STUDIO_BACKGROUND_CSS;
      ctx.fillRect(0, 0, outputWidth, previewHeight);
      ctx.drawImage(source, 0, 0, outputWidth, previewHeight);
      ctx.fillStyle = '#f2f4f1';
      ctx.fillRect(0, previewHeight, outputWidth, footerHeight);
      ctx.fillStyle = '#d9ded9';
      ctx.fillRect(0, previewHeight, outputWidth, px(2));
      ctx.fillStyle = '#35d3bd';
      ctx.fillRect(px(74), previewHeight + px(58), px(12), px(138));
      ctx.fillStyle = '#1b201e';
      ctx.font = `800 ${px(44)}px Arial`;
      ctx.fillText(`JOYSENT / ${view.toUpperCase()} PADDLE PREVIEW`, px(124), previewHeight + px(108));
      ctx.fillStyle = '#69716d';
      ctx.font = `600 ${px(25)}px monospace`;
      const code = codeOut.textContent;
      ctx.fillText(code.length > 76 ? `${code.slice(0, 76)}…` : code, px(124), previewHeight + px(160));
      ctx.fillStyle = '#343b37';
      ctx.font = `600 ${px(24)}px Arial`;
      ctx.fillText(`${BUILDS[state.build].label} · ${currentShape()?.label} · ${currentCore()?.label} · ${currentSurface()?.label}`, px(124), previewHeight + px(218));

      blob = await new Promise((resolve, reject) => output.toBlob(
        (value) => value ? resolve(value) : reject(new Error('Preview image could not be created.')),
        'image/jpeg',
        0.9
      ));
    } finally {
      root.rotation.x = previous.rootX;
      root.rotation.y = previous.rootY;
      targetRotationX = previous.targetX;
      targetRotationY = previous.targetY;
      autoSpin = previous.spin;
      currentModel.visible = previous.modelVisible;
      currentModel.scale.copy(previous.modelScale);
      currentModel.position.copy(previous.modelPosition);
      if (currentCoreModel && typeof previous.coreVisible === 'boolean') currentCoreModel.visible = previous.coreVisible;
      modelTransitions = previous.transitions;
      renderer.setPixelRatio(previous.rendererPixelRatio);
      renderer.setSize(previous.rendererWidth, previous.rendererHeight, false);
      camera.aspect = previous.cameraAspect;
      camera.zoom = previous.cameraZoom;
      camera.position.copy(previous.cameraPosition);
      camera.quaternion.copy(previous.cameraQuaternion);
      camera.updateProjectionMatrix();
      root.updateMatrixWorld(true);
      requestRender();
    }
    return new File([blob], `joysent-final-paddle-${view}-preview-${Date.now()}.jpg`, { type: 'image/jpeg' });
  }
  async function generateFinalPreview(scrollIntoView) {
    previewButton.disabled = true;
    previewButton.textContent = 'Creating Previews…';
    previewStatus.textContent = 'Rendering the front and back of your configured paddle…';
    try {
      const frontFile = await capturePreview('front');
      const backFile = await capturePreview('back');
      previewFiles = { front: frontFile, back: backFile };
      ['front', 'back'].forEach((view) => {
        if (previewObjectUrls[view]) URL.revokeObjectURL(previewObjectUrls[view]);
        previewObjectUrls[view] = URL.createObjectURL(previewFiles[view]);
        if (previewImages[view]) previewImages[view].src = previewObjectUrls[view];
        if (previewDownloads[view]) {
          previewDownloads[view].href = previewObjectUrls[view];
          previewDownloads[view].download = previewFiles[view].name;
        }
      });
      snapshotCard.hidden = false;
      previewStatus.textContent = 'Front and back previews are ready. Download either image now; both will also be attached to the cart and order.';
      if (scrollIntoView) snapshotCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return previewFiles;
    } catch (error) {
      previewStatus.textContent = error.message || 'The preview could not be generated. Please try again.';
      throw error;
    } finally {
      previewButton.disabled = false;
      previewButton.textContent = COPY.previewButton || 'Generate Final Preview';
    }
  }
  function canvasPngBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error('The combined production artwork could not be created.')),
        'image/png'
      );
    });
  }
  async function createProductionArtwork(face) {
    const cfg = face === 'back' && state.sync ? state.faces.front : state.faces[face];
    if (!cfg.layers.length) return null;
    const canvas = document.createElement('canvas');
    canvas.width = FACE_W;
    canvas.height = FACE_H;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(renderMaskedArtwork(face, cfg.layers), 0, 0);
    const blob = await canvasPngBlob(canvas);
    return new File(
      [blob],
      `joysent-production-artwork-${face}-${Date.now()}.png`,
      { type: 'image/png', lastModified: Date.now() }
    );
  }
  const waitForCartCheck = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  function cartUploadSize(form) {
    let bytes = 0;
    let files = 0;
    for (const value of form.values()) {
      if (value instanceof Blob) {
        bytes += Number(value.size || 0);
        files += 1;
      } else {
        bytes += new Blob([String(value)]).size;
      }
    }
    return { bytes, files, megabytes: Number((bytes / 1024 / 1024).toFixed(2)) };
  }
  async function submitCartUpload(url, form) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 120000);
    try {
      return await fetch(url, {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
    } finally {
      window.clearTimeout(timeout);
    }
  }
  async function cartHasConfiguration(shopRoot, configurationId) {
    if (!configurationId) return false;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      if (attempt) await waitForCartCheck(700 * attempt);
      try {
        const response = await fetch(`${shopRoot}cart.js`, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) continue;
        const cart = await response.json();
        if ((cart.items || []).some((cartItem) => (
          String(cartItem.properties?.['_Configuration ID'] || '') === String(configurationId)
        ))) return true;
      } catch (error) {
        /* The original cart error is more useful than a failed verification. */
      }
    }
    return false;
  }
  previewButton.addEventListener('click', () => generateFinalPreview(true).catch(() => {}));
  checkoutButton.addEventListener('click', async () => {
    if (cartSubmissionInProgress) return;
    if (!selectionIsCompatible()) {
      checkoutStatus.textContent = 'This paddle shape, core and surface combination is not available. Please choose from the options shown on this page.';
      return;
    }
    const sourceVariant = selectedVariant();
    const sourceProduct = productForBuild(state.build);
    if (!productBindingIsValid(state.build)) {
      const resolvedId = state.build === 'cold' ? CFG.coldResolvedProductId : CFG.hotResolvedProductId;
      checkoutStatus.textContent = `${BUILDS[state.build].label} product binding mismatch. Expected Product ID ${sourceProduct.id}, but the current handle resolved to ${resolvedId || 'no product'}.`;
      return;
    }
    if (!sourceVariant?.id) {
      checkoutStatus.textContent = `No matching ${BUILDS[state.build].label} Shopify variant. Check the fixed product's Shape, Core, Surface and Artwork options.`;
      return;
    }
    const checkoutVariant = checkoutVariantForSelection(sourceVariant);
    if (!checkoutVariant?.id) {
      checkoutStatus.textContent = `The checkout product has no available ${formatMoney(sourceVariant.price)} variant for ${BUILDS[state.build].label}.`;
      return;
    }
    if (!currentModel) {
      checkoutStatus.textContent = 'The 3D preview is still loading. Please wait a moment, then select Add to Cart again.';
      loadSelectedShapeModel();
      return;
    }
    cartSubmissionInProgress = true;
    checkoutButton.disabled = true;
    checkoutButton.firstElementChild.textContent = 'Preparing your custom paddle…';
    wizardNext.disabled = true;
    wizardNext.classList.add('is-loading');
    wizardNext.textContent = 'Adding…';
    checkoutStatus.textContent = '';
    let configurationId = '';
    let shopRoot = window.Shopify?.routes?.root || '/';
    try {
      if (navigator.onLine === false) throw new Error('You appear to be offline. Reconnect to the internet, then select Add to Cart again.');
      if (!previewFiles.front || !previewFiles.back) await generateFinalPreview(false);
      const form = new FormData();
      configurationId = `JC-${Date.now()}-${hash6(codeOut.textContent)}`;
      const cartConfiguration = configurationPayload();
      cartConfiguration.configurationId = configurationId;
      const configurationFile = new File(
        [JSON.stringify(cartConfiguration, null, 2)],
        `joysent-configuration-${configurationId}.json`,
        { type: 'application/json', lastModified: Date.now() }
      );
      // Submit the sellable checkout-proxy variant. Keep the exact configured
      // source product and variant below as line-item properties for production.
      form.append('id', checkoutVariant.id);
      form.append('quantity', '1');
      form.append('properties[_Configuration ID]', configurationId);
      form.append('properties[_Configuration JSON]', configurationFile, configurationFile.name);
      form.append('properties[Build]', BUILDS[state.build].label);
      form.append('properties[Paddle Shape]', `${currentShape()?.label} · ${currentShape()?.thickness || ''}`);
      form.append('properties[Core]', currentCore()?.label || '');
      form.append('properties[Player Type]', currentCore()?.playerType || '');
      form.append('properties[Surface]', currentSurface()?.label || '');
      if (state.build === 'hot') form.append('properties[Surface Grit]', currentSurface()?.grit || 'L100');
      form.append('properties[Edge Color]', item(EDGES, state.edge).label);
      if (item(EDGES, state.edge).mode === 'mirrored_gradient') {
        form.append(
          'properties[_Edge Gradient]',
          `HALF EDGE 0 ${item(EDGES, state.edge).stop0} | 25 ${item(EDGES, state.edge).stop25} | 50 ${item(EDGES, state.edge).stop50} | 75 ${item(EDGES, state.edge).stop25} | 100 ${item(EDGES, state.edge).stop0} | MIRROR TO SECOND HALF | FULL PERIMETER 0–200 | ${item(EDGES, state.edge).gradientAxis.toUpperCase()}`
        );
      }
      form.append('properties[Ferrule Color]', item(FERRULES, state.ferrule).label);
      form.append('properties[Grip Color]', item(GRIPS, state.grip).label);
      form.append('properties[Same artwork both sides]', state.sync ? 'Yes' : 'No');
      form.append('properties[Printable Area]', 'Paddle face only — artwork outside the face boundary is automatically clipped');
      if (FEATURES.usableTemplates && state.template.familyId) {
        form.append('properties[Template Family]', state.template.familyId);
        form.append('properties[Template Name]', state.template.familyName);
        form.append('properties[Template Variant]', state.template.variantId);
        form.append('properties[Template Version]', state.template.version);
        form.append('properties[Color Preset]', state.template.colorPreset || 'Default');
        form.append('properties[_Template ID]', state.template.familyId);
        form.append('properties[_Template Version]', state.template.version);
      }
      const frontUserLayers = state.faces.front.layers.filter((layer) => !layer.locked);
      const backUserLayers = state.faces.back.layers.filter((layer) => !layer.locked);
      form.append('properties[Front Artwork]', frontUserLayers.length ? frontUserLayers.map((layer) => layer.name).join(', ') : 'No artwork');
      form.append('properties[Back Artwork]', state.sync
        ? (frontUserLayers.length ? 'Same as front' : 'No artwork')
        : (backUserLayers.length ? backUserLayers.map((layer) => layer.name).join(', ') : 'No artwork'));
      const customTextSummary = ['front', 'back'].flatMap((face) => state.faces[face].layers
        .filter((layer) => layer.type === 'text' && layer.text)
        .map((layer) => `${face}: ${layer.text}`));
      if (customTextSummary.length) form.append('properties[Custom Text Summary]', customTextSummary.join(' | '));
      const customTextFontSummary = ['front', 'back'].flatMap((face) => state.faces[face].layers
        .filter((layer) => layer.type === 'text' && layer.text)
        .map((layer) => `${face}: "${layer.text}" — ${selectedFontLabel(layer.font)}`));
      if (customTextFontSummary.length) form.append('properties[Custom Text Fonts]', customTextFontSummary.join(' | '));
      const uploadReferences = ['front', 'back'].flatMap((face) => state.faces[face].layers
        .filter((layer) => layer.file)
        .map((layer) => `${face}: ${layer.file.name}`));
      if (uploadReferences.length) form.append('properties[Upload References]', uploadReferences.join(' | '));
      if (state.recommendedSetup) form.append('properties[Recommended Setup]', state.recommendedSetup);
      const notes = $('#customNotes')?.value.trim();
      if (notes) form.append('properties[Custom Notes]', notes);
      form.append('properties[Final Price]', formatMoney(sourceVariant.price));
      state.faces.front.layers.forEach((layer, index) => {
        if (layer.file) form.append(`properties[Front Custom Artwork ${index + 1}]`, layer.file, layer.name);
      });
      state.faces.back.layers.forEach((layer, index) => {
        if (layer.file) form.append(`properties[Back Custom Artwork ${index + 1}]`, layer.file, layer.name);
      });
      const productionFront = await createProductionArtwork('front');
      const productionBack = await createProductionArtwork('back');
      if (productionFront) form.append('properties[Production Artwork - Front (Flattened)]', productionFront, productionFront.name);
      if (productionBack) form.append('properties[Production Artwork - Back (Flattened)]', productionBack, productionBack.name);
      if (previewFiles.front) form.append('properties[Final Paddle Preview - Front]', previewFiles.front, previewFiles.front.name);
      if (previewFiles.back) form.append('properties[Final Paddle Preview - Back]', previewFiles.back, previewFiles.back.name);

      const upload = cartUploadSize(form);
      console.info('Joysent cart upload prepared', {
        configurationId,
        checkoutVariantId: checkoutVariant.id,
        files: upload.files,
        megabytes: upload.megabytes
      });
      checkoutStatus.textContent = `Uploading ${upload.files} design files to Shopify… Please keep this page open.`;
      const response = await submitCartUpload(`${shopRoot}cart/add.js`, form);
      const responseText = await response.text();
      let data = {};
      try { data = responseText ? JSON.parse(responseText) : {}; } catch (error) { data = {}; }
      if (!response.ok) {
        const serverMessage = String(data.description || data.message || '');
        const diagnostic = `HTTP ${response.status} · ${BUILDS[state.build].label} · ${CFG.checkoutProductTitle || 'CUSTOM JOYSENT PADDLE'} · Checkout Variant ${checkoutVariant.id}`;
        console.error('Joysent Shopify cart rejection', {
          diagnostic,
          response: data,
          checkoutProductHandle: CFG.checkoutProductHandle || 'joysent-custom-paddle',
          sourceProductHandle: sourceProduct.handle,
          sourceVariantId: sourceVariant.id,
          sourceVariantSku: sourceVariant.sku || '',
          sourceVariantOptions: sourceVariant.options || [sourceVariant.option1, sourceVariant.option2, sourceVariant.option3].filter(Boolean)
        });
        throw new Error(`${serverMessage || 'Cart request failed.'} (${diagnostic})`);
      }
      window.location.href = CFG.goDirectToCheckout ? `${shopRoot}checkout` : `${shopRoot}cart`;
    } catch (error) {
      const requestLost = error?.name === 'AbortError' || error instanceof TypeError || /failed to fetch|networkerror|load failed/i.test(String(error?.message || ''));
      if (requestLost && await cartHasConfiguration(shopRoot, configurationId)) {
        window.location.href = CFG.goDirectToCheckout ? `${shopRoot}checkout` : `${shopRoot}cart`;
        return;
      }
      console.error('Joysent cart upload failed', {
        configurationId,
        name: error?.name,
        message: error?.message,
        online: navigator.onLine
      });
      checkoutStatus.textContent = error?.name === 'AbortError'
        ? 'The design upload took too long. Your design is still on this page. Check your connection, then select Add to Cart again.'
        : requestLost
          ? 'The design files could not reach Shopify. Your design is still on this page. Check your connection, then select Add to Cart again.'
          : (error.message || 'We could not add this design. Please try again.');
      cartSubmissionInProgress = false;
      checkoutButton.disabled = false;
      checkoutButton.firstElementChild.textContent = COPY.addToCart || 'Add Custom Paddle to Cart';
      wizardNext.disabled = false;
      wizardNext.classList.remove('is-loading');
      wizardNext.textContent = stepActionLabel(currentStep);
    }
  });

  /* ---------- Start ---------- */
  function loadImages() {
    return Promise.all(Object.keys(TEX).map((key) => new Promise((resolve) => {
      const image = new Image();
      image.onload = () => { weaveImgs[key] = image; resolve(); };
      image.onerror = resolve;
      image.src = TEX[key];
    })));
  }
  function startWhenReady(attempt = 0) {
    const parts = window.JoysentPaddleModelParts || [];
    if (parts.length === 3 && parts.every(Boolean)) {
      loadImages().then(() => {
        ensureBranchSelections();
        applyThemeCopy();
        updateBuildUI();
        renderRecommendations();
        buildShapeUI();
        buildCoreUI();
        buildSurfaceUI();
        activateSurfaceTextures();
        renderUpload();
        refreshImageValue();
        refreshCode();
        refreshResult();
        updateStepChrome();
        resize();
        loadSelectedShapeModel(true);
      });
      return;
    }
    if (attempt < 100) {
      setTimeout(() => startWhenReady(attempt + 1), 50);
      return;
    }
    showModelError('Your paddle preview could not load. Select Retry 3D Preview to try again.');
  }
  startWhenReady();
})();
